import React, { useState, useEffect } from 'react';
import './App.css';

// TypeScript interfaces for our data
interface Acquisition {
  id: string;
  original_name: string;
  display_name: string;
  created_time: string;
  database_id?: number;  // Database ID from acquisition selection
}

interface Notification {
  id: number;
  type: 'success' | 'error' | 'info';
  message: string;
  url?: string;  // Optional URL for clickable notifications
}

interface Country {
  id: number;
  country: string;
  employee_count: number;
  rif_type: string;
  start_date: string;
  added_date: string;
  status: string;
}

interface RifType {
  rif_type: string;
  description: string;
  employee_count_min: number;
  employee_count_max: number | null;
  is_applicable: boolean;
}

interface ProcessStep {
  id?: number;
  step_number: string;
  step_name: string;
  step_type: string;
  process_type: string;
  process_category?: string;
  workflow_phase: string;  // Added for sequential workflow
  parent_step?: string | null;
  timeline_day_min?: number | null;
  timeline_day_max?: number | null;
  duration_days: number;
  dri_role: string;
  is_critical_path: boolean;
  is_legal_gate: boolean;
  is_optional?: boolean;  // Added for step customization
  risk_level: string;
  documents_required?: string | null;
  email_templates?: string | null;
  call_scripts?: string | null;
  notes?: string | null;
  depends_on?: string | null;
}

interface StepCompletion {
  id: number;
  step_number: string;
  status: string;
  planned_start_date: string | null;
  started_date: string | null;
  completed_date: string | null;
  completed_by: string | null;
  notes: string | null;
  completion_links: string | null;
  actual_duration_days: number | null;
}

interface User {
  email: string;
  name: string;
  picture?: string;
  hd?: string;
}

function App() {
  // Add CSS animation for spinner
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // React state - like variables that trigger re-renders when changed
  const [acquisitions, setAcquisitions] = useState<Acquisition[]>([]);
  const [selectedAcquisition, setSelectedAcquisition] = useState<Acquisition | null>(null);
  const [selectedAcquisitionId, setSelectedAcquisitionId] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  
  // Country management state
  const [assignedCountries, setAssignedCountries] = useState<Country[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [showCountryForm, setShowCountryForm] = useState(false);
  const [currentView, setCurrentView] = useState<'acquisition' | 'countries' | 'progress' | 'admin'>('acquisition');
  
  // Progress tracking state
  const [selectedCountryForProgress, setSelectedCountryForProgress] = useState<Country | null>(null);
  const [processSteps, setProcessSteps] = useState<ProcessStep[]>([]);
  const [stepCompletions, setStepCompletions] = useState<StepCompletion[]>([]);
  const [selectedStep, setSelectedStep] = useState<ProcessStep | null>(null);
  const [showStepDetail, setShowStepDetail] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<{[key: string]: boolean}>({});
  const [expandedParentSteps, setExpandedParentSteps] = useState<{[key: string]: boolean}>({});
  const [rifStartDate, setRifStartDate] = useState<string | null>(null);
  const [allExpanded, setAllExpanded] = useState<boolean>(false);
  
  // Step completion modal state
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [completionDate, setCompletionDate] = useState<string>('');
  const [actualStartDate, setActualStartDate] = useState<string>('');
  const [completionNotes, setCompletionNotes] = useState<string>('');
  const [completionLinks, setCompletionLinks] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('Completed');
  const [completingStep, setCompletingStep] = useState(false);
  const [updatingStepStatus, setUpdatingStepStatus] = useState<string | null>(null);
  const [activeModalTab, setActiveModalTab] = useState<'details' | 'history'>('details');
  const [stepHistory, setStepHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  
  // Edit RIF start date modal state
  const [showEditDateModal, setShowEditDateModal] = useState(false);
  const [selectedCountryForEdit, setSelectedCountryForEdit] = useState<Country | null>(null);
  const [editingStartDate, setEditingStartDate] = useState<string>('');
  const [updatingStartDate, setUpdatingStartDate] = useState(false);
  
  // Edit Planned Date modal state
  const [showEditPlannedDateModal, setShowEditPlannedDateModal] = useState(false);
  const [selectedStepForDateEdit, setSelectedStepForDateEdit] = useState<ProcessStep | null>(null);
  const [editingPlannedDate, setEditingPlannedDate] = useState<string>('');
  const [updatingPlannedDate, setUpdatingPlannedDate] = useState(false);
  
  // Step customization state
  const [showStepEditModal, setShowStepEditModal] = useState(false);
  const [showAddStepModal, setShowAddStepModal] = useState(false);
  const [selectedStepForEdit, setSelectedStepForEdit] = useState<ProcessStep | null>(null);
  const [editingStep, setEditingStep] = useState<any>({});
  const [dropdownData, setDropdownData] = useState<any>({});
  const [updatingStep, setUpdatingStep] = useState(false);
  const [deletingStep, setDeletingStep] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [stepToDelete, setStepToDelete] = useState<ProcessStep | null>(null);
  const [editingResources, setEditingResources] = useState<string | null>(null); // step_number of step being edited
  const [resourceEditData, setResourceEditData] = useState<{[key: string]: string}>({});
  const [savingResources, setSavingResources] = useState(false);
  
  // Admin console state
  const [adminSelectedCountry, setAdminSelectedCountry] = useState<string>('');
  const [adminSelectedAcquisition, setAdminSelectedAcquisition] = useState<string>('');
  const [adminSelectedCountryForRemoval, setAdminSelectedCountryForRemoval] = useState<string>('');
  const [adminLoading, setAdminLoading] = useState<{[key: string]: boolean}>({});
  const [adminResults, setAdminResults] = useState<any>(null);
  const [adminAvailableCountries, setAdminAvailableCountries] = useState<string[]>([]);
  const [syncAllEnabled, setSyncAllEnabled] = useState(false);
  
  // Status management state
  const [showAcquisitionStatusModal, setShowAcquisitionStatusModal] = useState(false);
  const [showCountryStatusModal, setShowCountryStatusModal] = useState(false);
  const [selectedCountryForStatus, setSelectedCountryForStatus] = useState<Country | null>(null);
  const [acquisitionStatus, setAcquisitionStatus] = useState<string>('');
  const [acquisitionRifStartDate, setAcquisitionRifStartDate] = useState<string>('');
  const [acquisitionRifEndDate, setAcquisitionRifEndDate] = useState<string>('');
  const [countryStatus, setCountryStatus] = useState<string>('');
  const [countryEndDate, setCountryEndDate] = useState<string>('');
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [currentAcquisitionStatus, setCurrentAcquisitionStatus] = useState<string>('Planning');
  const [currentAcquisitionDates, setCurrentAcquisitionDates] = useState<{rif_start_date?: string, rif_end_date?: string}>({});
  
  // Add Country form state
  const [availableCountries, setAvailableCountries] = useState<string[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [employeeCount, setEmployeeCount] = useState<number>(0);
  const [availableRifTypes, setAvailableRifTypes] = useState<RifType[]>([]);
  const [selectedRifType, setSelectedRifType] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [rifTypesLoading, setRifTypesLoading] = useState<boolean>(false);
  
  // Executive summary generation state
  const [generatingReports, setGeneratingReports] = useState<{[key: string]: boolean}>({});
  const [generatingMultiCountry, setGeneratingMultiCountry] = useState<boolean>(false);
  const [pollingForReports, setPollingForReports] = useState<{[key: string]: boolean}>({});
  const [pollingIntervals, setPollingIntervals] = useState<{[key: string]: NodeJS.Timeout}>({});
  const [notifiedReports, setNotifiedReports] = useState<string[]>([]); // Track notified reports
  const [generatingProgressReport, setGeneratingProgressReport] = useState<boolean>(false);
  const [latestProgressReport, setLatestProgressReport] = useState<{date: string, url: string} | null>(null);
  const [countryExecutiveSummaries, setCountryExecutiveSummaries] = useState<{[countryId: string]: {date: string, url: string}}>({});
  const [generatingAcquisitionShotClock, setGeneratingAcquisitionShotClock] = useState<boolean>(false);
  const [latestAcquisitionShotClock, setLatestAcquisitionShotClock] = useState<{date: string, url: string, countries: string[]} | null>(null);

  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [authLoading, setAuthLoading] = useState<boolean>(true);
  const [loggingIn, setLoggingIn] = useState<boolean>(false);
  const [processingCallback, setProcessingCallback] = useState<boolean>(false);

  // API base URL - dynamically determined based on environment
  // In production (deployed), uses the same host as the browser
  // In development (localhost), uses localhost:8000
  const API_BASE_URL = window.location.hostname === 'localhost' 
    ? 'http://localhost:8000'
    : `${window.location.protocol}//${window.location.host}`;

  // Modern notification system
  const addNotification = (type: 'success' | 'error' | 'info', message: string, persistent: boolean = false, url?: string) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, type, message, url }]);
    
    // Auto-remove after 5 seconds (unless persistent)
    if (!persistent) {
      setTimeout(() => {
        setNotifications(prev => prev.filter(n => n.id !== id));
      }, 5000);
    }
  };

  const removeNotification = (id: number) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // ============================================================================
  // AUTHENTICATION FUNCTIONS
  // ============================================================================

  // Check for existing authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        setAuthLoading(false);
        setIsAuthenticated(false);
        return;
      }

      try {
        // Validate token with backend
        const response = await fetch(`${API_BASE_URL}/api/auth/validate`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          if (data.valid && data.authenticated) {
            setIsAuthenticated(true);
            setUser(data.user);
          } else {
            // Token invalid, clear it
            localStorage.removeItem('auth_token');
            setIsAuthenticated(false);
          }
        } else {
          // Token invalid, clear it
          localStorage.removeItem('auth_token');
          setIsAuthenticated(false);
        }
      } catch (err) {
        console.error('Auth check failed:', err);
        localStorage.removeItem('auth_token');
        setIsAuthenticated(false);
      } finally {
        setAuthLoading(false);
      }
    };

    checkAuth();
  }, []);

  // Handle Google OAuth login
  const handleGoogleLogin = async () => {
    setLoggingIn(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/google`);
      const data = await response.json();
      
      if (data.auth_url) {
        // Redirect to Google OAuth
        window.location.href = data.auth_url;
      }
    } catch (err) {
      console.error('Login failed:', err);
      addNotification('error', 'Failed to initiate login');
      setLoggingIn(false);
    }
  };

  // Handle OAuth callback
  useEffect(() => {
    const handleCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      
      if (code && !isAuthenticated && !processingCallback) {
        setProcessingCallback(true);
        
        try {
          const response = await fetch(`${API_BASE_URL}/api/auth/callback`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              code,
              origin: window.location.origin
            })
          });

          if (response.ok) {
            const data = await response.json();
            
            // Store token
            localStorage.setItem('auth_token', data.access_token);
            
            // Set user state
            setIsAuthenticated(true);
            setUser(data.user);
            
            // Clean URL (remove code parameter)
            window.history.replaceState({}, document.title, window.location.pathname);
            
            addNotification('success', `Welcome, ${data.user.name}!`);
          } else {
            const error = await response.json();
            
            // Don't show error for duplicate code attempts (already logged in)
            if (!error.detail?.includes('invalid_grant') || !isAuthenticated) {
              addNotification('error', error.detail || 'Authentication failed');
            }
            
            // Clean URL
            window.history.replaceState({}, document.title, window.location.pathname);
          }
        } catch (err) {
          console.error('Callback error:', err);
          
          // Only show error if not already authenticated
          if (!isAuthenticated) {
            addNotification('error', 'Authentication failed');
          }
          
          // Clean URL
          window.history.replaceState({}, document.title, window.location.pathname);
        } finally {
          setProcessingCallback(false);
        }
      }
    };

    handleCallback();
  }, []);

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    setIsAuthenticated(false);
    setUser(null);
    addNotification('info', 'Logged out successfully');
  };

  // Helper function to make authenticated API calls
  const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    
    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`
    };

    return fetch(url, { ...options, headers });
  };

  // ============================================================================
  // END AUTHENTICATION FUNCTIONS
  // ============================================================================

  // Function to fetch available acquisitions from your backend
  const fetchAcquisitions = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/acquisitions/available`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setAcquisitions(data.available_acquisitions || []);
      addNotification('info', `Found ${data.available_acquisitions?.length || 0} available acquisitions`);
    } catch (err) {
      const errorMsg = `Failed to fetch acquisitions: ${err}`;
      setError(errorMsg);
      addNotification('error', errorMsg);
      console.error('Error fetching acquisitions:', err);
    } finally {
      setLoading(false);
    }
  };

  // Function to clear all form data
  const clearFormData = () => {
    setSelectedCountry('');
    setEmployeeCount(0);
    setSelectedRifType('');
    setStartDate('');
    setAvailableRifTypes([]);
    setAvailableCountries([]);
    setRifTypesLoading(false);
    setShowCountryForm(false);
  };

  // Function to handle dropdown selection
  const handleAcquisitionSelect = (acquisitionId: string) => {
    setSelectedAcquisitionId(acquisitionId);
    const acquisition = acquisitions.find(a => a.id === acquisitionId);
    if (acquisition) {
      setSelectedAcquisition(acquisition);
      // Clear form data when switching acquisitions
      clearFormData();
    }
  };

  // Function to confirm acquisition selection
  const confirmAcquisitionSelection = async () => {
    if (!selectedAcquisition) return;
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/acquisitions/select/${selectedAcquisition.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Show different notification based on whether folder was created
      if (data.folder_was_created) {
      addNotification('success', `Successfully selected ${selectedAcquisition.display_name}! RIFs-Shot Clock folder structure created.`);
      } else {
        addNotification('success', `Successfully selected ${selectedAcquisition.display_name}!`);
      }
      
      // Update selected acquisition with database ID
      setSelectedAcquisition({
        ...selectedAcquisition,
        database_id: data.acquisition_id  // Store the database ID
      });
      
      // Switch to country management view
      setCurrentView('countries');
      fetchAssignedCountries(data.acquisition_id);
      
      console.log('Selection result:', data);
    } catch (err) {
      const errorMsg = `Failed to select acquisition: ${err}`;
      addNotification('error', errorMsg);
      console.error('Error selecting acquisition:', err);
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch assigned countries for an acquisition
  const fetchAssignedCountries = async (acquisitionId: number) => {
    setLoadingCountries(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/countries/acquisition/${acquisitionId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setAssignedCountries(data.countries || []);
      console.log('Assigned countries:', data.countries);
      addNotification('info', `Found ${data.countries?.length || 0} assigned countries`);
    } catch (err) {
      console.error('Error fetching assigned countries:', err);
      addNotification('error', `Failed to fetch assigned countries: ${err}`);
      setAssignedCountries([]);
    } finally {
      setLoadingCountries(false);
    }
  };

  // Function to refresh acquisition and countries data
  const refreshDashboardData = async () => {
    if (!selectedAcquisition?.database_id) return;
    
    addNotification('info', 'Refreshing dashboard data...');
    
    // Refresh countries data
    await fetchAssignedCountries(selectedAcquisition.database_id);
    
    // Refresh acquisition status and dates
    try {
      const response = await fetch(`${API_BASE_URL}/api/acquisitions/${selectedAcquisition.database_id}`);
      if (response.ok) {
        const data = await response.json();
        setCurrentAcquisitionStatus(data.status || 'Planning');
        setCurrentAcquisitionDates({
          rif_start_date: data.rif_start_date || '',
          rif_end_date: data.rif_end_date || ''
        });
      }
    } catch (err) {
      console.error('Error refreshing acquisition data:', err);
    }
    
    addNotification('success', 'Dashboard data refreshed successfully!');
  };

  // Step customization functions
  const fetchDropdownData = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/acquisition-process-steps/dropdown-data`);
      if (response.ok) {
        const data = await response.json();
        setDropdownData(data);
      }
    } catch (err) {
      console.error('Error fetching dropdown data:', err);
    }
  };

  // Function to get process types for current country (RIF type + Universal)
  const getProcessTypesForCountry = () => {
    if (!selectedCountryForProgress) return ['Universal'];
    
    const countryRifType = selectedCountryForProgress.rif_type;
    const processTypes = ['Universal'];
    
    if (countryRifType && countryRifType !== 'Universal') {
      processTypes.push(countryRifType);
    }
    
    return processTypes;
  };

  const openStepEditModal = (step: ProcessStep) => {
    setSelectedStepForEdit(step);
    setEditingStep({
      step_number: step.step_number,
      step_name: step.step_name,
      step_type: step.step_type,
      process_type: step.process_type,
      process_category: step.process_category,
      workflow_phase: step.workflow_phase,
      parent_step: step.parent_step,
      timeline_day_min: step.timeline_day_min,
      timeline_day_max: step.timeline_day_max,
      duration_days: step.duration_days,
      dri_role: step.dri_role,
      step_description: step.notes || '',
      is_critical_path: step.is_critical_path,
      is_legal_gate: step.is_legal_gate,
      is_optional: step.is_optional || false,
      risk_level: step.risk_level,
      documents_required: step.documents_required || '',
      email_templates: step.email_templates || '',
      call_scripts: step.call_scripts || '',
      depends_on: step.depends_on || ''
    });
    setShowStepEditModal(true);
    fetchDropdownData();
  };

  const openAddStepModal = () => {
    setEditingStep({
      step_number: '',
      step_name: '',
      step_type: 'Single',
      process_type: 'Universal',
      process_category: '',
      workflow_phase: 'RIF-Execution',
      parent_step: '',
      timeline_day_min: 0,
      timeline_day_max: 0,
      duration_days: 1,
      dri_role: '',
      step_description: '',
      is_critical_path: false,
      is_legal_gate: false,
      is_optional: false,
      risk_level: 'MEDIUM',
      documents_required: '',
      email_templates: '',
      call_scripts: '',
      depends_on: ''
    });
    setShowAddStepModal(true);
    fetchDropdownData();
  };

  const closeStepEditModal = () => {
    setShowStepEditModal(false);
    setSelectedStepForEdit(null);
    setEditingStep({});
  };

  const closeAddStepModal = () => {
    setShowAddStepModal(false);
    setEditingStep({});
  };

  const updateStep = async () => {
    if (!selectedStepForEdit || !selectedCountryForProgress || !selectedAcquisition?.database_id) return;
    
    setUpdatingStep(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/acquisition-process-steps/acquisition/${selectedAcquisition.database_id}/country/${selectedCountryForProgress.id}/steps/${selectedStepForEdit.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(editingStep)
        }
      );

      if (response.ok) {
        const result = await response.json();
        addNotification('success', 'Step updated successfully!');
        closeStepEditModal();
        
        // Optimized: Just refresh the single step instead of all data
        // Update the step in local state instead of full refresh
        setProcessSteps(prev => prev.map(s => 
          s.step_number === selectedStepForEdit.step_number 
            ? { ...s, ...editingStep, id: selectedStepForEdit.id }
            : s
        ));
      } else {
        const errorData = await response.json();
        addNotification('error', `Failed to update step: ${errorData.detail}`);
      }
    } catch (err) {
      addNotification('error', `Error updating step: ${err}`);
    } finally {
      setUpdatingStep(false);
    }
  };

  const addNewStep = async () => {
    if (!selectedCountryForProgress || !selectedAcquisition?.database_id) return;
    
    setUpdatingStep(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/acquisition-process-steps/acquisition/${selectedAcquisition.database_id}/country/${selectedCountryForProgress.id}/steps`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(editingStep)
        }
      );

      if (response.ok) {
        addNotification('success', 'Step added successfully!');
        closeAddStepModal();
        // Refresh the process steps
        await fetchCountryProgress(selectedCountryForProgress);
      } else {
        const errorData = await response.json();
        addNotification('error', `Failed to add step: ${errorData.detail}`);
      }
    } catch (err) {
      addNotification('error', `Error adding step: ${err}`);
    } finally {
      setUpdatingStep(false);
    }
  };

  const openDeleteConfirmModal = (step: ProcessStep) => {
    setStepToDelete(step);
    setShowDeleteConfirmModal(true);
  };

  const deleteStep = async (step: ProcessStep) => {
    if (!selectedCountryForProgress || !selectedAcquisition?.database_id) return;
    
    const confirmed = window.confirm(
      `Are you sure you want to delete step "${step.step_number}: ${step.step_name}"?\n\nThis action will:\n- Remove the step from this acquisition\n- Mark any existing progress as inactive\n- This cannot be undone`
    );
    
    if (!confirmed) return;
    
    setDeletingStep(true);
    
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/acquisition-process-steps/acquisition/${selectedAcquisition.database_id}/country/${selectedCountryForProgress.id}/steps/${step.id}`,
        {
          method: 'DELETE'
        }
      );

      if (response.ok) {
        addNotification('success', 'Step deleted successfully!');
        // Optimized: Remove step from local state instead of full refresh
        setProcessSteps(prev => prev.filter(s => s.step_number !== step.step_number));
      } else {
        const errorData = await response.json();
        addNotification('error', `Failed to delete step: ${errorData.detail}`);
      }
    } catch (err) {
      addNotification('error', `Error deleting step: ${err}`);
    } finally {
      setDeletingStep(false);
    }
  };

  // Resource editing helper functions
  const startEditingResources = (step: ProcessStep) => {
    setEditingResources(step.step_number);
    setResourceEditData({
      documents_required: step.documents_required || '',
      email_templates: step.email_templates || '',
      call_scripts: step.call_scripts || ''
    });
  };

  const cancelEditingResources = () => {
    setEditingResources(null);
    setResourceEditData({});
  };

  const saveResourceChanges = async (step: ProcessStep) => {
    if (!selectedCountryForProgress || !selectedAcquisition?.database_id) return;
    
    setSavingResources(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/acquisition-process-steps/acquisition/${selectedAcquisition.database_id}/country/${selectedCountryForProgress.id}/steps/${step.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            documents_required: resourceEditData.documents_required,
            email_templates: resourceEditData.email_templates,
            call_scripts: resourceEditData.call_scripts
          })
        }
      );

      if (response.ok) {
        addNotification('success', 'Resources updated successfully!');
        // Update local state
        setProcessSteps(prev => prev.map(s => 
          s.step_number === step.step_number 
            ? { 
                ...s, 
                documents_required: resourceEditData.documents_required,
                email_templates: resourceEditData.email_templates,
                call_scripts: resourceEditData.call_scripts
              }
            : s
        ));
        cancelEditingResources();
      } else {
        const errorData = await response.json();
        addNotification('error', `Failed to update resources: ${errorData.detail}`);
      }
    } catch (err) {
      addNotification('error', `Error updating resources: ${err}`);
    } finally {
      setSavingResources(false);
    }
  };

  // Function to get available countries (that have process flows defined)
  const getAvailableCountries = async (): Promise<string[]> => {
    try {
      // For admin console, get countries directly from Google Sheets (includes new ones)
      const response = await fetch(`${API_BASE_URL}/api/countries/available-from-sheets`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data.available_countries?.map((c: any) => c.country) || [];
    } catch (err) {
      console.error('Error fetching available countries:', err);
      // Fallback to database countries if Google Sheets fails
      try {
        const fallbackResponse = await fetch(`${API_BASE_URL}/api/countries/available`);
        if (fallbackResponse.ok) {
          const fallbackData = await fallbackResponse.json();
          return fallbackData.available_countries?.map((c: any) => c.country) || [];
        }
      } catch (fallbackErr) {
        console.error('Fallback also failed:', fallbackErr);
      }
      return [];
    }
  };

  // Function to refresh admin page data
  const refreshAdminData = async () => {
    setAdminLoading(prev => ({...prev, refreshing: true}));
    try {
      // Reload available countries
      const countries = await getAvailableCountries();
      setAdminAvailableCountries(countries);
      
      // Clear any previous results
      setAdminResults(null);
      setAdminSelectedCountry('');
      setAdminSelectedAcquisition('');
      setAdminSelectedCountryForRemoval('');
      
      addNotification('success', 'Admin data refreshed successfully');
    } catch (error) {
      addNotification('error', 'Failed to refresh admin data');
    } finally {
      setAdminLoading(prev => ({...prev, refreshing: false}));
    }
  };

  // Load available countries when admin view is accessed (lazy loading)
  React.useEffect(() => {
    if (currentView === 'admin') {
      // Load countries in background without blocking UI
      const loadAvailableCountries = async () => {
        setAdminLoading(prev => ({...prev, loadingCountries: true}));
        try {
          const countries = await getAvailableCountries();
          setAdminAvailableCountries(countries);
          addNotification('info', `Loaded ${countries.length} available countries`);
        } catch (error) {
          addNotification('error', 'Failed to load available countries');
        } finally {
          setAdminLoading(prev => ({...prev, loadingCountries: false}));
        }
      };
      
      // Start loading in background (non-blocking)
      setTimeout(() => loadAvailableCountries(), 0);
    }
  }, [currentView]);

  // Function to get applicable RIF types for a country and employee count
  const getApplicableRifTypes = async (country: string, employeeCount: number): Promise<RifType[]> => {
    try {
      // First, get all RIF types for the country to check if they have employee count criteria
      const allTypesResponse = await fetch(`${API_BASE_URL}/api/rif-types/${country}`);
      
      if (!allTypesResponse.ok) {
        throw new Error(`HTTP error! status: ${allTypesResponse.status}`);
      }
      
      const allTypesData = await allTypesResponse.json();
      const allRifTypes = allTypesData.rif_types || [];
      
      // Check if any RIF type has employee count criteria (non-null min/max values)
      const hasEmployeeCountCriteria = allRifTypes.some((rt: any) => 
        rt.employee_count_min !== null || rt.employee_count_max !== null
      );
      
      if (!hasEmployeeCountCriteria) {
        // Manual selection country (like US) - return all RIF types
        console.log(`${country} uses manual RIF type selection - showing all types`);
        return allRifTypes.map((rt: any) => ({ ...rt, is_applicable: true }));
      } else {
        // Employee count-based country (like UK) - use smart filtering
        console.log(`${country} uses employee count-based RIF type filtering`);
      const response = await fetch(`${API_BASE_URL}/api/rif-types/${country}/applicable?employee_count=${employeeCount}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return [...(data.applicable_rif_types || []), ...(data.not_applicable_rif_types || [])];
      }
    } catch (err) {
      console.error('Error fetching RIF types:', err);
      return [];
    }
  };

  // Function to handle employee count change and update RIF types
  const handleEmployeeCountChange = async (count: number) => {
    setEmployeeCount(count);
    setSelectedRifType(''); // Reset RIF type selection
    setAvailableRifTypes([]); // Clear existing RIF types immediately
    
    if (selectedCountry && count > 0) {
      setRifTypesLoading(true); // Show loading state
      
      try {
        // Small delay to ensure loading state is visible
        await new Promise(resolve => setTimeout(resolve, 300));
        
        const rifTypes = await getApplicableRifTypes(selectedCountry, count);
        
        // Only update state after loading completes
        setAvailableRifTypes(rifTypes);
        setSelectedRifType(''); // Keep empty until user explicitly selects
        
      } catch (err) {
        console.error('Error fetching RIF types:', err);
        setAvailableRifTypes([]);
        setSelectedRifType('');
      } finally {
        setRifTypesLoading(false); // Hide loading state
      }
    } else {
      setRifTypesLoading(false);
    }
  };

  // Function to handle country selection
  const handleCountrySelection = async (country: string) => {
    setSelectedCountry(country);
    setSelectedRifType(''); // Reset RIF type
    setAvailableRifTypes([]); // Clear RIF types immediately
    setRifTypesLoading(false); // Reset loading state
    
    if (employeeCount > 0) {
      await handleEmployeeCountChange(employeeCount);
    }
  };

  // Function to submit country assignment
  const submitCountryAssignment = async () => {
    if (!selectedAcquisition || !selectedCountry || !selectedRifType || employeeCount <= 0) {
      addNotification('error', 'Please fill in all required fields');
      return;
    }

    // Check if we have a valid acquisition ID
    console.log('Selected acquisition:', selectedAcquisition);
    if (!selectedAcquisition.id) {
      addNotification('error', 'Invalid acquisition selected');
      return;
    }

    setLoading(true);
    
    const requestData = {
      country: selectedCountry,
      employee_count: employeeCount,
      rif_type: selectedRifType,
      start_date: startDate || new Date().toISOString().split('T')[0],
      added_by: 'user@company.com' // TODO: Get from auth system
    };
    
    console.log('Sending country assignment request:', requestData);
    console.log('Acquisition folder ID:', selectedAcquisition.id);
    console.log('Acquisition database ID:', selectedAcquisition.database_id);
    
    if (!selectedAcquisition.database_id) {
      addNotification('error', 'Acquisition not properly selected. Please reselect the acquisition.');
      setLoading(false);
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/countries/assign-to-acquisition/${selectedAcquisition.database_id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      addNotification('success', `${selectedCountry} successfully assigned with ${selectedRifType} process!`);
      
      // Reset form and refresh countries
      setShowCountryForm(false);
      setSelectedCountry('');
      setEmployeeCount(0);
      setSelectedRifType('');
      setStartDate('');
      setAvailableRifTypes([]);
      
      // Refresh assigned countries
      if (selectedAcquisition?.database_id) {
        fetchAssignedCountries(selectedAcquisition.database_id);
      }
      
    } catch (err) {
      addNotification('error', `Failed to assign country: ${err}`);
    } finally {
      setLoading(false);
    }
  };

  // Function to open Add Country form
  const openAddCountryForm = async () => {
    setShowCountryForm(true);
    
    // Load available countries
    try {
      const countries = await getAvailableCountries();
      setAvailableCountries(countries);
    } catch (err) {
      addNotification('error', 'Failed to load available countries');
    }
  };

  // Function to stop all polling for a specific report
  const stopPollingForReport = (reportKey: string) => {
    console.log(`🛑 Stopping all polling for ${reportKey}`);
    
    // Clear interval if it exists
    if (pollingIntervals[reportKey]) {
      clearInterval(pollingIntervals[reportKey]);
      console.log(`Cleared interval for ${reportKey}`);
    }
    
    // Update states
    setPollingForReports(prev => ({ ...prev, [reportKey]: false }));
    setPollingIntervals(prev => {
      const newIntervals = { ...prev };
      delete newIntervals[reportKey];
      return newIntervals;
    });
  };

  // Function to start polling for report completion
  const startPollingForReport = (reportKey: string, country: string, acquisitionId: number) => {
    // Stop any existing polling for this report first
    stopPollingForReport(reportKey);
    
    console.log(`🚀 Starting fresh polling for ${reportKey}`);
    
    setPollingForReports(prev => ({ ...prev, [reportKey]: true }));
    
    let pollCount = 0;
    const maxPolls = 30; // 5 minutes (30 * 10 seconds)
    let shouldContinuePolling = true; // Flag to control polling
    
    const intervalId = setInterval(async () => {
      if (!shouldContinuePolling) {
        clearInterval(intervalId);
        return;
      }
      
      pollCount++;
      
      try {
        const response = await fetch(`${API_BASE_URL}/api/executive-summaries/${acquisitionId}`);
        if (response.ok) {
          const data = await response.json();
          console.log(`Polling attempt ${pollCount}: Found ${data.summaries?.length || 0} total summaries`);
          
          // Check if there's a new report (within last 10 minutes) - TIMEZONE SAFE
          const recentReports = data.summaries?.filter((summary: any) => {
            // Parse database time as UTC (add 'Z' if not present)
            const dbTimeString = summary.generated_date.endsWith('Z') ? 
              summary.generated_date : summary.generated_date + 'Z';
            
            const generatedTime = new Date(dbTimeString).getTime();
            const currentTime = Date.now(); // Always UTC
            const tenMinutesAgo = currentTime - (10 * 60 * 1000);
            const hasCountry = JSON.parse(summary.countries).includes(country);
            
            const isRecent = generatedTime > tenMinutesAgo;
            const timeDiffMs = currentTime - generatedTime;
            const timeDiffMinutes = timeDiffMs / 1000 / 60;
            
            console.log(`Checking summary: ${summary.id}`);
            console.log(`  Generated (DB): ${summary.generated_date}`);
            console.log(`  Generated (UTC): ${dbTimeString}`);
            console.log(`  Generated timestamp: ${generatedTime}`);
            console.log(`  Current timestamp: ${currentTime}`);
            console.log(`  Time diff MS: ${timeDiffMs}`);
            console.log(`  Time diff: ${timeDiffMinutes.toFixed(1)} minutes ago`);
            console.log(`  Has ${country}: ${hasCountry}`);
            console.log(`  Is recent (< 10 min): ${isRecent}`);
            console.log(`  Matches criteria: ${isRecent && hasCountry}`);
            console.log('---');
            
            return isRecent && hasCountry;
          });
          
          if (recentReports && recentReports.length > 0) {
            const report = recentReports[0];
            const reportId = `${country}_${report.id}`;
            
            // Check if we've already notified for this report
            if (notifiedReports.includes(reportId)) {
              console.log(`Already notified for report ${reportId}, skipping...`);
              return;
            }
            
            // Report found! IMMEDIATELY stop polling
            console.log(`🎉 Report found for ${country}! Stopping polling.`);
            shouldContinuePolling = false; // Stop polling flag
            stopPollingForReport(reportKey); // Use centralized stop function
            
            // Mark this report as notified
            setNotifiedReports(prev => [...prev, reportId]);
            
            const fileUrl = report.google_sheets_url;
            
            // Create persistent notification with clickable link
            const notificationMessage = `✅ ${country} Shot Clock completed!`;
            addNotification('success', notificationMessage, true, fileUrl); // Persistent with URL
            
            console.log(`Report completed for ${country}:`, report);
            console.log(`File URL: ${fileUrl}`);
            console.log(`🔚 Polling should be completely stopped now.`);
            
            return; // Exit the polling function
          }
        }
        
        // Stop polling after max attempts
        if (pollCount >= maxPolls) {
          console.log(`⏰ Polling timeout for ${country} after ${maxPolls} attempts`);
          shouldContinuePolling = false;
          clearInterval(intervalId);
          setPollingForReports(prev => ({ ...prev, [reportKey]: false }));
          addNotification('info', `${country} Shot Clock generation is taking longer than expected. Please check your acquisition folder.`);
          
          setPollingIntervals(prev => {
            const newIntervals = { ...prev };
            delete newIntervals[reportKey];
            return newIntervals;
          });
        }
        
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 10000); // Poll every 10 seconds
    
    // Store interval reference in state for cleanup
    setPollingIntervals(prev => ({ ...prev, [reportKey]: intervalId }));
  };

  // Function to generate single-country executive summary
  const generateCountryReport = async (country: Country) => {
    if (!selectedAcquisition?.database_id) {
      addNotification('error', 'No acquisition selected');
      return;
    }

    const reportKey = `${country.country}_${country.id}`;
    setGeneratingReports(prev => ({ ...prev, [reportKey]: true }));

    try {
      const response = await fetch(`${API_BASE_URL}/api/executive-summaries/generate-country-direct`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          acquisition_id: selectedAcquisition.database_id,
          country: country.country,
          employee_count: country.employee_count,
          rif_type: country.rif_type,
          generated_by: 'user@company.com' // TODO: Get from auth system
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      
      // Show success notification with link if available
      if (data.spreadsheet_url) {
        addNotification('success', `${country.country} Executive Summary generated!`, true, data.spreadsheet_url);
      } else {
        addNotification('success', `${country.country} Executive Summary generated successfully! Check your acquisition folder.`);
      }
      
      console.log('Executive summary generated:', data);
      
    } catch (err) {
      addNotification('error', `Failed to generate ${country.country} report: ${err}`);
      console.error('Error generating country report:', err);
    } finally {
      setGeneratingReports(prev => ({ ...prev, [reportKey]: false }));
    }
  };

  // Function to generate multi-country overview
  const generateMultiCountryOverview = async () => {
    if (!selectedAcquisition?.database_id || assignedCountries.length < 2) {
      addNotification('error', 'Need at least 2 countries for multi-country overview');
      return;
    }

    setGeneratingMultiCountry(true);

    try {
      const countries = assignedCountries.map(c => c.country);
      const employeeCounts = assignedCountries.reduce((acc, c) => {
        acc[c.country] = c.employee_count;
        return acc;
      }, {} as {[key: string]: number});

      const response = await fetch(`${API_BASE_URL}/api/executive-summaries/generate-overview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          acquisition_id: selectedAcquisition.database_id,
          countries: countries,
          employee_counts: employeeCounts,
          additional_context: `Multi-country RIF overview for ${selectedAcquisition.display_name}`,
          generated_by: 'user@company.com' // TODO: Get from auth system
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      addNotification('success', `Multi-Country Executive Overview generation started! This may take 60-90 seconds.`);
      
      console.log('Multi-country report generation started:', data);
      
    } catch (err) {
      addNotification('error', `Failed to generate multi-country overview: ${err}`);
      console.error('Error generating multi-country overview:', err);
    } finally {
      setGeneratingMultiCountry(false);
    }
  };

  // Function to generate acquisition shot clock (multi-country progress reports)
  const generateAcquisitionShotClock = async () => {
    if (!selectedAcquisition?.database_id || assignedCountries.length === 0) {
      addNotification('error', 'No countries assigned to this acquisition');
      return;
    }

    setGeneratingAcquisitionShotClock(true);

    try {
      const countries = assignedCountries.map(c => c.country);
      
      addNotification('info', `Generating Acquisition Shot Clock with Executive Summary for ${countries.length} countries...`);
      
      const response = await fetch(`${API_BASE_URL}/api/process-steps/acquisition/${selectedAcquisition.database_id}/generate-acquisition-shot-clock`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          countries: countries,
          acquisition_name: selectedAcquisition.display_name,
          generated_by: 'user@company.com' // TODO: Get from auth system
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      
      // Show success notification with link if available
      if (data.spreadsheet_url) {
        addNotification('success', `Acquisition Shot Clock generated! Multi-country report with Executive Summary and ${countries.length} progress tabs created.`, true, data.spreadsheet_url);
      } else {
        addNotification('success', `Acquisition Shot Clock generation started! Check your Reports folder for the multi-country report with Executive Summary.`);
      }
      
      // Refresh the latest acquisition shot clock report
      await fetchLatestAcquisitionShotClock();
      
      console.log('Acquisition shot clock generated:', data);
      
    } catch (err) {
      addNotification('error', `Failed to generate acquisition shot clock: ${err}`);
      console.error('Error generating acquisition shot clock:', err);
    } finally {
      setGeneratingAcquisitionShotClock(false);
    }
  };

  // Function to fetch process steps and progress for a country
  const fetchCountryProgress = async (country: Country) => {
    if (!selectedAcquisition?.database_id) return;
    
    try {
      setLoading(true);
      
      // Fetch process steps for this country
      const apiUrl = `${API_BASE_URL}/api/process-steps/acquisition/${selectedAcquisition.database_id}/country/${country.country}`;
      console.log(`Calling API: ${apiUrl}`);
      
      const stepsResponse = await fetch(apiUrl);
      
      if (!stepsResponse.ok) {
        const errorText = await stepsResponse.text();
        console.error(`API Error: ${stepsResponse.status} - ${errorText}`);
        throw new Error(`Failed to fetch process steps: ${stepsResponse.status} - ${errorText}`);
      }
      
      const stepsData = await stepsResponse.json();
      console.log('API Response:', stepsData);
      
      // Use correct field names from API response
      const allSteps = stepsData.steps || [];
      const completions = stepsData.completions || [];  // Use completions field directly
      
      // Filter steps to only show Universal + the country's RIF type
      const relevantSteps = allSteps.filter((step: any) => 
        step.process_type === 'Universal' || step.process_type === country.rif_type
      );
      
      console.log(`Filtering ${allSteps.length} total steps to ${relevantSteps.length} relevant steps`);
      console.log(`RIF Type: ${country.rif_type}`);
      const processTypes = allSteps.map((s: any) => s.process_type).filter((value: string, index: number, self: string[]) => self.indexOf(value) === index);
      console.log('Process types found:', processTypes);
      
      // Debug workflow phases in filtered steps
      const workflowPhases = relevantSteps.map((s: any) => s.workflow_phase).filter((value: string, index: number, self: string[]) => self.indexOf(value) === index);
      console.log('Workflow phases in filtered steps:', workflowPhases);
      console.log('RIF Finalization steps in filtered data:', relevantSteps.filter((s: any) => s.workflow_phase === 'RIF Finalization').length);
      
      setProcessSteps(relevantSteps);
      setStepCompletions(completions);
      setRifStartDate(stepsData.rif_start_date);
      
      // Load latest progress report for this country
      await fetchLatestProgressReport(country);
      
      console.log(`Loaded ${relevantSteps.length} process steps for ${country.country}`);
      console.log(`Found ${completions.length} completed steps`);
      console.log('First few steps:', relevantSteps.slice(0, 3));
      console.log('Step completions:', completions.slice(0, 3));
      console.log('RIF Start Date:', stepsData.rif_start_date);
      
      // Debug: Check what fields are available in steps
      if (relevantSteps.length > 0) {
        console.log('Available fields in first step:', Object.keys(relevantSteps[0]));
      }
      
    } catch (err) {
      addNotification('error', `Failed to load progress data: ${err}`);
      console.error('Error fetching country progress:', err);
    } finally {
      setLoading(false);
    }
  };

  // Function to open progress tracking for a country
  const openProgressTracking = async (country: Country) => {
    setSelectedCountryForProgress(country);
    setCurrentView('progress');
    // Reset expanded groups (start with all collapsed)
    setExpandedGroups({});
    await fetchCountryProgress(country);
  };

  // Function to toggle group expansion
  const toggleGroupExpansion = (processType: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [processType]: !prev[processType]
    }));
  };

  // Function to toggle parent step expansion
  const toggleParentStepExpansion = (stepNumber: string) => {
    setExpandedParentSteps(prev => ({
      ...prev,
      [stepNumber]: !prev[stepNumber]
    }));
  };

  // Function to expand/collapse all workflow phases
  const toggleExpandAll = () => {
    const newExpandedState = !allExpanded;
    setAllExpanded(newExpandedState);
    
    if (newExpandedState) {
      // Expand all workflow phases
      const allPhases = organizeStepsByWorkflowPhase(processSteps).reduce((acc, group) => {
        acc[group.processType] = true;
        return acc;
      }, {} as {[key: string]: boolean});
      setExpandedGroups(allPhases);
    } else {
      // Collapse all
      setExpandedGroups({});
    }
  };

  // Function to get workflow phase colors (Navy & Gold theme)
  const getWorkflowPhaseColors = (phase: string) => {
    switch (phase) {
      case 'Pre-RIF':
        return {
          background: 'linear-gradient(135deg, #0A2540, #1A365D)', // Navy
          light: '#F5E6D3',  // Champagne
          text: '#0A2540'    // Navy
        };
      case 'RIF-Execution':
        return {
          background: 'linear-gradient(135deg, #D4AF37, #C4A028)', // Gold
          light: '#FFF9E6',  // Light gold
          text: '#8B7500'    // Dark gold
        };
      case 'RIF Finalization':
        return {
          background: 'linear-gradient(135deg, #50C878, #3FA760)', // Emerald
          light: '#E8F5E9',  // Light green
          text: '#2E7D32'    // Dark green
        };
      default:
        return {
          background: 'linear-gradient(135deg, #2C3E50, #34495E)', // Charcoal
          light: '#F8F4E8',  // Pearl
          text: '#2C3E50'    // Charcoal
        };
    }
  };

  // Function to calculate step start date
  const calculateStepStartDate = (step: ProcessStep): Date | null => {
    if (!rifStartDate || step.timeline_day_min === null || step.timeline_day_min === undefined) {
      return null;
    }
    
    const startDate = new Date(rifStartDate);
    startDate.setDate(startDate.getDate() + step.timeline_day_min);
    return startDate;
  };

  // Function to calculate weighted progress percentage
  const calculateWeightedProgress = (): number => {
    if (processSteps.length === 0) return 0;
    
    const completedSteps = stepCompletions.filter(c => c.status === 'Completed').length;
    const inProgressSteps = stepCompletions.filter(c => c.status === 'In Progress').length;
    const blockedSteps = stepCompletions.filter(c => c.status === 'Blocked').length;
    
    // Weighted calculation: Completed=100%, In Progress=60%, Blocked=20%, Pending=0%
    const weightedProgress = (completedSteps * 1.0) + (inProgressSteps * 0.6) + (blockedSteps * 0.2);
    return Math.round((weightedProgress / processSteps.length) * 100);
  };

  // Function to get the planned start date from database (step_completions table)
  const getPlannedStartDate = (step: ProcessStep): Date | null => {
    // First priority: Use the database-stored planned start date from step_completions table
    const completion = stepCompletions.find(c => c.step_number === step.step_number);
    if (completion && completion.planned_start_date) {
      return new Date(completion.planned_start_date);
    }
    
    // Second priority: If no completion record exists, calculate from RIF start date
    // This ensures steps always show a planned date even if not in completions table yet
    return calculateStepStartDate(step);
  };

  // Function to calculate tentative end date using database planned start date
  const calculateTentativeEndDate = (step: ProcessStep): Date | null => {
    const stepStartDate = getPlannedStartDate(step);  // Use database planned date
    if (!stepStartDate || !step.duration_days) {
      return null;
    }
    
    const endDate = new Date(stepStartDate);
    endDate.setDate(endDate.getDate() + step.duration_days - 1); // -1 because duration includes start day
    return endDate;
  };

  // Function to format date for display
  const formatDateForDisplay = (date: Date): string => {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // Function to parse resource links (accepts any valid URL) from database fields
  const parseGoogleDriveLinks = (fieldContent: string | null, fieldType: string) => {
    if (!fieldContent) return [];
    
    // Enhanced regex to catch ANY valid URL (http:// or https://)
    const urlRegex = /https?:\/\/[^\s,\n\r]+/g;
    const links = fieldContent.match(urlRegex) || [];
    
    return links.map((link, index) => {
      // Try to extract meaningful names from the content around the URL
      const beforeLink = fieldContent.substring(0, fieldContent.indexOf(link));
      const afterLink = fieldContent.substring(fieldContent.indexOf(link) + link.length);
      
      // Look for text immediately before the link that might be the name
      const nameMatch = beforeLink.match(/([^,\n\r]+)[\s]*$/);
      let resourceName = nameMatch ? nameMatch[1].trim() : `${fieldType} ${index + 1}`;
      
      // Clean up the name (remove common prefixes)
      resourceName = resourceName.replace(/^[-•*]\s*/, '').trim();
      
      // If name is too short or generic, use default
      if (resourceName.length < 3 || resourceName.toLowerCase().includes('http')) {
        resourceName = `${fieldType} ${index + 1}`;
      }
      
      return {
        name: resourceName,
        url: link
      };
    });
  };

  // Function to organize steps into sequential workflow structure
  const organizeStepsByWorkflowPhase = (steps: ProcessStep[]) => {
    // Group by workflow_phase for sequential workflow
    const stepsByPhase = steps.reduce((acc, step) => {
      const phase = step.workflow_phase || 'RIF-Execution';
      if (!acc[phase]) {
        acc[phase] = [];
      }
      acc[phase].push(step);
      return acc;
    }, {} as {[key: string]: ProcessStep[]});
    
    // Define phase order for logical workflow sequence
    const phaseOrder = ['Pre-RIF', 'RIF-Execution', 'RIF Finalization'];
    
    // Then organize each workflow phase by step hierarchy
    const organizedStructure = phaseOrder
      .filter(phase => stepsByPhase[phase]) // Only include phases that have steps
      .map(phase => {
        const phaseSteps = stepsByPhase[phase];
      
        // Separate Parent, Single, and Sub-Steps within this phase
        const parentSteps = phaseSteps.filter(step => step.step_type === 'Parent');
        const singleSteps = phaseSteps.filter(step => step.step_type === 'Single');
        const subSteps = phaseSteps.filter(step => step.step_type === 'Sub-Step');
        
        console.log(`${phase} workflow phase breakdown:`);
        console.log(`  Parent steps: ${parentSteps.length}`, parentSteps.map(p => p.step_number));
        console.log(`  Single steps: ${singleSteps.length}`, singleSteps.map(s => s.step_number));
        console.log(`  Sub-steps: ${subSteps.length}`, subSteps.map(s => `${s.step_number} (parent: ${s.parent_step})`));
      
      // Group sub-steps under their parent steps
      const hierarchicalSteps: (ProcessStep & { isParent: boolean; subSteps: ProcessStep[] })[] = [];
      
      // Add Parent steps with their sub-steps
      parentSteps.forEach(parentStep => {
        const matchingSubSteps = subSteps.filter(subStep => subStep.parent_step === parentStep.step_number);
        
        console.log(`Parent step ${parentStep.step_number} (${parentStep.step_name}):`);
        console.log(`  Looking for sub-steps with parent_step = "${parentStep.step_number}"`);
        console.log(`  Found ${matchingSubSteps.length} matching sub-steps:`, matchingSubSteps.map(s => s.step_number));
        
        hierarchicalSteps.push({
          ...parentStep,
          isParent: true,
          subSteps: matchingSubSteps
        });
      });
      
      // Add Single steps
      singleSteps.forEach(singleStep => {
        hierarchicalSteps.push({
          ...singleStep,
          isParent: false,
          subSteps: []
        });
      });
      
      // Sort by step_number
      hierarchicalSteps.sort((a, b) => {
        const aNum = parseFloat(a.step_number.replace(/[^\d.]/g, '')) || 0;
        const bNum = parseFloat(b.step_number.replace(/[^\d.]/g, '')) || 0;
        return aNum - bNum;
      });
      
        // Create display name for the phase
        let displayName = phase;
        if (phase === 'RIF-Execution' && selectedCountryForProgress) {
          displayName = `${selectedCountryForProgress.rif_type} - RIF Execution`;
        }
        
        return {
          processType: phase,  // Keep original phase for state management
          displayName: displayName,  // Use enhanced name for display
          stepCount: phaseSteps.length,
          steps: hierarchicalSteps
        };
    });
    
    console.log('Organized step structure:', organizedStructure);
    console.log('Available workflow phases:', Object.keys(stepsByPhase));
    console.log('Phase order filter results:', phaseOrder.map(phase => ({
      phase,
      hasSteps: !!stepsByPhase[phase],
      stepCount: stepsByPhase[phase]?.length || 0
    })));
    
    return organizedStructure;
  };

  // Function to get all templates/documents for a step
  const getStepResources = (step: ProcessStep) => {
    const resources = [];
    
    // Debug: Log what we're parsing
    if (step.documents_required || step.email_templates || step.call_scripts) {
      console.log(`Parsing resources for step ${step.step_number}:`);
      console.log('  documents_required:', step.documents_required);
      console.log('  email_templates:', step.email_templates);
      console.log('  call_scripts:', step.call_scripts);
    }
    
    // Documents Required
    const documents = parseGoogleDriveLinks(step.documents_required || null, 'Document');
    console.log(`  Found ${documents.length} documents:`, documents);
    resources.push(...documents.map(d => ({ ...d, type: 'document', icon: '📄' })));
    
    // Email Templates
    const emails = parseGoogleDriveLinks(step.email_templates || null, 'Email Template');
    console.log(`  Found ${emails.length} email templates:`, emails);
    resources.push(...emails.map(e => ({ ...e, type: 'email', icon: '📧' })));
    
    // Call Scripts
    const scripts = parseGoogleDriveLinks(step.call_scripts || null, 'Call Script');
    console.log(`  Found ${scripts.length} call scripts:`, scripts);
    resources.push(...scripts.map(s => ({ ...s, type: 'script', icon: '📞' })));
    
    console.log(`  Total resources for ${step.step_number}: ${resources.length}`);
    
    return resources;
  };

  // Function to get completion links for a step
  const getCompletionLinks = (step: ProcessStep) => {
    const completion = stepCompletions.find(c => c.step_number === step.step_number);
    if (!completion || !completion.completion_links) return [];
    
    return parseGoogleDriveLinks(completion.completion_links, 'Completion File');
  };

  // Helper functions for dynamic field labels based on status
  const getDateLabel = (status: string) => {
    switch(status) {
      case 'Completed': return 'Completion Date';
      case 'Blocked': return 'Blocked Date';
      case 'Skipped': return 'Skip Date';
      case 'In Progress': return 'Start Date';
      default: return 'Date';
    }
  };

  const getNotesLabel = (status: string) => {
    switch(status) {
      case 'Blocked': return 'Block Reason';
      case 'Skipped': return 'Skip Reason';
      case 'In Progress': return 'Progress Notes';
      case 'Completed': return 'Completion Notes';
      default: return 'Notes';
    }
  };

  const getNotesPlaceholder = (status: string) => {
    switch(status) {
      case 'Blocked': return 'Explain why this step is blocked and what needs to be resolved...';
      case 'Skipped': return 'Explain why this step was skipped...';
      case 'In Progress': return 'Add any progress notes or current status...';
      case 'Completed': return 'Add any completion notes or lessons learned...';
      default: return 'Add any additional notes...';
    }
  };

  // Function to calculate parent step status based on sub-steps
  const calculateParentStepStatus = (parentStep: any): string => {
    if (!parentStep.subSteps || parentStep.subSteps.length === 0) {
      return 'Pending'; // No sub-steps, default to Pending
    }
    
    const subStepStatuses = parentStep.subSteps.map((subStep: any) => {
      const completion = stepCompletions.find(c => c.step_number === subStep.step_number);
      return completion?.status || 'Pending';
    });
    
    // Count statuses
    const hasPending = subStepStatuses.some((status: string) => status === 'Pending');
    const hasInProgress = subStepStatuses.some((status: string) => status === 'In Progress');
    const hasCompleted = subStepStatuses.some((status: string) => status === 'Completed');
    const hasBlocked = subStepStatuses.some((status: string) => status === 'Blocked');
    const hasSkipped = subStepStatuses.some((status: string) => status === 'Skipped');
    const allPending = subStepStatuses.every((status: string) => status === 'Pending');
    
    // Rule 1: If all sub-steps are Pending → Parent = Pending
    if (allPending) {
      return 'Pending';
    }
    
    // Rule 2: If any sub-step is Pending or In Progress → Parent = In Progress
    if (hasPending || hasInProgress) {
      return 'In Progress';
    }
    
    // Rule 3: No Pending/In Progress + At least 1 Completed → Parent = Completed
    if (hasCompleted) {
      return 'Completed';
    }
    
    // Rule 4: No Pending/In Progress/Completed + Any Blocked → Parent = Blocked
    if (hasBlocked) {
      return 'Blocked';
    }
    
    // Rule 5: No Pending/In Progress/Completed + Any Skipped → Parent = Skipped
    if (hasSkipped) {
      return 'Skipped';
    }
    
    // Default to Pending (shouldn't reach here normally)
    return 'Pending';
  };

  // Function to get parent step date display based on sub-steps
  const getParentStepDateDisplay = (parentStep: any): string => {
    if (!parentStep.subSteps || parentStep.subSteps.length === 0) {
      return 'No sub-steps defined';
    }
    
    const parentStatus = calculateParentStepStatus(parentStep);
    const plannedStart = getPlannedStartDate(parentStep);
    const plannedEnd = calculateTentativeEndDate(parentStep);
    
    // Get sub-step completion data
    const subStepCompletions = parentStep.subSteps.map((subStep: any) => 
      stepCompletions.find((c: any) => c.step_number === subStep.step_number)
    ).filter(Boolean);
    
    // Find earliest start date and latest completion date from sub-steps
    const startDates = subStepCompletions
      .map((c: any) => c.started_date)
      .filter(Boolean)
      .map((date: any) => new Date(date));
    
    const completionDates = subStepCompletions
      .map((c: any) => c.completed_date)
      .filter(Boolean)
      .map((date: any) => new Date(date));
    
    const earliestStart = startDates.length > 0 ? new Date(Math.min(...startDates.map((d: any) => d.getTime()))) : null;
    const latestCompletion = completionDates.length > 0 ? new Date(Math.max(...completionDates.map((d: any) => d.getTime()))) : null;
    
    switch (parentStatus) {
      case 'Pending':
        return `Planned Start: ${plannedStart?.toLocaleDateString() || 'TBD'} | Tentative End: ${plannedEnd?.toLocaleDateString() || 'TBD'}`;
      
      case 'In Progress':
        if (earliestStart) {
          return `Planned Start: ${plannedStart?.toLocaleDateString() || 'TBD'} | Started: ${earliestStart.toLocaleDateString()} | Est. End: ${plannedEnd?.toLocaleDateString() || 'TBD'}`;
        }
        return `Planned Start: ${plannedStart?.toLocaleDateString() || 'TBD'} | Est. End: ${plannedEnd?.toLocaleDateString() || 'TBD'}`;
      
      case 'Completed':
        if (earliestStart && latestCompletion) {
          return `Planned Start: ${plannedStart?.toLocaleDateString() || 'TBD'} | Started: ${earliestStart.toLocaleDateString()} | Completed: ${latestCompletion.toLocaleDateString()}`;
        }
        return `Planned Start: ${plannedStart?.toLocaleDateString() || 'TBD'} | Status: All sub-steps completed`;
      
      case 'Blocked':
      case 'Skipped':
        return `Planned Start: ${plannedStart?.toLocaleDateString() || 'TBD'} | Status: ${parentStatus}`;
      
      default:
        return `Planned Start: ${plannedStart?.toLocaleDateString() || 'TBD'}`;
    }
  };

  // Function to get parent step date display with edit button next to "Planned Start"
  const getParentStepDateDisplayWithEdit = (parentStep: any) => {
    if (!parentStep.subSteps || parentStep.subSteps.length === 0) {
      return <span>No sub-steps defined</span>;
    }
    
    const parentStatus = calculateParentStepStatus(parentStep);
    const plannedStart = getPlannedStartDate(parentStep);
    const plannedEnd = calculateTentativeEndDate(parentStep);
    
    // Get sub-step completion data
    const subStepCompletions = parentStep.subSteps.map((subStep: any) => 
      stepCompletions.find((c: any) => c.step_number === subStep.step_number)
    ).filter(Boolean);
    
    // Find earliest start date and latest completion date from sub-steps
    const startDates = subStepCompletions
      .map((c: any) => c.started_date)
      .filter(Boolean)
      .map((date: any) => new Date(date));
    
    const completionDates = subStepCompletions
      .map((c: any) => c.completed_date)
      .filter(Boolean)
      .map((date: any) => new Date(date));
    
    const earliestStart = startDates.length > 0 ? new Date(Math.min(...startDates.map((d: any) => d.getTime()))) : null;
    const latestCompletion = completionDates.length > 0 ? new Date(Math.max(...completionDates.map((d: any) => d.getTime()))) : null;
    
    const editButton = (
      <button
        onClick={(e) => {
          e.stopPropagation();
          openPlannedDateEditModal(parentStep);
        }}
        style={{
          background: 'none',
          border: '1px solid #bdc3c7',
          borderRadius: '3px',
          padding: '1px 3px',
          cursor: 'pointer',
          fontSize: '8px',
          color: '#6B7280',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '2px',
          transition: 'all 0.2s ease',
          marginLeft: '4px',
          verticalAlign: 'middle'
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.background = '#ecf0f1';
          e.currentTarget.style.color = '#0A2540';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.background = 'none';
          e.currentTarget.style.color = '#6B7280';
        }}
        title="Edit Planned Start Date"
      >
        📅
      </button>
    );
    
    switch (parentStatus) {
      case 'Pending':
        return (
          <span>
            Planned Start: {plannedStart?.toLocaleDateString() || 'TBD'} {editButton} | Tentative End: {plannedEnd?.toLocaleDateString() || 'TBD'}
          </span>
        );
      
      case 'In Progress':
        if (earliestStart) {
          return (
            <span>
              Planned Start: {plannedStart?.toLocaleDateString() || 'TBD'} {editButton} | Started: {earliestStart.toLocaleDateString()} | Est. End: {plannedEnd?.toLocaleDateString() || 'TBD'}
            </span>
          );
        }
        return (
          <span>
            Planned Start: {plannedStart?.toLocaleDateString() || 'TBD'} {editButton} | Est. End: {plannedEnd?.toLocaleDateString() || 'TBD'}
          </span>
        );
      
      case 'Completed':
        if (earliestStart && latestCompletion) {
          return (
            <span>
              Planned Start: {plannedStart?.toLocaleDateString() || 'TBD'} {editButton} | Started: {earliestStart.toLocaleDateString()} | Completed: {latestCompletion.toLocaleDateString()}
            </span>
          );
        }
        return (
          <span>
            Planned Start: {plannedStart?.toLocaleDateString() || 'TBD'} {editButton} | Status: All sub-steps completed
          </span>
        );
      
      case 'Blocked':
      case 'Skipped':
        return (
          <span>
            Planned Start: {plannedStart?.toLocaleDateString() || 'TBD'} {editButton} | Status: {parentStatus}
          </span>
        );
      
      default:
        return (
          <span>
            Planned Start: {plannedStart?.toLocaleDateString() || 'TBD'} {editButton}
          </span>
        );
    }
  };

  // Function to get smart date display based on step status
  const getStepDateDisplay = (step: ProcessStep) => {
    const completion = stepCompletions.find(c => c.step_number === step.step_number);
    const plannedStart = getPlannedStartDate(step);
    const plannedEnd = calculateTentativeEndDate(step);
    
    if (!completion || completion.status === 'Pending') {
      // Pending: Show planned dates
      return `Planned Start: ${plannedStart?.toLocaleDateString() || 'TBD'} | Tentative End: ${plannedEnd?.toLocaleDateString() || 'TBD'}`;
    }
    
    switch (completion.status) {
      case 'Completed':
        return `Planned Start: ${plannedStart?.toLocaleDateString() || 'TBD'} | Actual Start: ${completion.started_date ? new Date(completion.started_date).toLocaleDateString() : 'N/A'} | Completed: ${completion.completed_date ? new Date(completion.completed_date).toLocaleDateString() : 'N/A'}`;
      
      case 'In Progress':
        return `Planned Start: ${plannedStart?.toLocaleDateString() || 'TBD'} | Started: ${completion.started_date ? new Date(completion.started_date).toLocaleDateString() : 'N/A'} | Est. End: ${plannedEnd?.toLocaleDateString() || 'TBD'}`;
      
      case 'Blocked':
        return `Planned Start: ${plannedStart?.toLocaleDateString() || 'TBD'} | Blocked: ${completion.completed_date ? new Date(completion.completed_date).toLocaleDateString() : 'N/A'}`;
      
      case 'Skipped':
        return `Planned Start: ${plannedStart?.toLocaleDateString() || 'TBD'} | Skipped: ${completion.completed_date ? new Date(completion.completed_date).toLocaleDateString() : 'N/A'}`;
      
      default:
        return `Planned Start: ${plannedStart?.toLocaleDateString() || 'TBD'} | Tentative End: ${plannedEnd?.toLocaleDateString() || 'TBD'}`;
    }
  };

  // Function to get step date display with edit button next to "Planned Start"
  const getStepDateDisplayWithEdit = (step: ProcessStep) => {
    const completion = stepCompletions.find(c => c.step_number === step.step_number);
    const plannedStart = getPlannedStartDate(step);
    const plannedEnd = calculateTentativeEndDate(step);
    
    const editButton = (
      <button
        onClick={(e) => {
          e.stopPropagation();
          openPlannedDateEditModal(step);
        }}
        style={{
          background: 'none',
          border: '1px solid #bdc3c7',
          borderRadius: '3px',
          padding: '1px 3px',
          cursor: 'pointer',
          fontSize: '8px',
          color: '#6B7280',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '2px',
          transition: 'all 0.2s ease',
          marginLeft: '4px',
          verticalAlign: 'middle'
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.background = '#ecf0f1';
          e.currentTarget.style.color = '#0A2540';
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.background = 'none';
          e.currentTarget.style.color = '#6B7280';
        }}
        title="Edit Planned Start Date"
      >
        📅
      </button>
    );
    
    if (!completion || completion.status === 'Pending') {
      // Pending: Show planned dates
      return (
        <span>
          Planned Start: {plannedStart?.toLocaleDateString() || 'TBD'} {editButton} | Tentative End: {plannedEnd?.toLocaleDateString() || 'TBD'}
        </span>
      );
    }
    
    switch (completion.status) {
      case 'Completed':
        return (
          <span>
            Planned Start: {plannedStart?.toLocaleDateString() || 'TBD'} {editButton} | Actual Start: {completion.started_date ? new Date(completion.started_date).toLocaleDateString() : 'N/A'} | Completed: {completion.completed_date ? new Date(completion.completed_date).toLocaleDateString() : 'N/A'}
          </span>
        );
      
      case 'In Progress':
        return (
          <span>
            Planned Start: {plannedStart?.toLocaleDateString() || 'TBD'} {editButton} | Started: {completion.started_date ? new Date(completion.started_date).toLocaleDateString() : 'N/A'} | Est. End: {plannedEnd?.toLocaleDateString() || 'TBD'}
          </span>
        );
      
      case 'Blocked':
        return (
          <span>
            Planned Start: {plannedStart?.toLocaleDateString() || 'TBD'} {editButton} | Blocked: {completion.completed_date ? new Date(completion.completed_date).toLocaleDateString() : 'N/A'}
          </span>
        );
      
      case 'Skipped':
        return (
          <span>
            Planned Start: {plannedStart?.toLocaleDateString() || 'TBD'} {editButton} | Skipped: {completion.completed_date ? new Date(completion.completed_date).toLocaleDateString() : 'N/A'}
          </span>
        );
      
      default:
        return (
          <span>
            Planned Start: {plannedStart?.toLocaleDateString() || 'TBD'} {editButton} | Tentative End: {plannedEnd?.toLocaleDateString() || 'TBD'}
          </span>
        );
    }
  };

  // Function to open planned date edit modal
  const openPlannedDateEditModal = (step: ProcessStep) => {
    setSelectedStepForDateEdit(step);
    const currentPlannedDate = getPlannedStartDate(step);
    setEditingPlannedDate(currentPlannedDate ? currentPlannedDate.toISOString().split('T')[0] : '');
    setShowEditPlannedDateModal(true);
  };

  // Function to submit planned date update
  const submitPlannedDateUpdate = async () => {
    if (!selectedStepForDateEdit || !selectedAcquisition?.database_id || !selectedCountryForProgress || !editingPlannedDate) {
      addNotification('error', 'Missing required information for planned date update');
      return;
    }

    setUpdatingPlannedDate(true);

    try {
      // Determine if this is a Parent step and use appropriate endpoint
      const isParentStep = selectedStepForDateEdit.step_type === 'Parent';
      const endpoint = isParentStep 
        ? `${API_BASE_URL}/api/process-steps/acquisition/${selectedAcquisition.database_id}/country/${selectedCountryForProgress.country}/step/${selectedStepForDateEdit.step_number}/planned-date-cascade`
        : `${API_BASE_URL}/api/process-steps/acquisition/${selectedAcquisition.database_id}/country/${selectedCountryForProgress.country}/step/${selectedStepForDateEdit.step_number}/planned-date`;

      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          planned_start_date: editingPlannedDate
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      
      // Update the step completion in local state
      let updatedCompletions = [...stepCompletions];
      
      // Update parent step
      const parentCompletionIndex = updatedCompletions.findIndex(c => c.step_number === selectedStepForDateEdit.step_number);
      if (parentCompletionIndex >= 0) {
        updatedCompletions[parentCompletionIndex] = {
          ...updatedCompletions[parentCompletionIndex],
          planned_start_date: editingPlannedDate
        };
      } else {
        // Add parent step if it wasn't in completions yet
        updatedCompletions.push({
          id: 0, // Temporary ID - will be updated when data is refreshed
          step_number: selectedStepForDateEdit.step_number,
          status: 'Pending',
          planned_start_date: editingPlannedDate,
          started_date: null,
          completed_date: null,
          completed_by: null,
          notes: null,
          completion_links: null,
          actual_duration_days: null
        });
      }
      
      // If this was a parent step, also update sub-steps
      if (isParentStep && result.updated_sub_steps) {
        result.updated_sub_steps.forEach((subStepUpdate: any) => {
          const subStepIndex = updatedCompletions.findIndex(c => c.step_number === subStepUpdate.step_number);
          if (subStepIndex >= 0) {
            updatedCompletions[subStepIndex] = {
              ...updatedCompletions[subStepIndex],
              planned_start_date: subStepUpdate.planned_start_date
            };
          } else {
            // Add sub-step if it wasn't in completions yet
            updatedCompletions.push({
              id: 0, // Temporary ID
              step_number: subStepUpdate.step_number,
              status: 'Pending',
              planned_start_date: subStepUpdate.planned_start_date,
              started_date: null,
              completed_date: null,
              completed_by: null,
              notes: null,
              completion_links: null,
              actual_duration_days: null
            });
          }
        });
      }
      
      setStepCompletions(updatedCompletions);
      
      // Show appropriate success message
      if (isParentStep && result.updated_sub_steps && result.updated_sub_steps.length > 0) {
        addNotification('success', `Planned start date updated for ${selectedStepForDateEdit.step_name} and ${result.updated_sub_steps.length} sub-steps`);
      } else {
        addNotification('success', `Planned start date updated for ${selectedStepForDateEdit.step_name}`);
      }
      
      // Close modal and reset state
      setShowEditPlannedDateModal(false);
      setSelectedStepForDateEdit(null);
      setEditingPlannedDate('');
      
    } catch (error) {
      console.error('Error updating planned date:', error);
      addNotification('error', `Failed to update planned date: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setUpdatingPlannedDate(false);
    }
  };

  // Admin Functions
  const handleSyncSingleCountry = async () => {
    if (!adminSelectedCountry) {
      addNotification('error', 'Please select a country to sync');
      return;
    }
    
    setAdminLoading(prev => ({...prev, syncSingle: true}));
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/etl/sync-country/${adminSelectedCountry}`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        throw new Error('Failed to sync country');
      }
      
      const result = await response.json();
      setAdminResults(result);
      addNotification('success', `Successfully synced ${adminSelectedCountry}: ${result.steps_processed} steps processed`);
    } catch (error) {
      addNotification('error', `Failed to sync ${adminSelectedCountry}: ${error}`);
    } finally {
      setAdminLoading(prev => ({...prev, syncSingle: false}));
    }
  };

  const handleSyncAllCountries = async () => {
    setAdminLoading(prev => ({...prev, syncAll: true}));
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/etl/sync-all`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        throw new Error('Failed to sync all countries');
      }
      
      const result = await response.json();
      setAdminResults(result);
      addNotification('success', `Successfully synced all countries: ${result.total_countries} countries, ${result.total_steps} steps processed`);
      // Auto-lock after successful operation for safety
      setSyncAllEnabled(false);
    } catch (error) {
      addNotification('error', `Failed to sync all countries: ${error}`);
    } finally {
      setAdminLoading(prev => ({...prev, syncAll: false}));
    }
  };

  const handleResyncStepCompletions = async () => {
    if (!adminSelectedAcquisition || !adminSelectedCountry) {
      addNotification('error', 'Please select both acquisition and country');
      return;
    }
    
    setAdminLoading(prev => ({...prev, resync: true}));
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/step-completions/resync/${adminSelectedAcquisition}/${adminSelectedCountry}`, {
        method: 'POST'
      });
      
      if (!response.ok) {
        throw new Error('Failed to resync step completions');
      }
      
      const result = await response.json();
      setAdminResults(result);
      addNotification('success', `Resynced step completions: ${result.deleted_completions} deleted, ${result.initialized_completions} created`);
    } catch (error) {
      addNotification('error', `Failed to resync step completions: ${error}`);
    } finally {
      setAdminLoading(prev => ({...prev, resync: false}));
    }
  };

  const handleRemoveCountryFromAcquisition = async () => {
    if (!adminSelectedAcquisition || !adminSelectedCountryForRemoval) {
      addNotification('error', 'Please select both acquisition and country to remove');
      return;
    }
    
    if (!window.confirm(`Are you sure you want to remove ${adminSelectedCountryForRemoval} from acquisition ${adminSelectedAcquisition}? This will permanently delete all tracking data.`)) {
      return;
    }
    
    setAdminLoading(prev => ({...prev, removeCountry: true}));
    try {
      const response = await fetch(`${API_BASE_URL}/api/countries/acquisition/${adminSelectedAcquisition}/country/${adminSelectedCountryForRemoval}`, {
        method: 'DELETE'
      });
      
      if (!response.ok) {
        throw new Error('Failed to remove country');
      }
      
      const result = await response.json();
      setAdminResults(result);
      addNotification('success', `Successfully removed ${adminSelectedCountryForRemoval}: ${result.total_deleted} records deleted`);
    } catch (error) {
      addNotification('error', `Failed to remove country: ${error}`);
    } finally {
      setAdminLoading(prev => ({...prev, removeCountry: false}));
    }
  };


  const handleViewAcquisitionCountries = async () => {
    if (!adminSelectedAcquisition) {
      addNotification('error', 'Please select an acquisition');
      return;
    }
    
    setAdminLoading(prev => ({...prev, viewCountries: true}));
    try {
      const response = await fetch(`${API_BASE_URL}/api/countries/acquisition/${adminSelectedAcquisition}`);
      
      if (!response.ok) {
        throw new Error('Failed to get acquisition countries');
      }
      
      const result = await response.json();
      setAdminResults(result);
      addNotification('info', `Acquisition countries loaded: ${result.countries?.length || 0} countries found`);
    } catch (error) {
      addNotification('error', `Failed to get acquisition countries: ${error}`);
    } finally {
      setAdminLoading(prev => ({...prev, viewCountries: false}));
    }
  };

  // Function to load acquisition status for display
  const loadAcquisitionStatus = async () => {
    if (!selectedAcquisition?.database_id) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/status/acquisition/${selectedAcquisition.database_id}/status`);
      
      if (response.ok) {
        const data = await response.json();
        const acquisition = data.acquisition;
        
        setCurrentAcquisitionStatus(acquisition.status || 'Planning');
        setCurrentAcquisitionDates({
          rif_start_date: acquisition.rif_start_date,
          rif_end_date: acquisition.rif_end_date
        });
      }
    } catch (error) {
      console.error('Failed to load acquisition status:', error);
    }
  };

  // Load acquisition status when acquisition is selected
  React.useEffect(() => {
    if (selectedAcquisition && currentView === 'countries') {
      loadAcquisitionStatus();
    }
  }, [selectedAcquisition, currentView]);

  // Status Management Functions
  const openAcquisitionStatusModal = async () => {
    if (!selectedAcquisition?.database_id) {
      addNotification('error', 'No acquisition selected');
      return;
    }
    
    try {
      // Fetch current acquisition status
      const response = await fetch(`${API_BASE_URL}/api/status/acquisition/${selectedAcquisition.database_id}/status`);
      
      if (response.ok) {
        const data = await response.json();
        const acquisition = data.acquisition;
        
        setAcquisitionStatus(acquisition.status || 'Planning');
        setAcquisitionRifStartDate(acquisition.rif_start_date || '');
        setAcquisitionRifEndDate(acquisition.rif_end_date || '');
      } else {
        // Default values if fetch fails
        setAcquisitionStatus('Planning');
        setAcquisitionRifStartDate('');
        setAcquisitionRifEndDate('');
      }
      
      setShowAcquisitionStatusModal(true);
    } catch (error) {
      addNotification('error', 'Failed to load acquisition status');
    }
  };

  const openCountryStatusModal = async (country: Country) => {
    if (!selectedAcquisition?.database_id) {
      addNotification('error', 'No acquisition selected');
      return;
    }
    
    try {
      // Fetch current country status
      const response = await fetch(`${API_BASE_URL}/api/status/acquisition/${selectedAcquisition.database_id}/country/${country.country}/status`);
      
      if (response.ok) {
        const data = await response.json();
        const countryInfo = data.country_info;
        
        setCountryStatus(countryInfo.status || 'Planning');
        setCountryEndDate(countryInfo.end_date || '');
      } else {
        // Default values if fetch fails
        setCountryStatus(country.status || 'Planning');
        setCountryEndDate('');
      }
      
      setSelectedCountryForStatus(country);
      setShowCountryStatusModal(true);
    } catch (error) {
      addNotification('error', 'Failed to load country status');
    }
  };

  const submitAcquisitionStatusUpdate = async () => {
    if (!selectedAcquisition?.database_id) {
      addNotification('error', 'No acquisition selected');
      return;
    }

    // Validation: If status is Completed, end date is required
    if (acquisitionStatus === 'Completed' && !acquisitionRifEndDate) {
      addNotification('error', 'End date is required when status is Completed');
      return;
    }

    setUpdatingStatus(true);

    try {
      const updateData: any = {
        status: acquisitionStatus
      };

      if (acquisitionRifStartDate) {
        updateData.rif_start_date = acquisitionRifStartDate;
      }

      if (acquisitionRifEndDate) {
        updateData.rif_end_date = acquisitionRifEndDate;
      }

      const response = await fetch(
        `${API_BASE_URL}/api/status/acquisition/${selectedAcquisition.database_id}/status`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updateData)
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText);
      }

      const message = acquisitionStatus === 'Completed' ? 
        `Acquisition completed with end date ${acquisitionRifEndDate}` :
        'Acquisition dates updated successfully';
      
      addNotification('success', message);
      setShowAcquisitionStatusModal(false);
      
      // Update local state
      setCurrentAcquisitionStatus(acquisitionStatus);
      setCurrentAcquisitionDates({
        rif_start_date: acquisitionRifStartDate,
        rif_end_date: acquisitionRifEndDate
      });
      
      // If RIF start date was updated and we're currently viewing progress tracking, refresh the step completions
      if (updateData.rif_start_date && selectedCountryForProgress) {
        addNotification('info', 'Refreshing planned dates after RIF start date update...');
        await fetchCountryProgress(selectedCountryForProgress);
        addNotification('success', 'Planned dates updated based on new RIF start date');
      }
      
    } catch (error) {
      addNotification('error', `Failed to update acquisition: ${error}`);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const submitCountryStatusUpdate = async () => {
    if (!selectedAcquisition?.database_id || !selectedCountryForStatus) {
      addNotification('error', 'Missing required information');
      return;
    }

    // Validation: If status is Completed, end date is required
    if (countryStatus === 'Completed' && !countryEndDate) {
      addNotification('error', 'End date is required when status is Completed');
      return;
    }

    setUpdatingStatus(true);

    try {
      const updateData: any = {
        status: countryStatus
      };

      if (countryEndDate) {
        updateData.end_date = countryEndDate;
      }

      const response = await fetch(
        `${API_BASE_URL}/api/status/acquisition/${selectedAcquisition.database_id}/country/${selectedCountryForStatus.country}/status`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updateData)
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText);
      }

      addNotification('success', `${selectedCountryForStatus.country} status updated to ${countryStatus}`);
      setShowCountryStatusModal(false);
      
      // Update the country in the local state
      setAssignedCountries(prev => prev.map(c => 
        c.id === selectedCountryForStatus.id 
          ? { ...c, status: countryStatus }
          : c
      ));
      
      // Reload acquisition status since it might have changed
      loadAcquisitionStatus();
      
    } catch (error) {
      addNotification('error', `Failed to update country status: ${error}`);
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Inline status change functions
  const handleAcquisitionStatusChange = async (newStatus: string) => {
    if (!selectedAcquisition?.database_id) return;

    // If changing to Completed, open modal for end date
    if (newStatus === 'Completed') {
      setAcquisitionStatus(newStatus);
      setAcquisitionRifStartDate(currentAcquisitionDates.rif_start_date || '');
      setAcquisitionRifEndDate(currentAcquisitionDates.rif_end_date || '');
      setShowAcquisitionStatusModal(true);
      return;
    }

    // For other statuses, update directly
    try {
      const updateData = { status: newStatus };
      
      const response = await fetch(
        `${API_BASE_URL}/api/status/acquisition/${selectedAcquisition.database_id}/status`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updateData)
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update status');
      }

      setCurrentAcquisitionStatus(newStatus);
      addNotification('success', `Acquisition status updated to ${newStatus}`);
    } catch (error) {
      addNotification('error', `Failed to update acquisition status: ${error}`);
    }
  };

  const handleCountryStatusChange = async (country: Country, newStatus: string) => {
    if (!selectedAcquisition?.database_id) return;

    // If changing to Completed, open modal for end date
    if (newStatus === 'Completed') {
      setSelectedCountryForStatus(country);
      setCountryStatus(newStatus);
      setCountryEndDate('');
      setShowCountryStatusModal(true);
      return;
    }

    // For other statuses, update directly
    try {
      const updateData = { status: newStatus };
      
      const response = await fetch(
        `${API_BASE_URL}/api/status/acquisition/${selectedAcquisition.database_id}/country/${country.country}/status`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(updateData)
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update status');
      }

      // Update the country in the local state
      setAssignedCountries(prev => prev.map(c => 
        c.id === country.id 
          ? { ...c, status: newStatus }
          : c
      ));

      // Reload acquisition status since it might have changed
      loadAcquisitionStatus();
      
      addNotification('success', `${country.country} status updated to ${newStatus}`);
    } catch (error) {
      addNotification('error', `Failed to update country status: ${error}`);
    }
  };

  // Function to handle quick status changes from inline dropdown
  const handleQuickStatusChange = async (step: ProcessStep, newStatus: string) => {
    if (!selectedAcquisition?.database_id || !selectedCountryForProgress) {
      addNotification('error', 'Missing required information for status update');
      return;
    }

    // Set loading state for this specific step
    setUpdatingStepStatus(step.step_number);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/process-steps/acquisition/${selectedAcquisition.database_id}/country/${selectedCountryForProgress.country}/step/${step.step_number}/complete`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            status: newStatus,
            planned_start_date: getPlannedStartDate(step)?.toISOString().split('T')[0],
            started_date: newStatus === 'In Progress' ? new Date().toISOString().split('T')[0] : null,
            completed_date: ['Completed', 'Blocked', 'Skipped'].includes(newStatus) ? new Date().toISOString().split('T')[0] : null,
            completed_by: 'user@company.com', // TODO: Get from auth system
            notes: '', // Empty for quick changes - user can add details later
            completion_links: ''
          })
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Update the step completion in local state instead of full refresh
      const updatedCompletions = stepCompletions.map(completion => 
        completion.step_number === step.step_number 
          ? { 
              ...completion, 
              status: newStatus,
              completed_date: ['Completed', 'Blocked', 'Skipped'].includes(newStatus) ? new Date().toISOString().split('T')[0] : completion.completed_date,
              started_date: newStatus === 'In Progress' ? new Date().toISOString().split('T')[0] : completion.started_date
            }
          : completion
      );
      
      // If step wasn't in completions yet, add it
      if (!stepCompletions.find(c => c.step_number === step.step_number)) {
        updatedCompletions.push({
          id: Date.now(), // Temporary ID for new completion
          step_number: step.step_number,
          status: newStatus,
          planned_start_date: getPlannedStartDate(step)?.toISOString().split('T')[0] || null,
          started_date: newStatus === 'In Progress' ? new Date().toISOString().split('T')[0] : null,
          completed_date: ['Completed', 'Blocked', 'Skipped'].includes(newStatus) ? new Date().toISOString().split('T')[0] : null,
          completed_by: 'user@company.com',
          notes: '',
          completion_links: '',
          actual_duration_days: null
        });
      }
      
      setStepCompletions(updatedCompletions);
      
      // Refresh acquisition status since step changes can affect it
      await loadAcquisitionStatus();
      
      addNotification('success', `Step ${step.step_number} status updated to ${newStatus}`);
    } catch (err) {
      addNotification('error', `Failed to update step status: ${err}`);
      console.error('Error updating step status:', err);
    } finally {
      // Clear loading state
      setUpdatingStepStatus(null);
    }
  };

  // Function to fetch step history
  const fetchStepHistory = async (step: ProcessStep) => {
    if (!selectedAcquisition?.database_id || !selectedCountryForProgress) {
      return;
    }

    setLoadingHistory(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/process-steps/acquisition/${selectedAcquisition.database_id}/country/${selectedCountryForProgress.country}/step/${step.step_number}/history`
      );
      
      if (response.ok) {
        const data = await response.json();
        setStepHistory(data.history || []);
      } else {
        console.error('Failed to fetch step history');
        setStepHistory([]);
      }
    } catch (err) {
      console.error('Error fetching step history:', err);
      setStepHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Function to open step completion modal
  const openStepCompletionModal = (step: ProcessStep) => {
    setSelectedStep(step);
    
    // Check if step has existing completion data
    const completion = stepCompletions.find(c => c.step_number === step.step_number);
    
    if (completion) {
      // If selectedStatus was set by dropdown (status change), use that instead of existing status
      const statusToUse = selectedStatus !== 'Completed' ? selectedStatus : completion.status;
      
      // Pre-fill with existing data, but clear dates that don't apply to new status
      setSelectedStatus(statusToUse || 'Pending');
      
      // Smart date handling based on status
      if (statusToUse === 'Completed') {
        setCompletionDate(completion.completed_date || new Date().toISOString().split('T')[0]);
        setActualStartDate(completion.started_date || '');
      } else if (statusToUse === 'In Progress') {
        setCompletionDate(''); // Clear completion date for in progress
        setActualStartDate(completion.started_date || new Date().toISOString().split('T')[0]);
      } else if (['Blocked', 'Skipped'].includes(statusToUse)) {
        setCompletionDate(completion.completed_date || new Date().toISOString().split('T')[0]);
        setActualStartDate(''); // Clear start date for blocked/skipped
      } else {
        // Pending or other status - clear all dates
        setCompletionDate('');
        setActualStartDate('');
      }
      
      setCompletionNotes(completion.notes || '');
      setCompletionLinks(completion.completion_links || '');
    } else {
      // New completion - use defaults based on selected status
      const statusToUse = selectedStatus || 'Completed';
      setSelectedStatus(statusToUse);
      
      if (['Completed', 'Blocked', 'Skipped'].includes(statusToUse)) {
        setCompletionDate(new Date().toISOString().split('T')[0]);
      } else {
        setCompletionDate('');
      }
      
      if (['Completed', 'In Progress'].includes(statusToUse)) {
        const plannedStart = getPlannedStartDate(step);
        setActualStartDate(plannedStart ? plannedStart.toISOString().split('T')[0] : '');
      } else {
        setActualStartDate('');
      }
      
      setCompletionNotes('');
      setCompletionLinks('');
    }
    
    // Fetch step history when opening modal
    fetchStepHistory(step);
    
    setShowCompletionModal(true);
  };

  // Function to close completion modal
  const closeCompletionModal = () => {
    setShowCompletionModal(false);
    setSelectedStep(null);
    setActiveModalTab('details'); // Reset to details tab
    setStepHistory([]); // Clear history data
    setLoadingHistory(false); // Reset loading state
    setCompletionDate('');
    setActualStartDate('');
    setCompletionNotes('');
    setCompletionLinks('');
  };

  // Function to submit step completion
  const submitStepCompletion = async () => {
    if (!selectedStep || !selectedAcquisition?.database_id || !selectedCountryForProgress) {
      addNotification('error', 'Missing required information for step completion');
      return;
    }

    setCompletingStep(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/process-steps/acquisition/${selectedAcquisition.database_id}/country/${selectedCountryForProgress.country}/step/${selectedStep.step_number}/complete`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            status: selectedStatus,
            planned_start_date: getPlannedStartDate(selectedStep)?.toISOString().split('T')[0],
            started_date: (['In Progress', 'Completed'].includes(selectedStatus) && actualStartDate) ? actualStartDate : null,
            completed_date: (['Completed', 'Blocked', 'Skipped'].includes(selectedStatus) && completionDate) ? completionDate : null,
            completed_by: 'user@company.com', // TODO: Get from auth system
            notes: completionNotes,
            completion_links: completionLinks
          })
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      addNotification('success', `Step ${selectedStep.step_number} status updated to ${selectedStatus}!`);
      
      // Refresh progress data to show updated completion
      await fetchCountryProgress(selectedCountryForProgress);
      
      // Refresh acquisition status since step changes can affect it
      await loadAcquisitionStatus();
      
      // Close modal
      closeCompletionModal();
      
    } catch (err) {
      addNotification('error', `Failed to update step status: ${err}`);
      console.error('Error updating step status:', err);
    } finally {
      setCompletingStep(false);
    }
  };

  // Function to open RIF start date edit modal
  const openEditStartDateModal = (country: Country) => {
    setSelectedCountryForEdit(country);
    setEditingStartDate(country.start_date);
    setShowEditDateModal(true);
  };

  // Function to close edit date modal
  const closeEditDateModal = () => {
    setShowEditDateModal(false);
    setSelectedCountryForEdit(null);
    setEditingStartDate('');
  };

  // Function to update RIF start date
  const updateRifStartDate = async () => {
    if (!selectedCountryForEdit || !editingStartDate || !selectedAcquisition?.database_id) {
      addNotification('error', 'Missing required information');
      return;
    }

    setUpdatingStartDate(true);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/countries/acquisition/${selectedAcquisition.database_id}/country/${selectedCountryForEdit.country}/start-date`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            start_date: editingStartDate
          })
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      addNotification('success', `Start Date updated for ${selectedCountryForEdit.country}!`);
      
      // Refresh assigned countries to show updated date
      await fetchAssignedCountries(selectedAcquisition.database_id);
      
      closeEditDateModal();
      
    } catch (err) {
      addNotification('error', `Failed to update RIF start date: ${err}`);
    } finally {
      setUpdatingStartDate(false);
    }
  };

  // Function to generate progress Shot Clock report
  const generateProgressReport = async () => {
    if (!selectedAcquisition?.database_id || !selectedCountryForProgress) {
      addNotification('error', 'No acquisition or country selected');
      return;
    }

    setGeneratingProgressReport(true);

    try {
      addNotification('info', `Generating ${selectedCountryForProgress.country} Shot Clock Report...`);
      
      const response = await fetch(
        `${API_BASE_URL}/api/process-steps/acquisition/${selectedAcquisition.database_id}/country/${selectedCountryForProgress.country}/generate-progress-report`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      
      // Show success notification with link if available
      if (data.spreadsheet_url) {
        addNotification('success', `${selectedCountryForProgress.country} Shot Clock Report generated!`, true, data.spreadsheet_url);
      } else {
        addNotification('success', `${selectedCountryForProgress.country} Shot Clock Report generated successfully!`);
      }
      
      console.log('Progress report generated:', data);
      
    } catch (err) {
      addNotification('error', `Failed to generate progress report: ${err}`);
      console.error('Error generating progress report:', err);
    } finally {
      setGeneratingProgressReport(false);
    }
  };

  // Function to fetch latest progress report
  const fetchLatestProgressReport = async (country?: Country) => {
    // Use passed country parameter or fall back to state
    const targetCountry = country || selectedCountryForProgress;
    
    console.log('🔍 fetchLatestProgressReport: Parameters', {
      passedCountry: country,
      selectedCountryForProgress,
      targetCountry,
      acquisition_id: selectedAcquisition?.database_id
    });
    
    if (!selectedAcquisition?.database_id || !targetCountry) {
      console.log('🔍 fetchLatestProgressReport: Missing data', {
        acquisition_id: selectedAcquisition?.database_id,
        country: targetCountry?.country,
        hasTargetCountry: !!targetCountry,
        targetCountryKeys: targetCountry ? Object.keys(targetCountry) : 'null'
      });
      return;
    }
    
    const url = `${API_BASE_URL}/api/process-steps/acquisition/${selectedAcquisition.database_id}/country/${targetCountry.country}/latest-progress-report`;
    console.log('🔍 fetchLatestProgressReport: Calling API', url);
    
    try {
      const response = await fetch(url);
      console.log('🔍 fetchLatestProgressReport: Response status', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('🔍 fetchLatestProgressReport: API response', data);
        
        if (data.has_report) {
          const reportData = {
            date: new Date(data.generated_date).toLocaleDateString(),
            url: data.report_url
          };
          console.log('🔍 fetchLatestProgressReport: Setting state', reportData);
          setLatestProgressReport(reportData);
          console.log('🔍 fetchLatestProgressReport: State should now be set to', reportData);
        } else {
          console.log('🔍 fetchLatestProgressReport: No report found');
          setLatestProgressReport(null);
        }
      } else {
        console.log('🔍 fetchLatestProgressReport: API error', response.status);
        setLatestProgressReport(null);
      }
    } catch (err) {
      console.error('🔍 fetchLatestProgressReport: Error', err);
      setLatestProgressReport(null);
    }
  };

  // useEffect - runs when component loads (like __init__ in Python classes)
  useEffect(() => {
    fetchAcquisitions();
  }, []);

  // Debug: Log when latestProgressReport state changes
  useEffect(() => {
    console.log('🔍 State Change: latestProgressReport updated to', latestProgressReport);
  }, [latestProgressReport]);


  // Debug: Log when countryExecutiveSummaries state changes
  useEffect(() => {
    console.log('🔍 State Change: countryExecutiveSummaries updated to', countryExecutiveSummaries);
  }, [countryExecutiveSummaries]);

  // Fetch latest executive summary for each country when countries are loaded
  useEffect(() => {
    if (assignedCountries.length > 0 && selectedAcquisition?.database_id) {
      console.log('🔍 Countries loaded, fetching executive summaries per country');
      fetchExecutiveSummariesPerCountry();
      // Also fetch latest acquisition shot clock report
      fetchLatestAcquisitionShotClock();
    }
  }, [assignedCountries, selectedAcquisition]);

  // Function to fetch executive summary for each country individually
  const fetchExecutiveSummariesPerCountry = async () => {
    if (!selectedAcquisition?.database_id || assignedCountries.length === 0) {
      return;
    }
    
    const summaries: {[countryId: string]: {date: string, url: string}} = {};
    
    // Fetch executive summary for each country
    for (const country of assignedCountries) {
      try {
        const url = `${API_BASE_URL}/api/executive-summaries/acquisition/${selectedAcquisition.database_id}/country/${country.country}/latest`;
        console.log(`🔍 Fetching executive summary for ${country.country}`);
        
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          
          if (data.has_summary) {
            summaries[`${country.country}_${country.id}`] = {
              date: new Date(data.generated_date).toLocaleDateString(),
              url: data.summary_url
            };
            console.log(`🔍 Found executive summary for ${country.country}:`, summaries[`${country.country}_${country.id}`]);
          } else {
            console.log(`🔍 No executive summary found for ${country.country}`);
          }
        }
      } catch (err) {
        console.error(`🔍 Error fetching executive summary for ${country.country}:`, err);
      }
    }
    
    console.log('🔍 Setting per-country executive summaries:', summaries);
    setCountryExecutiveSummaries(summaries);
  };

  // Function to fetch latest acquisition shot clock report
  const fetchLatestAcquisitionShotClock = async () => {
    if (!selectedAcquisition?.database_id) {
      return;
    }
    
    console.log('🔍 fetchLatestAcquisitionShotClock: Fetching for acquisition', selectedAcquisition.database_id);
    
    try {
      const url = `${API_BASE_URL}/api/process-steps/acquisition/${selectedAcquisition.database_id}/latest-acquisition-shot-clock`;
      console.log('🔍 fetchLatestAcquisitionShotClock: Calling API', url);
      
      const response = await fetch(url);
      console.log('🔍 fetchLatestAcquisitionShotClock: Response status', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('🔍 fetchLatestAcquisitionShotClock: API response', data);
        
        if (data.has_report) {
          const reportData = {
            date: new Date(data.generated_date).toLocaleDateString(),
            url: data.report_url,
            countries: data.countries_included || []
          };
          console.log('🔍 fetchLatestAcquisitionShotClock: Setting state', reportData);
          setLatestAcquisitionShotClock(reportData);
        } else {
          console.log('🔍 fetchLatestAcquisitionShotClock: No report found');
          setLatestAcquisitionShotClock(null);
        }
      } else {
        console.log('🔍 fetchLatestAcquisitionShotClock: API error', response.status);
        setLatestAcquisitionShotClock(null);
      }
    } catch (err) {
      console.error('🔍 fetchLatestAcquisitionShotClock: Error', err);
      setLatestAcquisitionShotClock(null);
    }
  };

  // Show loading screen while checking authentication or processing OAuth callback
  if (authLoading || processingCallback) {
  return (
      <div style={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(135deg, #0A2540 0%, #1A365D 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ textAlign: 'center', color: 'white' }}>
          <div style={{
            width: '60px',
            height: '60px',
            border: '4px solid rgba(212, 175, 55, 0.3)',
            borderTop: '4px solid #D4AF37',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <h2 style={{ margin: '0 0 10px 0', fontSize: '24px', fontFamily: "'Montserrat', sans-serif" }}>
            {processingCallback ? 'Signing you in...' : 'Loading...'}
          </h2>
          {processingCallback && (
            <p style={{ margin: '0', fontSize: '14px', opacity: 0.8, fontFamily: "'Open Sans', sans-serif" }}>
              Verifying your credentials
            </p>
          )}
        </div>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return (
      <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0A2540 0%, #1A365D 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Open Sans', sans-serif" }}>
        <div style={{ 
          background: 'white', 
          borderRadius: '20px', 
          padding: '50px 60px', 
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          maxWidth: '500px',
          textAlign: 'center'
        }}>
          {/* App Logo/Icon */}
          <div style={{ 
            background: 'linear-gradient(45deg, #0A2540, #1A365D)', 
            borderRadius: '50%', 
            width: '100px', 
            height: '100px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            fontSize: '48px',
            margin: '0 auto 30px',
            boxShadow: '0 8px 20px rgba(10, 37, 64, 0.4)',
            border: '3px solid #D4AF37'
          }}>
            🎯
          </div>

          {/* App Title */}
          <h1 style={{ 
            margin: '0 0 10px 0', 
            fontSize: '42px', 
            color: '#0A2540', 
            fontWeight: '700',
            fontFamily: "'Raleway', sans-serif",
            letterSpacing: '-0.5px'
          }}>
            RIF Navigator
          </h1>
          <p style={{ 
            margin: '0 0 40px 0', 
            fontSize: '18px', 
            color: '#6B7280',
            fontWeight: '400',
            fontFamily: "'Open Sans', sans-serif"
          }}>
            M&A HR RIF Process Management
          </p>

          {/* Login Button */}
          <button
            onClick={handleGoogleLogin}
            disabled={loggingIn}
            style={{
              background: loggingIn ? '#bdc3c7' : 'linear-gradient(135deg, #D4AF37, #C4A028)',
              color: '#0A2540',
              border: 'none',
              padding: '16px 32px',
              borderRadius: '50px',
              cursor: loggingIn ? 'not-allowed' : 'pointer',
              fontSize: '16px',
              fontWeight: '700',
              fontFamily: "'Montserrat', sans-serif",
              display: 'inline-flex',
              alignItems: 'center',
              gap: '12px',
              boxShadow: loggingIn ? 'none' : '0 4px 15px rgba(212, 175, 55, 0.4)',
              transition: 'all 0.3s ease',
              transform: loggingIn ? 'scale(0.98)' : 'scale(1)'
            }}
            onMouseOver={(e) => {
              if (!loggingIn) e.currentTarget.style.transform = 'scale(1.05)';
            }}
            onMouseOut={(e) => {
              if (!loggingIn) e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            {loggingIn ? (
              <>
                <div style={{
                  width: '20px',
                  height: '20px',
                  border: '3px solid rgba(255, 255, 255, 0.3)',
                  borderTop: '3px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                Signing in...
              </>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Sign in with Google
              </>
            )}
          </button>

          {/* Info Text */}
          <p style={{ 
            margin: '30px 0 0 0', 
            fontSize: '13px', 
            color: '#95a5a6',
            lineHeight: '1.6'
          }}>
            Click the button above to sign in with your Trilogy Google account
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0A2540 0%, #1A365D 100%)', fontFamily: "'Open Sans', 'Segoe UI', sans-serif" }}>
      {/* Modern Notification System */}
      <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 1000 }}>
        {notifications.map((notification) => (
          <div
            key={notification.id}
            style={{
              background: notification.type === 'success' ? '#4caf50' : 
                         notification.type === 'error' ? '#FF6B6B' : '#2196f3',
              color: 'white',
              padding: '15px 20px',
              borderRadius: '8px',
              marginBottom: '10px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              maxWidth: '400px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              animation: 'slideIn 0.3s ease-out'
            }}
          >
            <div style={{ flex: 1 }}>
              <div>{notification.message}</div>
              {notification.url && (
                <div style={{ marginTop: '8px' }}>
                  <a
                    href={notification.url}
          target="_blank"
          rel="noopener noreferrer"
                    style={{
                      color: 'white',
                      textDecoration: 'underline',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    📄 Open Report in Google Drive
                  </a>
                </div>
              )}
            </div>
            <button
              onClick={() => removeNotification(notification.id)}
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                fontSize: '18px',
                marginLeft: '10px',
                flexShrink: 0
              }}
            >
              ×
            </button>
          </div>
        ))}
      </div>

      {/* Compact Professional Header */}
      <header style={{
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        padding: '15px 0',
        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
        boxShadow: '0 2px 20px rgba(0,0,0,0.1)'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <div style={{ 
              background: 'linear-gradient(45deg, #0A2540, #1A365D)', 
              borderRadius: '50%', 
              width: '50px', 
              height: '50px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              fontSize: '28px',
              fontWeight: 'bold',
              boxShadow: '0 4px 12px rgba(10, 37, 64, 0.4)',
              border: '2px solid #D4AF37'
            }}>
              🎯
            </div>
            <div>
              <h1 style={{ 
                margin: '0', 
                fontSize: '32px', 
                color: '#0A2540', 
                fontWeight: '700',
                fontFamily: "'Raleway', sans-serif",
                letterSpacing: '-0.5px'
              }}>
                RIF Navigator
              </h1>
              <p style={{ 
                margin: '0', 
                fontSize: '16px', 
                color: '#6B7280',
                fontWeight: '400',
                fontFamily: "'Open Sans', sans-serif",
                letterSpacing: '0.1px'
              }}>
                M&A HR RIF Process Management
              </p>
            </div>
          </div>
          
          {/* User Info & Actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            {/* User Info */}
            {user && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                {/* User Icon (using first letter of name) */}
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #D4AF37, #C4A028)',
                  color: '#0A2540',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  fontWeight: '700',
                  fontFamily: "'Montserrat', sans-serif",
                  border: '2px solid #D4AF37',
                  boxShadow: '0 2px 8px rgba(212, 175, 55, 0.3)'
                }}>
                  {user.name.charAt(0).toUpperCase()}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#0A2540', fontFamily: "'Montserrat', sans-serif" }}>
                    {user.name}
                  </div>
                  <div style={{ fontSize: '11px', color: '#6B7280', fontFamily: "'Open Sans', sans-serif" }}>
                    {user.email}
                  </div>
                </div>
              </div>
            )}
          
          {/* Admin Button */}
          <button
            onClick={() => setCurrentView('admin')}
            style={{
                background: currentView === 'admin' ? 'linear-gradient(135deg, #800020, #6B001A)' : 'linear-gradient(135deg, #0A2540, #1A365D)',
              color: 'white',
                border: currentView === 'admin' ? 'none' : '1px solid #D4AF37',
              padding: '12px 20px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '600',
                fontFamily: "'Montserrat', sans-serif",
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.3s ease',
                boxShadow: currentView === 'admin' ? '0 4px 12px rgba(128, 0, 32, 0.3)' : '0 4px 12px rgba(10, 37, 64, 0.3)'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(52, 152, 219, 0.4)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(52, 152, 219, 0.3)';
            }}
          >
            🔧 Admin Console
          </button>

            {/* Logout Button */}
            <button
              onClick={handleLogout}
              style={{
                background: 'linear-gradient(135deg, #6B7280, #4B5563)',
                color: 'white',
                border: 'none',
                padding: '12px 20px',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '600',
                fontFamily: "'Montserrat', sans-serif",
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(107, 114, 128, 0.3)'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(149, 165, 166, 0.4)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(149, 165, 166, 0.3)';
              }}
            >
              🚪 Logout
          </button>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main style={{ padding: '40px 20px', maxWidth: '1200px', margin: '0 auto' }}>
        {currentView === 'acquisition' ? (
          // Acquisition Selection View
          <div style={{ 
            background: 'white', 
            padding: '40px', 
            borderRadius: '16px', 
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <h2 style={{ fontSize: '28px', color: '#0A2540', marginBottom: '10px', fontWeight: '600' }}>
              Select Acquisition
            </h2>
            <p style={{ color: '#6B7280', fontSize: '16px', margin: '0' }}>
              Choose the acquisition you want to manage RIF processes for
            </p>
          </div>
          
          {error && (
            <div style={{ 
              background: 'linear-gradient(135deg, #ff6b6b, #ff8e8e)', 
              color: 'white', 
              padding: '20px', 
              borderRadius: '12px', 
              marginBottom: '30px',
              boxShadow: '0 4px 12px rgba(255, 107, 107, 0.3)'
            }}>
              <strong>⚠️ Error:</strong> {error}
            </div>
          )}
          
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #0A2540',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 20px'
              }}></div>
              <p style={{ color: '#6B7280' }}>Loading acquisitions...</p>
            </div>
          ) : (
            <div style={{ maxWidth: '500px', margin: '0 auto' }}>
              {acquisitions.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <p style={{ color: '#6B7280', fontSize: '16px' }}>
                    No acquisitions found. Make sure your FastAPI backend is running at {API_BASE_URL}
                  </p>
                  <button 
                    onClick={fetchAcquisitions}
                    style={{
                      background: 'linear-gradient(135deg, #1e3c72, #2a5298)',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      marginTop: '20px'
                    }}
                  >
                    🔄 Retry Connection
                  </button>
                </div>
              ) : (
                <>
                  {/* Modern Dropdown Selector */}
                  <div style={{ marginBottom: '30px' }}>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '10px', 
                      fontSize: '16px', 
                      fontWeight: '500', 
                      color: '#0A2540' 
                    }}>
                      Choose Acquisition:
                    </label>
                    <select
                      value={selectedAcquisitionId}
                      onChange={(e) => handleAcquisitionSelect(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '15px',
                        fontSize: '16px',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        background: 'white',
                        color: '#0A2540',
                        cursor: 'pointer',
                        transition: 'border-color 0.3s ease'
                      }}
                      onFocus={(e) => e.target.style.borderColor = '#D4AF37'}
                      onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                    >
                      <option value="">-- Select an acquisition --</option>
                      {acquisitions.map((acquisition) => (
                        <option key={acquisition.id} value={acquisition.id}>
                          🏢 {acquisition.display_name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Selected Acquisition Details */}
                  {selectedAcquisition && (
                    <div style={{
                      background: 'linear-gradient(135deg, #e3f2fd, #f3e5f5)',
                      padding: '25px',
                      borderRadius: '12px',
                      marginBottom: '30px',
                      border: '1px solid #e1bee7'
                    }}>
                      <h3 style={{ margin: '0 0 15px 0', color: '#0A2540', fontSize: '20px' }}>
                        📋 Selected: {selectedAcquisition.display_name}
                      </h3>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', fontSize: '14px', color: '#5d4e75' }}>
                        <div>
                          <strong>Original Name:</strong><br />
                          {selectedAcquisition.original_name}
                        </div>
                        <div>
                          <strong>Created:</strong><br />
                          {new Date(selectedAcquisition.created_time).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
                    <button
                      onClick={confirmAcquisitionSelection}
                      disabled={!selectedAcquisition || loading}
                      style={{
                        background: selectedAcquisition ? 'linear-gradient(135deg, #4caf50, #45a049)' : '#bdc3c7',
                        color: 'white',
                        border: 'none',
                        padding: '15px 30px',
                        borderRadius: '8px',
                        cursor: selectedAcquisition ? 'pointer' : 'not-allowed',
                        fontSize: '16px',
                        fontWeight: '500',
                        boxShadow: selectedAcquisition ? '0 4px 12px rgba(76, 175, 80, 0.3)' : 'none',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {loading ? '⏳ Processing...' : '✅ Confirm Selection'}
                    </button>
                    
                    <button 
                      onClick={fetchAcquisitions}
                      disabled={loading}
                      style={{
                        background: 'linear-gradient(135deg, #1e3c72, #2a5298)',
                        color: 'white',
                        border: 'none',
                        padding: '15px 30px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '16px',
                        fontWeight: '500',
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.3)',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      🔄 Refresh List
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
        ) : currentView === 'admin' ? (
          // Admin Console View
          <div style={{ 
            background: 'white', 
            padding: '40px', 
            borderRadius: '16px', 
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
              <div>
                <h2 style={{ fontSize: '28px', color: '#0A2540', margin: '0', fontWeight: '600' }}>
                  🔧 Admin Console
                </h2>
                <p style={{ fontSize: '16px', color: '#6B7280', margin: '8px 0 0 0' }}>
                  System administration and maintenance tools
                </p>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  onClick={refreshAdminData}
                  disabled={adminLoading.refreshing}
                  style={{
                    background: adminLoading.refreshing ? '#bdc3c7' : 'linear-gradient(135deg, #D4AF37, #C4A028)',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    cursor: adminLoading.refreshing ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {adminLoading.refreshing ? '⏳ Refreshing...' : '🔄 Refresh'}
                </button>
              <button
                onClick={() => setCurrentView('acquisition')}
                style={{
                  background: 'linear-gradient(135deg, #95a5a6, #7f8c8d)',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                ← Back to App
              </button>
              </div>
            </div>

            {/* Page-level spinner when any admin task is running */}
            {Object.values(adminLoading).some(loading => loading) && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.3)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 999,
                backdropFilter: 'blur(2px)'
              }}>
                <div style={{
                  background: 'white',
                  padding: '30px 40px',
                  borderRadius: '16px',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '20px',
                  minWidth: '300px'
                }}>
                  <div style={{
                    width: '50px',
                    height: '50px',
                    border: '4px solid #e3f2fd',
                    borderTop: '4px solid #0A2540',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <div style={{ textAlign: 'center' }}>
                    <h4 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#0A2540' }}>
                      🔧 Admin Task Running
                    </h4>
                    <p style={{ margin: '0', fontSize: '14px', color: '#6B7280' }}>
                      {adminLoading.syncSingle && 'Syncing country process steps...'}
                      {adminLoading.syncAll && 'Syncing all countries...'}
                      {adminLoading.removeCountry && 'Removing country from acquisition...'}
                      {adminLoading.viewCountries && 'Loading acquisition countries...'}
                      {adminLoading.refreshing && 'Refreshing admin data...'}
                      {adminLoading.loadingCountries && 'Loading available countries...'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Admin Sections - Converting to Tiles */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '25px' }}>
              
              {/* Tile 1: Process Steps Management */}
              <div style={{ 
                background: 'linear-gradient(135deg, #0A2540, #1A365D)',
                borderRadius: '16px',
                padding: '25px',
                color: 'white',
                boxShadow: '0 8px 25px rgba(52, 152, 219, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                  <div style={{ fontSize: '32px' }}>📊</div>
                  <div>
                    <h3 style={{ margin: '0', fontSize: '20px', fontWeight: '600' }}>
                      Process Steps Management
                </h3>
                    <p style={{ margin: '5px 0 0 0', fontSize: '14px', opacity: '0.9' }}>
                      Load process steps from Google Sheets to database tables
                    </p>
                  </div>
                </div>
                
                <div style={{ display: 'grid', gap: '15px' }}>
                  {/* Load to Process Steps Table Only */}
                  <div style={{ 
                    background: 'rgba(255, 255, 255, 0.1)', 
                    padding: '15px', 
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                  }}>
                    <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}>
                      📄 Load to Process Steps Table Only
                    </h4>
                    <p style={{ margin: '0 0 12px 0', fontSize: '13px', opacity: '0.9', lineHeight: '1.4' }}>
                      Updates the default process steps template. Safe operation that preserves all acquisition customizations and progress data.
                    </p>
                    
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <select 
                      value={adminSelectedCountry}
                      onChange={(e) => setAdminSelectedCountry(e.target.value)}
                      style={{
                          background: 'rgba(255, 255, 255, 0.9)',
                          color: '#333',
                          border: 'none',
                        padding: '8px 12px',
                        borderRadius: '6px',
                          fontSize: '12px',
                          minWidth: '120px'
                      }}
                    >
                      <option value="">
                          {adminLoading.loadingCountries ? 'Loading...' : 'Select Country'}
                      </option>
                        {adminAvailableCountries.map(country => (
                        <option key={country} value={country}>{country}</option>
                      ))}
                    </select>
                      
                    <button 
                        onClick={() => {
                          // Will implement single country sync
                        }}
                        disabled={!adminSelectedCountry || adminLoading.loadingCountries}
                      style={{
                          background: (adminSelectedCountry && !adminLoading.loadingCountries) ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.3)',
                          color: (adminSelectedCountry && !adminLoading.loadingCountries) ? '#333' : 'rgba(255, 255, 255, 0.7)',
                        border: 'none',
                          padding: '8px 12px',
                        borderRadius: '6px',
                          cursor: (adminSelectedCountry && !adminLoading.loadingCountries) ? 'pointer' : 'not-allowed',
                          fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                        Load One Country
                    </button>
                      
                        <button 
                        onClick={() => {
                          // Will implement all countries sync
                        }}
                        disabled={adminLoading.loadingCountries}
                          style={{
                          background: !adminLoading.loadingCountries ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.3)',
                          color: !adminLoading.loadingCountries ? '#333' : 'rgba(255, 255, 255, 0.7)',
                            border: 'none',
                          padding: '8px 12px',
                            borderRadius: '6px',
                          cursor: !adminLoading.loadingCountries ? 'pointer' : 'not-allowed',
                          fontSize: '12px',
                            fontWeight: '500'
                          }}
                        >
                        Load All Countries
                        </button>
                        </div>
                      </div>
                  
                  {/* Load to Both Tables */}
                  <div style={{ 
                    background: 'rgba(255, 255, 255, 0.1)', 
                    padding: '15px', 
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                  }}>
                    <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}>
                      📊 Load to Both Process Tables
                    </h4>
                    <p style={{ margin: '0 0 12px 0', fontSize: '13px', opacity: '0.9', lineHeight: '1.4' }}>
                      Updates both default process steps AND acquisition-specific steps for a country. Preserves progress data but updates step customizations.
                    </p>
                    
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <select
                        value={adminSelectedCountry}
                        onChange={(e) => setAdminSelectedCountry(e.target.value)}
                            style={{
                          background: 'rgba(255, 255, 255, 0.9)',
                          color: '#333',
                              border: 'none',
                          padding: '8px 12px',
                              borderRadius: '6px',
                          fontSize: '12px',
                          minWidth: '120px'
                            }}
                          >
                        <option value="">
                          {adminLoading.loadingCountries ? 'Loading...' : 'Select Country'}
                        </option>
                        {adminAvailableCountries.map(country => (
                          <option key={country} value={country}>{country}</option>
                        ))}
                      </select>
                      
                          <button 
                        onClick={() => {
                          // Will implement both tables sync
                        }}
                        disabled={!adminSelectedCountry || adminLoading.loadingCountries}
                            style={{
                          background: (adminSelectedCountry && !adminLoading.loadingCountries) ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.3)',
                          color: (adminSelectedCountry && !adminLoading.loadingCountries) ? '#333' : 'rgba(255, 255, 255, 0.7)',
                              border: 'none',
                              padding: '8px 12px',
                              borderRadius: '6px',
                          cursor: (adminSelectedCountry && !adminLoading.loadingCountries) ? 'pointer' : 'not-allowed',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}
                          >
                        Load to Both Tables
                          </button>
                        </div>
                        </div>
                      </div>
                  </div>

              {/* Tile 2: Step Completions Management */}
              <div style={{
                background: 'linear-gradient(135deg, #e67e22, #d35400)',
                borderRadius: '16px',
                padding: '25px',
                color: 'white',
                boxShadow: '0 8px 25px rgba(230, 126, 34, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                  <div style={{ fontSize: '32px' }}>🔄</div>
                  <div>
                    <h3 style={{ margin: '0', fontSize: '20px', fontWeight: '600' }}>
                      Step Completions Management
                    </h3>
                    <p style={{ margin: '5px 0 0 0', fontSize: '14px', opacity: '0.9' }}>
                      Reset progress data while preserving step customizations
                    </p>
                </div>
              </div>

                <div style={{ display: 'grid', gap: '15px' }}>
                  {/* Resync Step Completions */}
              <div style={{ 
                    background: 'rgba(255, 255, 255, 0.1)', 
                    padding: '15px', 
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
              }}>
                    <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}>
                      🔄 Reload Step Completions Table
                    </h4>
                    <p style={{ margin: '0 0 12px 0', fontSize: '13px', opacity: '0.9', lineHeight: '1.4' }}>
                      Resets all progress data (status, completion dates, notes) for a specific acquisition/country. Preserves step customizations and recalculates planned dates.
                    </p>
                    
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <select 
                      value={adminSelectedAcquisition}
                      onChange={(e) => setAdminSelectedAcquisition(e.target.value)}
                      style={{
                          background: 'rgba(255, 255, 255, 0.9)',
                          color: '#333',
                          border: 'none',
                        padding: '8px 12px',
                        borderRadius: '6px',
                          fontSize: '12px',
                          minWidth: '140px'
                      }}
                    >
                        <option value="">Select Acquisition</option>
                        {/* Will populate with acquisitions */}
                    </select>
                      
                    <select 
                      value={adminSelectedCountry}
                      onChange={(e) => setAdminSelectedCountry(e.target.value)}
                      style={{
                          background: 'rgba(255, 255, 255, 0.9)',
                          color: '#333',
                          border: 'none',
                        padding: '8px 12px',
                        borderRadius: '6px',
                          fontSize: '12px',
                          minWidth: '120px'
                      }}
                    >
                        <option value="">
                          {adminLoading.loadingCountries ? 'Loading...' : 'Select Country'}
                        </option>
                        {adminAvailableCountries.map(country => (
                        <option key={country} value={country}>{country}</option>
                      ))}
                    </select>
                      
                    <button 
                        onClick={() => {
                          // Will implement resync functionality
                        }}
                        disabled={!adminSelectedAcquisition || !adminSelectedCountry || adminLoading.loadingCountries}
                      style={{
                          background: (adminSelectedAcquisition && adminSelectedCountry && !adminLoading.loadingCountries) ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.3)',
                          color: (adminSelectedAcquisition && adminSelectedCountry && !adminLoading.loadingCountries) ? '#333' : 'rgba(255, 255, 255, 0.7)',
                        border: 'none',
                          padding: '8px 12px',
                        borderRadius: '6px',
                          cursor: (adminSelectedAcquisition && adminSelectedCountry && !adminLoading.loadingCountries) ? 'pointer' : 'not-allowed',
                          fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                        Resync Completions
                    </button>
                  </div>
                  
                    <div style={{ 
                      background: 'rgba(255, 255, 255, 0.1)', 
                      padding: '10px', 
                      borderRadius: '6px', 
                      marginTop: '10px',
                      fontSize: '12px',
                      opacity: '0.9'
                    }}>
                      ⚠️ <strong>Warning:</strong> This deletes all progress data (status, completion dates, notes) and recreates step completions
                    </div>
                  </div>
                </div>
              </div>

              {/* Tile 3: Country Management */}
              <div style={{ 
                background: 'linear-gradient(135deg, #800020, #6B001A)',
                borderRadius: '16px',
                padding: '25px',
                color: 'white',
                boxShadow: '0 8px 25px rgba(231, 76, 60, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '20px' }}>
                  <div style={{ fontSize: '32px' }}>🗑️</div>
                  <div>
                    <h3 style={{ margin: '0', fontSize: '20px', fontWeight: '600' }}>
                      Country Management
                </h3>
                    <p style={{ margin: '5px 0 0 0', fontSize: '14px', opacity: '0.9' }}>
                      Remove countries from acquisitions
                    </p>
                  </div>
                </div>
                
                <div style={{ display: 'grid', gap: '15px' }}>
                  {/* Remove Country */}
                  <div style={{ 
                    background: 'rgba(255, 255, 255, 0.1)', 
                    padding: '15px', 
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.2)'
                  }}>
                    <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}>
                      🗑️ Remove Country from Acquisition
                    </h4>
                    <p style={{ margin: '0 0 12px 0', fontSize: '13px', opacity: '0.9', lineHeight: '1.4' }}>
                      Permanently removes a country from an acquisition. Deletes all related data including custom steps, progress tracking, and history for that country.
                    </p>
                    
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <select 
                      value={adminSelectedAcquisition}
                      onChange={(e) => setAdminSelectedAcquisition(e.target.value)}
                      style={{
                          background: 'rgba(255, 255, 255, 0.9)',
                          color: '#333',
                          border: 'none',
                        padding: '8px 12px',
                        borderRadius: '6px',
                          fontSize: '12px',
                          minWidth: '140px'
                      }}
                    >
                        <option value="">Select Acquisition</option>
                        {/* Will populate with acquisitions */}
                    </select>
                      
                    <select 
                      value={adminSelectedCountryForRemoval}
                      onChange={(e) => setAdminSelectedCountryForRemoval(e.target.value)}
                      style={{
                          background: 'rgba(255, 255, 255, 0.9)',
                          color: '#333',
                          border: 'none',
                        padding: '8px 12px',
                        borderRadius: '6px',
                          fontSize: '12px',
                          minWidth: '120px'
                      }}
                    >
                        <option value="">Select Country</option>
                        {/* Will populate with countries for selected acquisition */}
                    </select>
                      
                    <button 
                        onClick={() => {
                          // Will implement country removal
                        }}
                        disabled={!adminSelectedAcquisition || !adminSelectedCountryForRemoval}
                      style={{
                          background: (adminSelectedAcquisition && adminSelectedCountryForRemoval) ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.3)',
                          color: (adminSelectedAcquisition && adminSelectedCountryForRemoval) ? '#333' : 'rgba(255, 255, 255, 0.7)',
                        border: 'none',
                          padding: '8px 12px',
                        borderRadius: '6px',
                          cursor: (adminSelectedAcquisition && adminSelectedCountryForRemoval) ? 'pointer' : 'not-allowed',
                          fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                        Remove Country
                    </button>
                  </div>
                  
                    <div style={{ 
                      background: 'rgba(255, 255, 255, 0.1)', 
                      padding: '10px', 
                        borderRadius: '6px',
                      marginTop: '10px',
                      fontSize: '12px',
                      opacity: '0.9'
                    }}>
                      ⚠️ <strong>Danger:</strong> This permanently deletes ALL data for the country in this acquisition (cannot be undone)
                    </div>
                  </div>
                </div>
              </div>


     {/* Admin Results Display */}
              {adminResults && (
                <div style={{ 
                  border: '2px solid #d5dbdb', 
                  borderRadius: '12px', 
                  padding: '24px',
                  background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)'
                }}>
                  <h3 style={{ fontSize: '20px', color: '#0A2540', margin: '0 0 16px 0', fontWeight: '600' }}>
                    📊 Operation Results
                  </h3>
                  
                  <div style={{ 
                    background: 'white', 
                    padding: '16px', 
                    borderRadius: '8px', 
                    border: '1px solid #dee2e6',
                    fontFamily: 'monospace',
                    fontSize: '13px',
                    maxHeight: '400px',
                    overflow: 'auto'
                  }}>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(adminResults, null, 2)}
                    </pre>
                  </div>
                  
                  <button 
                    onClick={() => setAdminResults(null)}
                    style={{
                      background: 'linear-gradient(135deg, #95a5a6, #7f8c8d)',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '500',
                      marginTop: '12px'
                    }}
                  >
                    Clear Results
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : currentView === 'countries' ? (
          // Country Management View
          <div style={{ 
            background: 'white', 
            padding: '40px', 
            borderRadius: '16px', 
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
              <div>
                <h2 style={{ fontSize: '28px', color: '#0A2540', margin: '0', fontWeight: '600' }}>
                  🏢 {selectedAcquisition?.display_name} - RIF Dashboard
                </h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', margin: '8px 0 0 0' }}>
                  <p style={{ color: '#6B7280', fontSize: '16px', margin: '0' }}>
                    Manage RIF processes across multiple countries
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>
                        Start Date:
                      </span>
                      <span style={{ fontSize: '13px', color: '#0A2540', fontWeight: '500' }}>
                        {currentAcquisitionDates.rif_start_date ? 
                          new Date(currentAcquisitionDates.rif_start_date).toLocaleDateString() : 
                          'Not set'
                        }
                      </span>
                      <button
                        onClick={() => {
                          setAcquisitionStatus(currentAcquisitionStatus);
                          setAcquisitionRifStartDate(currentAcquisitionDates.rif_start_date || '');
                          setAcquisitionRifEndDate(currentAcquisitionDates.rif_end_date || '');
                          setShowAcquisitionStatusModal(true);
                        }}
                        style={{
                          background: 'linear-gradient(135deg, #9c27b0, #7b1fa2)',
                          color: 'white',
                          border: 'none',
                          padding: '2px 6px',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          fontSize: '10px'
                        }}
                      >
                        📅
                      </button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '14px', color: '#6B7280', fontWeight: '500' }}>
                        Status:
                      </span>
                      <div style={{ position: 'relative' }}>
                        <select
                          value={currentAcquisitionStatus}
                          onChange={(e) => handleAcquisitionStatusChange(e.target.value)}
                          style={{
                            padding: '4px 24px 4px 8px',
                            border: '1px solid #e0e0e0',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: '500',
                            color: currentAcquisitionStatus === 'Planning' ? '#f39c12' : 
                                   currentAcquisitionStatus === 'In Progress' ? '#0A2540' : '#50C878',
                            background: 'white',
                            cursor: 'pointer',
                            appearance: 'none'
                          }}
                        >
                          <option value="Planning">Planning</option>
                          <option value="In Progress">In Progress</option>
                          <option value="Completed">Completed</option>
                        </select>
                        <div style={{
                          position: 'absolute',
                          right: '6px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          pointerEvents: 'none',
                          fontSize: '10px',
                          color: '#6B7280'
                        }}>
                          ▼
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                      <button
                  onClick={refreshDashboardData}
                        style={{
                    background: 'linear-gradient(135deg, #D4AF37, #C4A028)',
                          color: 'white',
                          border: 'none',
                    padding: '10px 20px',
                    borderRadius: '8px',
                          cursor: 'pointer',
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                        }}
                      >
                  🔄 Refresh
                      </button>
                <button
                  onClick={() => {
                    setCurrentView('acquisition');
                    clearFormData(); // Clear form when going back
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #95a5a6, #7f8c8d)',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  ← Back to Acquisitions
                </button>
              </div>
            </div>

            {/* Assigned Countries Section */}
            <div style={{ marginBottom: '40px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ fontSize: '20px', color: '#0A2540', margin: '0' }}>
                  RIF Countries ({loadingCountries ? '...' : assignedCountries.length})
                </h3>
                
                {/* Generate Acquisition Shot Clock Button and Latest Report Link */}
                {assignedCountries.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <button
                      onClick={generateAcquisitionShotClock}
                      disabled={generatingAcquisitionShotClock}
                      style={{
                        background: generatingAcquisitionShotClock ? 
                          '#bdc3c7' : 'linear-gradient(135deg, #e67e22, #d35400)',
                        color: 'white',
                        border: 'none',
                        padding: '12px 20px',
                        borderRadius: '8px',
                        cursor: generatingAcquisitionShotClock ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontWeight: '500',
                        boxShadow: generatingAcquisitionShotClock ? 'none' : '0 4px 12px rgba(230, 126, 34, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      {generatingAcquisitionShotClock ? (
                        <>
                          <div style={{
                            width: '16px',
                            height: '16px',
                            border: '2px solid #ffffff',
                            borderTop: '2px solid transparent',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }}></div>
                          Generating Shot Clock...
                        </>
                      ) : (
                        <>🎯 Generate Acquisition Shot Clock</>
                      )}
                    </button>
                    
                    {/* Latest Acquisition Shot Clock Report Link */}
                    {latestAcquisitionShotClock && (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                        <span style={{ color: '#6B7280', fontSize: '10px', fontWeight: '500' }}>
                          View Latest Acquisition Shot Clock Report
                        </span>
                        <a
                          href={latestAcquisitionShotClock.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{
                            background: 'linear-gradient(135deg, #50C878, #3FA760)',
                            color: 'white',
                            textDecoration: 'none',
                            padding: '6px 12px',
                            borderRadius: '6px',
                            fontSize: '11px',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          📊 {latestAcquisitionShotClock.date} ({latestAcquisitionShotClock.countries.length} countries)
                        </a>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              {loadingCountries ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px', 
                  background: '#f8f9fa', 
                  borderRadius: '8px',
                  border: '2px dashed #dee2e6'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '4px solid #f3f3f3',
                    borderTop: '4px solid #0A2540',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 15px'
                  }}></div>
                  <p style={{ color: '#6c757d', fontSize: '16px', margin: '0' }}>
                    Loading assigned countries...
                  </p>
                </div>
              ) : assignedCountries.length === 0 ? (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px', 
                  background: '#f8f9fa', 
                  borderRadius: '8px',
                  border: '2px dashed #dee2e6'
                }}>
                  <p style={{ color: '#6c757d', fontSize: '16px', margin: '0' }}>
                    No countries assigned yet. Click "Add Country" to get started.
                  </p>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '15px' }}>
                  {assignedCountries.map((country) => (
                    <div
                      key={country.id}
                      style={{
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        padding: '20px',
                        background: '#fafafa',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                          <h4 style={{ margin: '0', fontSize: '16px', color: '#0A2540', minWidth: '40px' }}>
                          🌍 {country.country}
                        </h4>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '13px', color: '#6B7280' }}>
                            <span>{country.employee_count.toLocaleString()} employees</span>
                            <span>{country.rif_type}</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span>Start: {new Date(country.start_date).toLocaleDateString()}</span>
                            <button
                              onClick={() => openEditStartDateModal(country)}
                              style={{
                                background: 'linear-gradient(135deg, #9c27b0, #7b1fa2)',
                                color: 'white',
                                border: 'none',
                                  padding: '2px 5px',
                                borderRadius: '3px',
                                cursor: 'pointer',
                                  fontSize: '9px'
                              }}
                            >
                              📅
                            </button>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span>Status:</span>
                            <div style={{ position: 'relative' }}>
                              <select
                                value={country.status || 'Planning'}
                                onChange={(e) => handleCountryStatusChange(country, e.target.value)}
                                style={{
                                    padding: '2px 18px 2px 5px',
                                  border: '1px solid #e0e0e0',
                                  borderRadius: '4px',
                                    fontSize: '11px',
                                  fontWeight: '500',
                                  color: (country.status || 'Planning') === 'Planning' ? '#f39c12' : 
                                         (country.status || 'Planning') === 'In Progress' ? '#0A2540' : '#50C878',
                                  background: 'white',
                                  cursor: 'pointer',
                                  appearance: 'none'
                                }}
                              >
                                <option value="Planning">Planning</option>
                                <option value="In Progress">In Progress</option>
                                <option value="Completed">Completed</option>
                              </select>
                              <div style={{
                                position: 'absolute',
                                  right: '3px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                pointerEvents: 'none',
                                fontSize: '8px',
                                color: '#6B7280'
                              }}>
                                ▼
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                        <div style={{ marginLeft: 'auto', marginRight: '100px' }}>
                        <button
                          onClick={() => openProgressTracking(country)}
                          style={{
                            background: 'linear-gradient(135deg, #800020, #6B001A)',
                            color: 'white',
                            border: 'none',
                              padding: '10px 18px',
                              borderRadius: '8px',
                            cursor: 'pointer',
                              fontSize: '14px',
                              fontWeight: '700',
                            display: 'flex',
                            alignItems: 'center',
                              gap: '8px',
                              whiteSpace: 'nowrap',
                              boxShadow: '0 4px 12px rgba(231, 76, 60, 0.3)',
                              transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.transform = 'translateY(-1px)';
                              e.currentTarget.style.boxShadow = '0 6px 16px rgba(231, 76, 60, 0.4)';
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(231, 76, 60, 0.3)';
                            }}
                          >
                            🎯 Shot Clock Tracker
                        </button>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        
                        {/* COMMENTED OUT: Individual country executive summary generation - now handled at acquisition level
                        <button
                          onClick={() => generateCountryReport(country)}
                          disabled={generatingReports[`${country.country}_${country.id}`]}
                          style={{
                            background: generatingReports[`${country.country}_${country.id}`] ? 
                              '#bdc3c7' : 'linear-gradient(135deg, #0A2540, #1A365D)',
                            color: 'white',
                            border: 'none',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            cursor: generatingReports[`${country.country}_${country.id}`] ? 'not-allowed' : 'pointer',
                            fontSize: '13px',
                            fontWeight: '600',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '2px',
                            lineHeight: '1.2',
                            minWidth: '80px'
                          }}
                        >
                          {generatingReports[`${country.country}_${country.id}`] ? (
                            <>
                              <div style={{
                                width: '12px',
                                height: '12px',
                                border: '2px solid #ffffff',
                                borderTop: '2px solid transparent',
                                borderRadius: '50%',
                                animation: 'spin 1s linear infinite'
                              }}></div>
                              <span>Generating...</span>
                            </>
                          ) : (
                            <>
                              <span>📊 Generate</span>
                              <span>Executive Summary</span>
                            </>
                          )}
                        </button>
                        */}
                        
                        {/* COMMENTED OUT: Individual country executive summary view - now handled at acquisition level
                        {countryExecutiveSummaries[`${country.country}_${country.id}`] && (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', marginLeft: '4px' }}>
                            <span style={{ color: '#6B7280', fontSize: '8px', fontWeight: '500' }}>
                              View Exec. Summary Report
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <span style={{ color: '#6B7280', fontSize: '10px' }}>|</span>
                              <a
                                href={countryExecutiveSummaries[`${country.country}_${country.id}`].url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  background: 'linear-gradient(135deg, #50C878, #3FA760)',
                                  color: 'white',
                                  textDecoration: 'none',
                                  padding: '3px 6px',
                                  borderRadius: '3px',
                                  fontSize: '9px',
                                  fontWeight: '600',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '3px'
                                }}
                              >
                                📄 {countryExecutiveSummaries[`${country.country}_${country.id}`].date}
                              </a>
                            </div>
                          </div>
                        )}
                        */}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
              <button
                onClick={openAddCountryForm}
                style={{
                  background: 'linear-gradient(135deg, #4caf50, #45a049)',
                  color: 'white',
                  border: 'none',
                  padding: '15px 30px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '500',
                  boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
                }}
              >
                + Add Country
              </button>
              
              {false && assignedCountries.length > 1 && (
                <button
                  onClick={generateMultiCountryOverview}
                  disabled={generatingMultiCountry}
                  style={{
                    background: generatingMultiCountry ? 
                      '#bdc3c7' : 'linear-gradient(135deg, #9c27b0, #7b1fa2)',
                    color: 'white',
                    border: 'none',
                    padding: '15px 30px',
                    borderRadius: '8px',
                    cursor: generatingMultiCountry ? 'not-allowed' : 'pointer',
                    fontSize: '16px',
                    fontWeight: '500',
                    boxShadow: generatingMultiCountry ? 'none' : '0 4px 12px rgba(156, 39, 176, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  {generatingMultiCountry ? (
                    <>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid #ffffff',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      Generating Overview...
                    </>
                  ) : (
                    <>📊 Generate Acquisition Exec. Summary Report</>
                  )}
                </button>
              )}
            </div>

            {/* Smart Add Country Form Modal */}
            {showCountryForm && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
              }}>
                <div style={{
                  background: 'white',
                  padding: '40px',
                  borderRadius: '16px',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                  maxWidth: '500px',
                  width: '90%',
                  maxHeight: '80vh',
                  overflowY: 'auto',
                  position: 'relative'
                }}>
                  
                  {/* Loading Overlay */}
                  {loading && (
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'rgba(255, 255, 255, 0.9)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '16px',
                      zIndex: 10
                    }}>
                      <div style={{
                        width: '50px',
                        height: '50px',
                        border: '5px solid #f3f3f3',
                        borderTop: '5px solid #4caf50',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        marginBottom: '20px'
                      }}></div>
                      <p style={{ 
                        color: '#0A2540', 
                        fontSize: '16px', 
                        fontWeight: '500',
                        margin: '0',
                        textAlign: 'center'
                      }}>
                        Adding {selectedCountry} to RIF...<br />
                        <span style={{ fontSize: '14px', color: '#6B7280' }}>
                          Setting up folder structure and initializing process steps
                        </span>
                      </p>
                    </div>
                  )}
                  <h3 style={{ fontSize: '24px', color: '#0A2540', marginBottom: '20px', textAlign: 'center' }}>
                    Add Country to RIF
                  </h3>
                  
                  {/* Country Selection */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#0A2540' }}>
                      Country *
                    </label>
                    <select
                      value={selectedCountry}
                      onChange={(e) => handleCountrySelection(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px',
                        fontSize: '16px',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        background: 'white'
                      }}
                    >
                      <option value="">-- Select Country --</option>
                      {availableCountries.map((country) => (
                        <option key={country} value={country}>
                          {country}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Employee Count */}
                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#0A2540' }}>
                      Employee Count *
                    </label>
                    <input
                      type="number"
                      value={employeeCount || ''}
                      onChange={(e) => handleEmployeeCountChange(parseInt(e.target.value) || 0)}
                      placeholder="Enter number of employees"
                      style={{
                        width: '100%',
                        padding: '12px',
                        fontSize: '16px',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px'
                      }}
                    />
                  </div>

                  {/* Smart RIF Type Dropdown */}
                  {(selectedCountry && employeeCount > 0) && (
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#0A2540' }}>
                        RIF Type *
                      </label>
                      
                      {rifTypesLoading ? (
                        // Loading state for RIF types - ONLY show this, nothing else
                        <div style={{
                          width: '100%',
                          padding: '12px',
                          fontSize: '16px',
                          border: '2px solid #e0e0e0',
                          borderRadius: '8px',
                          background: '#f8f9fa',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px'
                        }}>
                          <div style={{
                            width: '20px',
                            height: '20px',
                            border: '2px solid #f3f3f3',
                            borderTop: '2px solid #0A2540',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }}></div>
                          <span style={{ color: '#6B7280' }}>Loading RIF types for {employeeCount.toLocaleString()} employees...</span>
                        </div>
                      ) : (!rifTypesLoading && availableRifTypes.length > 0) ? (
                        // RIF types loaded - show dropdown (ensure no auto-selection)
                        <select
                          value={selectedRifType || ""} // Force empty value if selectedRifType is undefined
                          onChange={(e) => setSelectedRifType(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '12px',
                            fontSize: '16px',
                            border: '2px solid #e0e0e0',
                            borderRadius: '8px',
                            background: 'white'
                          }}
                        >
                          <option value="">-- Select RIF Type --</option>
                          {availableRifTypes.map((rifType) => (
                            <option 
                              key={rifType.rif_type} 
                              value={rifType.rif_type}
                              disabled={!rifType.is_applicable}
                              style={{
                                color: rifType.is_applicable ? '#0A2540' : '#bdc3c7',
                                fontWeight: rifType.is_applicable ? 'normal' : '300'
                              }}
                            >
                              {rifType.rif_type} {rifType.is_applicable ? '✓' : '✗'} - {rifType.description}
                            </option>
                          ))}
                        </select>
                      ) : (!rifTypesLoading && availableRifTypes.length === 0 && selectedCountry && employeeCount > 0) ? (
                        // No RIF types found (only show if we've actually tried to load them)
                        <div style={{
                          width: '100%',
                          padding: '12px',
                          fontSize: '16px',
                          border: '2px solid #e0e0e0',
                          borderRadius: '8px',
                          background: '#fff3cd',
                          color: '#856404'
                        }}>
                          No RIF types defined for {selectedCountry}. Please add RIF types to the RIF_Types tab and run ETL sync.
                        </div>
                      ) : null}
                      
                      {/* RIF Type Explanation */}
                      {selectedRifType && (
                        <div style={{ 
                          marginTop: '10px', 
                          padding: '12px', 
                          background: '#e3f2fd', 
                          borderRadius: '6px',
                          fontSize: '14px',
                          color: '#1565c0'
                        }}>
                          {availableRifTypes.find(rt => rt.rif_type === selectedRifType)?.description}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Start Date */}
                  <div style={{ marginBottom: '30px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#0A2540' }}>
                      Planned Start Date
                    </label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '12px',
                        fontSize: '16px',
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px'
                      }}
                    />
                  </div>

                  {/* Form Actions */}
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                    <button
                      onClick={clearFormData}
                      style={{
                        background: '#95a5a6',
                        color: 'white',
                        border: 'none',
                        padding: '12px 20px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        setSelectedCountry('');
                        setEmployeeCount(0);
                        setSelectedRifType('');
                        setStartDate('');
                        setAvailableRifTypes([]);
                      }}
                      style={{
                        background: '#f39c12',
                        color: 'white',
                        border: 'none',
                        padding: '12px 20px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      🗑️ Clear
                    </button>
                    <button
                      onClick={submitCountryAssignment}
                      disabled={!selectedCountry || !selectedRifType || employeeCount <= 0 || loading}
                      style={{
                        background: selectedCountry && selectedRifType && employeeCount > 0 ? 
                          'linear-gradient(135deg, #4caf50, #45a049)' : '#bdc3c7',
                        color: 'white',
                        border: 'none',
                        padding: '12px 24px',
                        borderRadius: '8px',
                        cursor: selectedCountry && selectedRifType && employeeCount > 0 ? 'pointer' : 'not-allowed',
                        fontSize: '14px',
                        fontWeight: '500'
                      }}
                    >
                      {loading ? '⏳ Adding...' : '✅ Add Country'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          // Progress Tracking View
          <div style={{ 
            background: 'white', 
            padding: '40px', 
            borderRadius: '16px', 
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            

            {/* Step deletion spinner */}
            {deletingStep && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.4)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 999,
                backdropFilter: 'blur(2px)'
              }}>
                <div style={{
                  background: 'white',
                  padding: '30px 40px',
                  borderRadius: '16px',
                  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '20px',
                  minWidth: '300px'
                }}>
                  <div style={{
                    width: '50px',
                    height: '50px',
                    border: '4px solid #ffebee',
                    borderTop: '4px solid #800020',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  <div style={{ textAlign: 'center' }}>
                    <h4 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#0A2540' }}>
                      🗑️ Deleting Step
                    </h4>
                    <p style={{ margin: '0', fontSize: '14px', color: '#6B7280' }}>
                      Removing step from acquisition...
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
              <div>
                <h2 style={{ fontSize: '28px', color: '#0A2540', margin: '0', fontWeight: '600' }}>
                  📈 {selectedCountryForProgress?.country} RIF Progress - {selectedAcquisition?.display_name}
                </h2>
``                <div style={{ display: 'flex', alignItems: 'center', gap: '20px', margin: '5px 0 0 0' }}>
                  <p style={{ color: '#6B7280', fontSize: '16px', margin: '0' }}>
                    {selectedCountryForProgress?.rif_type} | {selectedCountryForProgress?.employee_count.toLocaleString()} employees
                  </p>
                      
                      {/* HIDDEN: Individual Country Shot Clock Report Button - Change 'false' to 'true' to re-enable */}
                      {false && (
                      <button
                        onClick={generateProgressReport}
                        disabled={generatingProgressReport}
                        style={{
                          background: generatingProgressReport ? '#bdc3c7' : 'linear-gradient(135deg, #e67e22, #d35400)',
                          color: 'white',
                          border: 'none',
                          padding: '6px 12px',
                          borderRadius: '6px',
                          cursor: generatingProgressReport ? 'not-allowed' : 'pointer',
                          fontSize: '11px',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        {generatingProgressReport ? (
                          <>
                            <div style={{
                              width: '12px',
                              height: '12px',
                              border: '2px solid #ffffff',
                              borderTop: '2px solid transparent',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite'
                            }}></div>
                            Generating...
                          </>
                        ) : (
                          <>📊 Generate Shot Clock Report</>
                        )}
                      </button>
                      )}
                      
                      {/* Latest Report Link */}
                      {latestProgressReport && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ color: '#6B7280', fontSize: '11px' }}>|</span>
                          <span style={{ color: '#6B7280', fontSize: '11px' }}>Latest:</span>
                          <a
                            href={latestProgressReport.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              background: 'linear-gradient(135deg, #50C878, #3FA760)',
                              color: 'white',
                              textDecoration: 'none',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              fontSize: '10px',
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}
                          >
                            📄 {latestProgressReport.date}
                          </a>
                        </div>
                      )}
                </div>
              </div>
              <button
                onClick={() => {
                  setCurrentView('countries');
                  setSelectedCountryForProgress(null);
                  setProcessSteps([]);
                  setStepCompletions([]);
                }}
                style={{
                  background: 'linear-gradient(135deg, #95a5a6, #7f8c8d)',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                ← Back to Countries
              </button>
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '60px' }}>
                <div style={{
                  width: '50px',
                  height: '50px',
                  border: '4px solid #f3f3f3',
                  borderTop: '4px solid #0A2540',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  margin: '0 auto 20px'
                }}></div>
                <p style={{ color: '#6B7280', fontSize: '16px' }}>Loading progress data...</p>
              </div>
            ) : (
              <>
                {/* Progress Overview */}
                <div style={{ marginBottom: '40px' }}>
                  <h3 style={{ fontSize: '20px', color: '#0A2540', marginBottom: '20px' }}>
                    Progress Overview
                  </h3>
                  
                  {processSteps.length > 0 && (
                    <div style={{ 
                      background: '#f8f9fa', 
                      padding: '25px', 
                      borderRadius: '12px',
                      marginBottom: '30px'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                        <span style={{ fontSize: '16px', fontWeight: '600', color: '#0A2540' }}>
                          Overall Progress
                        </span>
                        <span style={{ fontSize: '16px', fontWeight: '600', color: '#0A2540' }}>
                          {stepCompletions.filter(c => c.status === 'Completed').length}/{processSteps.length} ({calculateWeightedProgress()}%)
                        </span>
                      </div>
                      
                      {/* Progress Bar */}
                      <div style={{ 
                        background: '#e0e0e0', 
                        borderRadius: '10px', 
                        height: '20px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          background: 'linear-gradient(135deg, #4caf50, #45a049)',
                          height: '100%',
                          width: `${calculateWeightedProgress()}%`,
                          transition: 'width 0.3s ease'
                        }}></div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Hierarchical Process Steps */}
                <div style={{ marginBottom: '40px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3 style={{ fontSize: '20px', color: '#0A2540', margin: '0' }}>
                      Process Steps ({processSteps.length})
                    </h3>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      <button
                        onClick={openAddStepModal}
                        style={{
                          background: 'linear-gradient(135deg, #50C878, #3FA760)',
                          color: 'white',
                          border: 'none',
                          padding: '8px 16px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}
                      >
                        ➕ Add Step
                      </button>
                      <button
                        onClick={() => selectedCountryForProgress && fetchCountryProgress(selectedCountryForProgress)}
                        style={{
                          background: 'linear-gradient(135deg, #D4AF37, #C4A028)',
                          color: 'white',
                          border: 'none',
                          padding: '8px 16px',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}
                      >
                        🔄 Refresh
                      </button>
                    <button
                      onClick={toggleExpandAll}
                      style={{
                        background: 'linear-gradient(135deg, #6c5ce7, #5a4fcf)',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                      {allExpanded ? '📂 Collapse All' : '📁 Expand All'}
                    </button>
                    </div>
                  </div>
                  
                  {processSteps.length === 0 ? (
                    <p style={{ color: '#6B7280', textAlign: 'center', padding: '40px' }}>
                      No process steps found. Make sure the country has been synced with ETL.
                    </p>
                  ) : (
                    <div style={{ display: 'grid', gap: '25px' }}>
                      {organizeStepsByWorkflowPhase(processSteps).map((processGroup, groupIndex) => (
                        <div key={processGroup.processType} style={{ marginBottom: '30px' }}>
                          {/* Workflow Phase Header - Clickable with Color Coding */}
                          <div 
                            onClick={() => toggleGroupExpansion(processGroup.processType)}
                            style={{
                              background: getWorkflowPhaseColors(processGroup.processType).background,
                              color: 'white',
                              padding: '15px 25px',
                              borderRadius: '8px 8px 0 0',
                              marginBottom: '0',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h4 style={{ margin: '0', fontSize: '18px', fontWeight: '600' }}>
                              📋 {processGroup.displayName || processGroup.processType} ({processGroup.stepCount} steps)
                            </h4>
                              <span style={{ fontSize: '20px', transition: 'transform 0.3s ease' }}>
                                {expandedGroups[processGroup.processType] ? '▼' : '▶'}
                              </span>
                            </div>
                          </div>
                          
                          {/* Steps within this process type - Only show if expanded */}
                          {expandedGroups[processGroup.processType] && (
                            <div style={{
                              border: '1px solid #2c3e50',
                              borderTop: 'none',
                              borderRadius: '0 0 8px 8px',
                              background: 'white'
                            }}>
                              {processGroup.steps.map((step, stepIndex) => (
                                <div key={step.step_number}>
                                {/* Parent or Single Step - Clickable if Parent */}
                                <div 
                                  onClick={step.isParent ? () => toggleParentStepExpansion(step.step_number) : undefined}
                                  style={{
                                    padding: '15px 25px',
                                    borderBottom: stepIndex < processGroup.steps.length - 1 ? '1px solid #e0e0e0' : 'none',
                                    background: step.isParent ? getWorkflowPhaseColors(processGroup.processType).light : 'white',
                                    cursor: step.isParent ? 'pointer' : 'default',
                                    transition: 'all 0.3s ease'
                                  }}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div>
                                      <h5 style={{ margin: '0 0 5px 0', fontSize: '16px', color: '#0A2540' }}>
                                        {step.isParent ? '📁' : '📄'} {step.step_number}: {step.step_name}
                                      </h5>
                                      
                                      {/* Step Description/Notes */}
                                      {step.notes && (
                                        <div style={{ fontSize: '12px', color: '#6B7280', fontStyle: 'italic', marginBottom: '8px' }}>
                                          {step.notes}
                                        </div>
                                      )}
                                      
                                      <div style={{ fontSize: '12px', color: '#6B7280', display: 'flex', flexWrap: 'wrap', gap: '15px' }}>
                                        <span>DRI: {step.dri_role}</span>
                                        <span style={{ 
                                          color: step.risk_level === 'HIGH' ? '#FF6B6B' : step.risk_level === 'MEDIUM' ? '#ff9800' : '#4caf50',
                                          fontWeight: '600'
                                        }}>
                                          Risk: {step.risk_level}
                                        </span>
                                        {(step.timeline_day_min !== null && step.timeline_day_min !== undefined) && (
                                          <span>
                                            Timeline: Day {step.timeline_day_min}
                                            {(step.duration_days && step.duration_days > 1) ? ` (${step.duration_days} days)` : ''}
                                          </span>
                                        )}
                                        <span style={{ color: step.is_critical_path ? '#FF6B6B' : '#6B7280', fontWeight: step.is_critical_path ? '600' : 'normal' }}>
                                          Critical Path: {step.is_critical_path ? 'Yes' : 'No'}
                                        </span>
                                        <span style={{ color: step.is_legal_gate ? '#ff9800' : '#6B7280', fontWeight: step.is_legal_gate ? '600' : 'normal' }}>
                                          Legal Gate: {step.is_legal_gate ? 'Yes' : 'No'}
                                        </span>
                                        {step.depends_on && (
                                          <span>Dependencies: {step.depends_on}</span>
                                        )}
                                        {stepCompletions.find(c => c.step_number === step.step_number) && (
                                          <span style={{ 
                                            color: stepCompletions.find(c => c.step_number === step.step_number)?.status === 'Completed' ? '#4caf50' : '#ff9800',
                                            fontWeight: '600'
                                          }}>
                                            Status: {step.isParent && step.subSteps && step.subSteps.length > 0 
                                              ? calculateParentStepStatus(step)
                                              : (stepCompletions.find(c => c.step_number === step.step_number)?.status || 'Pending')
                                            }
                                          </span>
                                        )}
                                      </div>
                                      
                                      {/* Calculated dates (faint italic) with edit button for planned start */}
                                      {rifStartDate && getPlannedStartDate(step) && (
                                        <div style={{ fontSize: '11px', color: '#95a5a6', fontStyle: 'italic', marginTop: '6px' }}>
                                          {/* Show different info for completed vs pending steps */}
                                          {step.isParent && step.subSteps && step.subSteps.length > 0 
                                            ? getParentStepDateDisplayWithEdit(step) 
                                            : getStepDateDisplayWithEdit(step)
                                          }
                                        </div>
                                      )}
                                    </div>
                                    
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                      {/* Action buttons for non-parent steps or show sub-step info for parents */}
                                      {step.isParent && step.subSteps && step.subSteps.length > 0 ? (
                                        <>
                                          {/* Parent Step Status (calculated from sub-steps) */}
                                          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                            <div style={{
                                              padding: '3px 6px',
                                              fontSize: '9px',
                                              border: '1px solid #ddd',
                                              borderRadius: '3px',
                                              background: '#e3f2fd',
                                              color: '#1976d2',
                                              minWidth: '80px',
                                              fontWeight: '500'
                                            }}>
                                              {(() => {
                                                const parentStatus = calculateParentStepStatus(step);
                                                const statusIcons = {
                                                  'Pending': '📋',
                                                  'In Progress': '🔄',
                                                  'Completed': '✅',
                                                  'Blocked': '🚫',
                                                  'Skipped': '⏭️'
                                                };
                                                return `${statusIcons[parentStatus as keyof typeof statusIcons]} ${parentStatus}`;
                                              })()}
                                            </div>
                                          <span style={{ fontSize: '12px', color: getWorkflowPhaseColors(processGroup.processType).text }}>
                                            {step.subSteps.length} sub-steps
                                          </span>
                                          <span style={{ fontSize: '16px', color: getWorkflowPhaseColors(processGroup.processType).text }}>
                                            {expandedParentSteps[step.step_number] ? '▼' : '▶'}
                                          </span>
                                          </div>
                                          
                                          {/* Edit/Delete buttons for parent steps */}
                                          <div style={{ display: 'flex', gap: '4px', marginLeft: '10px' }}>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                openStepEditModal(step);
                                              }}
                                              style={{
                                                background: 'linear-gradient(135deg, #0A2540, #1A365D)',
                                                color: 'white',
                                                border: 'none',
                                                padding: '3px 6px',
                                                borderRadius: '3px',
                                                cursor: 'pointer',
                                                fontSize: '9px'
                                              }}
                                              title="Edit Step"
                                            >
                                              ✏️
                                            </button>
                                            
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                openDeleteConfirmModal(step);
                                              }}
                                              style={{
                                                background: 'linear-gradient(135deg, #800020, #6B001A)',
                                                color: 'white',
                                                border: 'none',
                                                padding: '3px 6px',
                                                borderRadius: '3px',
                                                cursor: 'pointer',
                                                fontSize: '9px'
                                              }}
                                              title="Delete Step"
                                            >
                                              🗑️
                                            </button>
                                          </div>
                                        </>
                                      ) : (
                                        // Inline Status Controls
                                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                          {(() => {
                                            const completion = stepCompletions.find(c => c.step_number === step.step_number);
                                            const isCompleted = completion?.status === 'Completed';
                                            
                                            return (
                                              <>
                                                {/* Status Display/Dropdown */}
                                                {isCompleted ? (
                                                  // Completed steps: Show grayed out status
                                                  <div style={{
                                                    padding: '3px 6px',
                                                    fontSize: '9px',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '3px',
                                                    background: '#f8f9fa',
                                                    color: '#6c757d',
                                                    minWidth: '80px'
                                                  }}>
                                                    ✅ Completed
                                                  </div>
                                                ) : (
                                                  // Non-completed steps: Show editable dropdown or spinner
                                                  updatingStepStatus === step.step_number ? (
                                                    // Show spinner while updating
                                                    <div style={{
                                                      padding: '3px 6px',
                                                      fontSize: '9px',
                                                      border: '1px solid #ddd',
                                                      borderRadius: '3px',
                                                      background: '#f8f9fa',
                                                      color: '#6c757d',
                                                      minWidth: '80px',
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      gap: '4px'
                                                    }}>
                                                      <div style={{
                                                        width: '8px',
                                                        height: '8px',
                                                        border: '1px solid #6c757d',
                                                        borderTop: '1px solid transparent',
                                                        borderRadius: '50%',
                                                        animation: 'spin 1s linear infinite'
                                                      }}></div>
                                                      Updating...
                                                    </div>
                                                  ) : (
                                                    // Show editable dropdown
                                                    <select
                                                      value={completion?.status || 'Pending'}
                                                      onChange={(e) => {
                                                        e.stopPropagation();
                                                        const newStatus = e.target.value;
                                                        
                                                        // For all status changes (except Pending), open modal first to allow user to add details
                                                        if (['In Progress', 'Completed', 'Blocked', 'Skipped'].includes(newStatus)) {
                                                          // Set the selected status for the modal
                                                          setSelectedStatus(newStatus);
                                                          openStepCompletionModal(step);
                                                        } else {
                                                          // For Pending status, update immediately (no additional info needed)
                                                          handleQuickStatusChange(step, newStatus);
                                                        }
                                                      }}
                                              style={{
                                                        padding: '3px 6px',
                                                        fontSize: '9px',
                                                        border: '1px solid #ddd',
                                                        borderRadius: '3px',
                                                        background: 'white',
                                                        cursor: 'pointer'
                                                      }}
                                                    >
                                                      <option value="Pending">📋 Pending</option>
                                                      <option value="In Progress">🔄 In Progress</option>
                                                      <option value="Completed">✅ Completed</option>
                                                      <option value="Blocked">🚫 Blocked</option>
                                                      <option value="Skipped">⏭️ Skipped</option>
                                                    </select>
                                                  )
                                                )}
                                                
                                                {/* Action Button */}
                                            <button
                                              onClick={(e) => {
                                                    e.stopPropagation();
                                                openStepCompletionModal(step);
                                              }}
                                              style={{
                                                    background: isCompleted ? 
                                                      'linear-gradient(135deg, #ff9800, #f57c00)' : 
                                                      'linear-gradient(135deg, #6c757d, #5a6268)',
                                                color: 'white',
                                                border: 'none',
                                                    padding: '3px 6px',
                                                    borderRadius: '3px',
                                                cursor: 'pointer',
                                                    fontSize: '9px'
                                              }}
                                            >
                                                  {isCompleted ? '✏️' : '⚙️'}
                                            </button>
                                            
                                            {/* Step Edit Button */}
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                openStepEditModal(step);
                                              }}
                                              style={{
                                                background: 'linear-gradient(135deg, #0A2540, #1A365D)',
                                                color: 'white',
                                                border: 'none',
                                                padding: '3px 6px',
                                                borderRadius: '3px',
                                                cursor: 'pointer',
                                                fontSize: '9px'
                                              }}
                                              title="Edit Step"
                                            >
                                              ✏️
                                            </button>
                                            
                                            {/* Step Delete Button */}
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                openDeleteConfirmModal(step);
                                              }}
                                              style={{
                                                background: 'linear-gradient(135deg, #800020, #6B001A)',
                                                color: 'white',
                                                border: 'none',
                                                padding: '3px 6px',
                                                borderRadius: '3px',
                                                cursor: 'pointer',
                                                fontSize: '9px'
                                              }}
                                              title="Delete Step"
                                            >
                                              🗑️
                                            </button>
                                              </>
                                            );
                                          })()}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  
                                  {/* Resource Edit Mode (exclude Parent Steps) */}
                                  {editingResources === step.step_number && step.step_type !== 'Parent' ? (
                                    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e0e0e0' }}>
                                      <div style={{ background: '#f8f9fa', padding: '15px', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                                        <h6 style={{ margin: '0 0 15px 0', fontSize: '14px', color: '#0A2540', fontWeight: '600' }}>
                                          {getStepResources(step).length === 0 ? 'Add Resources' : 'Edit Resources'}
                                        </h6>
                                        
                                        <div style={{ display: 'grid', gap: '12px' }}>
                                          {/* Documents */}
                                          <div>
                                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: '600', color: '#0A2540' }}>
                                              📄 Documents Required
                                            </label>
                                            <div style={{ fontSize: '10px', color: '#6c757d', fontStyle: 'italic', marginBottom: '5px' }}>
                                              Multiple URLs can be separated by commas, spaces, or new lines
                                            </div>
                                            <textarea
                                              value={resourceEditData.documents_required || ''}
                                              onChange={(e) => setResourceEditData(prev => ({...prev, documents_required: e.target.value}))}
                                              placeholder="Google Drive links (one per line)"
                                              rows={2}
                                              style={{
                                                width: '100%',
                                                padding: '8px',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px',
                                                fontSize: '11px',
                                                fontFamily: 'monospace'
                                              }}
                                            />
                                          </div>
                                          
                                          {/* Email Templates */}
                                          <div>
                                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: '600', color: '#0A2540' }}>
                                              📧 Email Templates
                                            </label>
                                            <div style={{ fontSize: '10px', color: '#6c757d', fontStyle: 'italic', marginBottom: '5px' }}>
                                              Multiple URLs can be separated by commas, spaces, or new lines
                                            </div>
                                            <textarea
                                              value={resourceEditData.email_templates || ''}
                                              onChange={(e) => setResourceEditData(prev => ({...prev, email_templates: e.target.value}))}
                                              placeholder="Google Drive links (one per line)"
                                              rows={2}
                                              style={{
                                                width: '100%',
                                                padding: '8px',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px',
                                                fontSize: '11px',
                                                fontFamily: 'monospace'
                                              }}
                                            />
                                          </div>
                                          
                                          {/* Call Scripts */}
                                          <div>
                                            <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: '600', color: '#0A2540' }}>
                                              📞 Call Scripts
                                            </label>
                                            <div style={{ fontSize: '10px', color: '#6c757d', fontStyle: 'italic', marginBottom: '5px' }}>
                                              Multiple URLs can be separated by commas, spaces, or new lines
                                            </div>
                                            <textarea
                                              value={resourceEditData.call_scripts || ''}
                                              onChange={(e) => setResourceEditData(prev => ({...prev, call_scripts: e.target.value}))}
                                              placeholder="Google Drive links (one per line)"
                                              rows={2}
                                              style={{
                                                width: '100%',
                                                padding: '8px',
                                                border: '1px solid #ddd',
                                                borderRadius: '4px',
                                                fontSize: '11px',
                                                fontFamily: 'monospace'
                                              }}
                                            />
                                          </div>
                                        </div>
                                        
                                        {/* Save/Cancel Buttons */}
                                        <div style={{ display: 'flex', gap: '8px', marginTop: '15px', justifyContent: 'flex-end' }}>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              cancelEditingResources();
                                            }}
                                            style={{
                                              background: 'linear-gradient(135deg, #95a5a6, #7f8c8d)',
                                              color: 'white',
                                              border: 'none',
                                              padding: '6px 12px',
                                              borderRadius: '4px',
                                              cursor: 'pointer',
                                              fontSize: '11px',
                                              fontWeight: '500'
                                            }}
                                          >
                                            Cancel
                                          </button>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                saveResourceChanges(step);
                                              }}
                                              disabled={savingResources}
                                              style={{
                                                background: savingResources ? '#bdc3c7' : 'linear-gradient(135deg, #50C878, #3FA760)',
                                                color: 'white',
                                                border: 'none',
                                                padding: '6px 12px',
                                                borderRadius: '4px',
                                                cursor: savingResources ? 'not-allowed' : 'pointer',
                                                fontSize: '11px',
                                                fontWeight: '500'
                                              }}
                                            >
                                              {savingResources ? '⏳ Saving...' : '💾 Save'}
                                            </button>
                                        </div>
                                      </div>
                                    </div>
                                  ) : (
                                    /* Available Resources for this step (exclude Parent Steps) */
                                    getStepResources(step).length > 0 && step.step_type !== 'Parent' && (
                                    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e0e0e0' }}>
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                                        {getStepResources(step).map((resource, index) => (
                                          <a
                                            key={index}
                                            href={resource.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()} // Prevent parent step click
                                            style={{
                                              background: getWorkflowPhaseColors(processGroup.processType).light,
                                              color: getWorkflowPhaseColors(processGroup.processType).text,
                                              padding: '6px 10px',
                                              borderRadius: '6px',
                                              textDecoration: 'none',
                                              fontSize: '11px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '4px',
                                              border: `1px solid ${getWorkflowPhaseColors(processGroup.processType).text}20`
                                            }}
                                          >
                                            {resource.icon} {resource.name}
                                          </a>
                                        ))}
                                        
                                        {/* Edit Resources Button */}
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            startEditingResources(step);
                                          }}
                                          style={{
                                            background: 'linear-gradient(135deg, #D4AF37, #C4A028)',
                                            color: 'white',
                                            border: 'none',
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '10px',
                                            fontWeight: '500',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '3px'
                                          }}
                                          title="Edit Resources"
                                        >
                                          ✏️ Edit
                                        </button>
                                      </div>
                                    </div>
                                  ))}

                                  {/* Add Resource link for steps without resources (exclude Parent Steps) */}
                                  {getStepResources(step).length === 0 && editingResources !== step.step_number && step.step_type !== 'Parent' && (
                                    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e0e0e0' }}>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          startEditingResources(step);
                                        }}
                                        style={{
                                          background: 'linear-gradient(135deg, #6c757d, #5a6268)',
                                          color: 'white',
                                          border: 'none',
                                          padding: '6px 12px',
                                          borderRadius: '6px',
                                          cursor: 'pointer',
                                          fontSize: '11px',
                                          fontWeight: '500',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '5px'
                                        }}
                                        title="Add Resources"
                                      >
                                        ➕ Add Resources
                                      </button>
                                    </div>
                                  )}

                                  {/* Show completion links for completed steps */}
                                  {stepCompletions.find(c => c.step_number === step.step_number && c.status === 'Completed') && getCompletionLinks(step).length > 0 && (
                                    <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #4caf50' }}>
                                      <div style={{ fontSize: '11px', color: '#4caf50', fontWeight: '600', marginBottom: '6px' }}>
                                        ✅ Actual Files Used:
                                      </div>
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                        {getCompletionLinks(step).map((link, index) => (
                                          <a
                                            key={index}
                                            href={link.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={(e) => e.stopPropagation()}
                                            style={{
                                              background: '#e8f5e8',
                                              color: '#2e7d32',
                                              padding: '6px 10px',
                                              borderRadius: '6px',
                                              textDecoration: 'none',
                                              fontSize: '11px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '4px',
                                              border: '1px solid #4caf5040'
                                            }}
                                          >
                                            📄 {link.name}
                                          </a>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Sub-steps (if this is a Parent step and expanded) */}
                                {step.isParent && step.subSteps && expandedParentSteps[step.step_number] && step.subSteps.map((subStep: ProcessStep, subIndex: number) => (
                                  <div
                                    key={subStep.step_number}
                                    style={{
                                      padding: '12px 50px',
                                      borderBottom: '1px solid #f0f0f0',
                                      background: '#fafafa',
                                      borderLeft: `3px solid ${getWorkflowPhaseColors(processGroup.processType).text}`
                                    }}
                                  >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                      <div>
                                        <h6 style={{ margin: '0 0 3px 0', fontSize: '14px', color: '#0A2540' }}>
                                          📄 {subStep.step_number}: {subStep.step_name}
                                        </h6>
                                        
                                        {/* Sub-step Description/Notes */}
                                        {subStep.notes && (
                                          <div style={{ fontSize: '11px', color: '#6B7280', fontStyle: 'italic', marginBottom: '6px' }}>
                                            {subStep.notes}
                                          </div>
                                        )}
                                        
                                        <div style={{ fontSize: '11px', color: '#6B7280', display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
                                          <span>DRI: {subStep.dri_role}</span>
                                          <span style={{ 
                                            color: subStep.risk_level === 'HIGH' ? '#FF6B6B' : subStep.risk_level === 'MEDIUM' ? '#ff9800' : '#4caf50',
                                            fontWeight: '600'
                                          }}>
                                            Risk: {subStep.risk_level}
                                          </span>
                                          {(subStep.timeline_day_min !== null && subStep.timeline_day_min !== undefined) && (
                                            <span>
                                              Timeline: Day {subStep.timeline_day_min}
                                              {(subStep.duration_days && subStep.duration_days > 1) ? ` (${subStep.duration_days} days)` : ''}
                                            </span>
                                          )}
                                          <span style={{ color: subStep.is_critical_path ? '#FF6B6B' : '#6B7280', fontWeight: subStep.is_critical_path ? '600' : 'normal' }}>
                                            Critical Path: {subStep.is_critical_path ? 'Yes' : 'No'}
                                          </span>
                                          <span style={{ color: subStep.is_legal_gate ? '#ff9800' : '#6B7280', fontWeight: subStep.is_legal_gate ? '600' : 'normal' }}>
                                            Legal Gate: {subStep.is_legal_gate ? 'Yes' : 'No'}
                                          </span>
                                          {subStep.depends_on && (
                                            <span>Dependencies: {subStep.depends_on}</span>
                                          )}
                                          {stepCompletions.find(c => c.step_number === subStep.step_number) && (
                                            <span style={{ 
                                              color: stepCompletions.find(c => c.step_number === subStep.step_number)?.status === 'Completed' ? '#4caf50' : '#ff9800',
                                              fontWeight: '600'
                                            }}>
                                              Status: {stepCompletions.find(c => c.step_number === subStep.step_number)?.status || 'Pending'}
                                            </span>
                                          )}
                                        </div>
                                        
                                        {/* Sub-step Date Display with edit button */}
                                        <div style={{ fontSize: '10px', color: '#6B7280', fontStyle: 'italic', marginTop: '4px' }}>
                                          {getStepDateDisplayWithEdit(subStep)}
                                        </div>
                                      </div>
                                      
                                      {/* Inline Status Controls for sub-steps */}
                                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                        {(() => {
                                          const completion = stepCompletions.find(c => c.step_number === subStep.step_number);
                                          const isCompleted = completion?.status === 'Completed';
                                          
                                          return (
                                            <>
                                              {/* Status Display/Dropdown */}
                                              {isCompleted ? (
                                                // Completed sub-steps: Show grayed out status
                                                <div style={{
                                                  padding: '2px 4px',
                                                  fontSize: '8px',
                                                  border: '1px solid #ddd',
                                                  borderRadius: '2px',
                                                  background: '#f8f9fa',
                                                  color: '#6c757d',
                                                  minWidth: '80px'
                                                }}>
                                                  ✅ Completed
                                                </div>
                                              ) : (
                                                // Non-completed sub-steps: Show editable dropdown or spinner
                                                updatingStepStatus === subStep.step_number ? (
                                                  // Show spinner while updating
                                                  <div style={{
                                                    padding: '2px 4px',
                                                    fontSize: '8px',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '2px',
                                                    background: '#f8f9fa',
                                                    color: '#6c757d',
                                                    minWidth: '80px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '3px'
                                                  }}>
                                                    <div style={{
                                                      width: '6px',
                                                      height: '6px',
                                                      border: '1px solid #6c757d',
                                                      borderTop: '1px solid transparent',
                                                      borderRadius: '50%',
                                                      animation: 'spin 1s linear infinite'
                                                    }}></div>
                                                    Updating...
                                                  </div>
                                                ) : (
                                                  // Show editable dropdown
                                                  <select
                                                    value={completion?.status || 'Pending'}
                                                    onChange={(e) => {
                                                      const newStatus = e.target.value;
                                                      
                                                      // For all status changes (except Pending), open modal first to allow user to add details
                                                      if (['In Progress', 'Completed', 'Blocked', 'Skipped'].includes(newStatus)) {
                                                        // Set the selected status for the modal
                                                        setSelectedStatus(newStatus);
                                                        openStepCompletionModal(subStep);
                                                      } else {
                                                        // For Pending status, update immediately (no additional info needed)
                                                        handleQuickStatusChange(subStep, newStatus);
                                                      }
                                                    }}
                                            style={{
                                                      padding: '2px 4px',
                                                      fontSize: '8px',
                                                      border: '1px solid #ddd',
                                                      borderRadius: '2px',
                                                      background: 'white',
                                              cursor: 'pointer',
                                                      minWidth: '100px'
                                                    }}
                                                  >
                                                    <option value="Pending">📋 Pending</option>
                                                    <option value="In Progress">🔄 In Progress</option>
                                                    <option value="Completed">✅ Completed</option>
                                                    <option value="Blocked">🚫 Blocked</option>
                                                    <option value="Skipped">⏭️ Skipped</option>
                                                  </select>
                                                )
                                              )}
                                              
                                              {/* Action Button */}
                                          <button
                                            onClick={() => openStepCompletionModal(subStep)}
                                            style={{
                                                  background: isCompleted ? 
                                                    'linear-gradient(135deg, #ff9800, #f57c00)' : 
                                                    'linear-gradient(135deg, #6c757d, #5a6268)',
                                              color: 'white',
                                              border: 'none',
                                                  padding: '2px 4px',
                                                  borderRadius: '2px',
                                              cursor: 'pointer',
                                                  fontSize: '8px'
                                            }}
                                          >
                                                {isCompleted ? '✏️' : '⚙️'}
                                          </button>
                                          
                                          {/* Sub-step Edit Button */}
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              openStepEditModal(subStep);
                                            }}
                                            style={{
                                              background: 'linear-gradient(135deg, #0A2540, #1A365D)',
                                              color: 'white',
                                              border: 'none',
                                              padding: '2px 4px',
                                              borderRadius: '2px',
                                              cursor: 'pointer',
                                              fontSize: '8px'
                                            }}
                                            title="Edit Sub-Step"
                                          >
                                            ✏️
                                          </button>
                                          
                                          {/* Sub-step Delete Button */}
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              deleteStep(subStep);
                                            }}
                                            style={{
                                              background: 'linear-gradient(135deg, #800020, #6B001A)',
                                              color: 'white',
                                              border: 'none',
                                              padding: '2px 4px',
                                              borderRadius: '2px',
                                              cursor: 'pointer',
                                              fontSize: '8px'
                                            }}
                                            title="Delete Sub-Step"
                                          >
                                            🗑️
                                          </button>
                                            </>
                                          );
                                        })()}
                                      </div>
                                    </div>
                                    
                                    {/* Resource Edit Mode for sub-steps */}
                                    {editingResources === subStep.step_number ? (
                                      <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #f0f0f0' }}>
                                        <div style={{ background: '#f8f9fa', padding: '10px', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                                          <h6 style={{ margin: '0 0 10px 0', fontSize: '12px', color: '#0A2540', fontWeight: '600' }}>
                                            {getStepResources(subStep).length === 0 ? 'Add Resources' : 'Edit Resources'}
                                          </h6>
                                          
                                          <div style={{ display: 'grid', gap: '8px' }}>
                                            {/* Documents */}
                                            <div>
                                              <label style={{ display: 'block', marginBottom: '3px', fontSize: '10px', fontWeight: '600', color: '#0A2540' }}>
                                                📄 Documents
                                              </label>
                                              <div style={{ fontSize: '8px', color: '#6c757d', fontStyle: 'italic', marginBottom: '3px' }}>
                                                Multiple URLs: separate by commas, spaces, or new lines
                                              </div>
                                              <textarea
                                                value={resourceEditData.documents_required || ''}
                                                onChange={(e) => setResourceEditData(prev => ({...prev, documents_required: e.target.value}))}
                                                placeholder="Google Drive links (one per line)"
                                                rows={1}
                                                style={{
                                                  width: '100%',
                                                  padding: '6px',
                                                  border: '1px solid #ddd',
                                                  borderRadius: '3px',
                                                  fontSize: '10px',
                                                  fontFamily: 'monospace'
                                                }}
                                              />
                                            </div>
                                            
                                            {/* Email Templates */}
                                            <div>
                                              <label style={{ display: 'block', marginBottom: '3px', fontSize: '10px', fontWeight: '600', color: '#0A2540' }}>
                                                📧 Email Templates
                                              </label>
                                              <div style={{ fontSize: '8px', color: '#6c757d', fontStyle: 'italic', marginBottom: '3px' }}>
                                                Multiple URLs: separate by commas, spaces, or new lines
                                              </div>
                                              <textarea
                                                value={resourceEditData.email_templates || ''}
                                                onChange={(e) => setResourceEditData(prev => ({...prev, email_templates: e.target.value}))}
                                                placeholder="Google Drive links (one per line)"
                                                rows={1}
                                                style={{
                                                  width: '100%',
                                                  padding: '6px',
                                                  border: '1px solid #ddd',
                                                  borderRadius: '3px',
                                                  fontSize: '10px',
                                                  fontFamily: 'monospace'
                                                }}
                                              />
                                            </div>
                                            
                                            {/* Call Scripts */}
                                            <div>
                                              <label style={{ display: 'block', marginBottom: '3px', fontSize: '10px', fontWeight: '600', color: '#0A2540' }}>
                                                📞 Call Scripts
                                              </label>
                                              <div style={{ fontSize: '8px', color: '#6c757d', fontStyle: 'italic', marginBottom: '3px' }}>
                                                Multiple URLs: separate by commas, spaces, or new lines
                                              </div>
                                              <textarea
                                                value={resourceEditData.call_scripts || ''}
                                                onChange={(e) => setResourceEditData(prev => ({...prev, call_scripts: e.target.value}))}
                                                placeholder="Google Drive links (one per line)"
                                                rows={1}
                                                style={{
                                                  width: '100%',
                                                  padding: '6px',
                                                  border: '1px solid #ddd',
                                                  borderRadius: '3px',
                                                  fontSize: '10px',
                                                  fontFamily: 'monospace'
                                                }}
                                              />
                                            </div>
                                          </div>
                                          
                                          {/* Save/Cancel Buttons */}
                                          <div style={{ display: 'flex', gap: '6px', marginTop: '10px', justifyContent: 'flex-end' }}>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                cancelEditingResources();
                                              }}
                                              style={{
                                                background: 'linear-gradient(135deg, #95a5a6, #7f8c8d)',
                                                color: 'white',
                                                border: 'none',
                                                padding: '4px 8px',
                                                borderRadius: '3px',
                                                cursor: 'pointer',
                                                fontSize: '9px',
                                                fontWeight: '500'
                                              }}
                                            >
                                              Cancel
                                            </button>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                saveResourceChanges(subStep);
                                              }}
                                              disabled={savingResources}
                                              style={{
                                                background: savingResources ? '#bdc3c7' : 'linear-gradient(135deg, #50C878, #3FA760)',
                                                color: 'white',
                                                border: 'none',
                                                padding: '4px 8px',
                                                borderRadius: '3px',
                                                cursor: savingResources ? 'not-allowed' : 'pointer',
                                                fontSize: '9px',
                                                fontWeight: '500'
                                              }}
                                            >
                                              {savingResources ? '⏳ Saving...' : '💾 Save'}
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    ) : (
                                      /* Available Resources for sub-steps */
                                      getStepResources(subStep).length > 0 && (
                                      <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #f0f0f0' }}>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                                          {getStepResources(subStep).map((resource, index) => (
                                            <a
                                              key={index}
                                              href={resource.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              style={{
                                                background: getWorkflowPhaseColors(processGroup.processType).light,
                                                color: getWorkflowPhaseColors(processGroup.processType).text,
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                                textDecoration: 'none',
                                                fontSize: '10px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '3px',
                                                border: `1px solid ${getWorkflowPhaseColors(processGroup.processType).text}20`
                                              }}
                                            >
                                              {resource.icon} {resource.name}
                                            </a>
                                          ))}
                                          
                                          {/* Edit Resources Button for sub-steps */}
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              startEditingResources(subStep);
                                            }}
                                            style={{
                                              background: 'linear-gradient(135deg, #D4AF37, #C4A028)',
                                              color: 'white',
                                              border: 'none',
                                              padding: '3px 6px',
                                              borderRadius: '3px',
                                              cursor: 'pointer',
                                              fontSize: '8px',
                                              fontWeight: '500',
                                              display: 'flex',
                                              alignItems: 'center',
                                              gap: '2px'
                                            }}
                                            title="Edit Resources"
                                          >
                                            ✏️ Edit
                                          </button>
                                        </div>
                                      </div>
                                    ))}

                                    {/* Add Resource link for sub-steps without resources (exclude Parent Steps) */}
                                    {getStepResources(subStep).length === 0 && editingResources !== subStep.step_number && subStep.step_type !== 'Parent' && (
                                      <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #f0f0f0' }}>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            startEditingResources(subStep);
                                          }}
                                          style={{
                                            background: 'linear-gradient(135deg, #6c757d, #5a6268)',
                                            color: 'white',
                                            border: 'none',
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '9px',
                                            fontWeight: '500',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '3px'
                                          }}
                                          title="Add Resources"
                                        >
                                          ➕ Add Resources
                                        </button>
                                      </div>
                                    )}

                                    {/* Show completion links for completed sub-steps */}
                                    {stepCompletions.find(c => c.step_number === subStep.step_number && c.status === 'Completed') && getCompletionLinks(subStep).length > 0 && (
                                      <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #4caf50' }}>
                                        <div style={{ fontSize: '10px', color: '#4caf50', fontWeight: '600', marginBottom: '4px' }}>
                                          ✅ Actual Files Used:
                                        </div>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                          {getCompletionLinks(subStep).map((link, index) => (
                                            <a
                                              key={index}
                                              href={link.url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              style={{
                                                background: '#e8f5e8',
                                                color: '#2e7d32',
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                                textDecoration: 'none',
                                                fontSize: '10px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '3px',
                                                border: '1px solid #4caf5040'
                                              }}
                                            >
                                              📄 {link.name}
                                            </a>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Edit Start Date Modal */}
        {showEditDateModal && selectedCountryForEdit && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1500
          }}>
            <div style={{
              background: 'white',
              padding: '30px',
              borderRadius: '16px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              maxWidth: '400px',
              width: '90%',
              position: 'relative'
            }}>
              
              {/* Loading Overlay */}
              {updatingStartDate && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(255, 255, 255, 0.9)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '16px',
                  zIndex: 10
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '4px solid #f3f3f3',
                    borderTop: '4px solid #9c27b0',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    marginBottom: '15px'
                  }}></div>
                  <p style={{ color: '#0A2540', fontSize: '14px', margin: '0' }}>
                    Updating RIF start date...
                  </p>
                </div>
              )}

              <h3 style={{ fontSize: '20px', color: '#0A2540', marginBottom: '20px', textAlign: 'center' }}>
                Edit Start Date
              </h3>
              
              <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                <h4 style={{ margin: '0 0 5px 0', color: '#0A2540' }}>
                  🌍 {selectedCountryForEdit.country}
                </h4>
                <p style={{ margin: '0', color: '#6B7280', fontSize: '14px' }}>
                  {selectedCountryForEdit.employee_count.toLocaleString()} employees | {selectedCountryForEdit.rif_type}
                </p>
              </div>

              <div style={{ marginBottom: '25px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#0A2540' }}>
                  Start Date *
                </label>
                <input
                  type="date"
                  value={editingStartDate}
                  onChange={(e) => setEditingStartDate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '16px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px'
                  }}
                />
              </div>

              {/* Modal Actions */}
              <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
                <button
                  onClick={closeEditDateModal}
                  disabled={updatingStartDate}
                  style={{
                    background: '#95a5a6',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    cursor: updatingStartDate ? 'not-allowed' : 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={updateRifStartDate}
                  disabled={!editingStartDate || updatingStartDate}
                  style={{
                    background: editingStartDate ? 'linear-gradient(135deg, #9c27b0, #7b1fa2)' : '#bdc3c7',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    cursor: editingStartDate && !updatingStartDate ? 'pointer' : 'not-allowed',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  {updatingStartDate ? '⏳ Updating...' : '✅ Update Date'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Planned Date Modal */}
        {showEditPlannedDateModal && selectedStepForDateEdit && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1500
          }}>
            <div style={{
              background: 'white',
              padding: '30px',
              borderRadius: '16px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              maxWidth: '400px',
              width: '90%',
              position: 'relative'
            }}>
              
              {/* Loading Overlay */}
              {updatingPlannedDate && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(255, 255, 255, 0.9)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '16px',
                  zIndex: 10
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '4px solid #f3f3f3',
                    borderTop: '4px solid #0A2540',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    marginBottom: '15px'
                  }}></div>
                  <p style={{ color: '#0A2540', fontSize: '14px', margin: '0' }}>
                    Updating planned start date...
                  </p>
                </div>
              )}

              <h3 style={{ fontSize: '20px', color: '#0A2540', marginBottom: '20px', textAlign: 'center' }}>
                📅 Edit Planned Start Date
              </h3>
              
              <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                <h4 style={{ margin: '0 0 5px 0', color: '#0A2540' }}>
                  {selectedStepForDateEdit.step_number}: {selectedStepForDateEdit.step_name}
                </h4>
                <p style={{ margin: '0', color: '#6B7280', fontSize: '14px' }}>
                  {selectedStepForDateEdit.step_type} | {selectedStepForDateEdit.workflow_phase}
                </p>
                {selectedStepForDateEdit.step_type === 'Parent' && (
                  <div style={{ 
                    marginTop: '10px', 
                    padding: '8px 12px', 
                    background: '#e3f2fd', 
                    border: '1px solid #2196f3', 
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: '#1976d2'
                  }}>
                    ℹ️ Updating this Parent Step will also update the planned dates of all its Sub-Steps
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '25px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#0A2540' }}>
                  Planned Start Date *
                </label>
                <input
                  type="date"
                  value={editingPlannedDate}
                  onChange={(e) => setEditingPlannedDate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '16px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px'
                  }}
                />
              </div>

              {/* Modal Actions */}
              <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
                <button
                  onClick={() => {
                    setShowEditPlannedDateModal(false);
                    setSelectedStepForDateEdit(null);
                    setEditingPlannedDate('');
                  }}
                  disabled={updatingPlannedDate}
                  style={{
                    background: '#95a5a6',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    cursor: updatingPlannedDate ? 'not-allowed' : 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={submitPlannedDateUpdate}
                  disabled={!editingPlannedDate || updatingPlannedDate}
                  style={{
                    background: editingPlannedDate ? 'linear-gradient(135deg, #0A2540, #1A365D)' : '#bdc3c7',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    cursor: editingPlannedDate && !updatingPlannedDate ? 'pointer' : 'not-allowed',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  {updatingPlannedDate ? '⏳ Updating...' : '✅ Update Date'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Acquisition Status Modal */}
        {showAcquisitionStatusModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1500
          }}>
            <div style={{
              background: 'white',
              padding: '30px',
              borderRadius: '16px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              maxWidth: '500px',
              width: '90%',
              position: 'relative'
            }}>
              <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                <h4 style={{ margin: '0 0 5px 0', color: '#0A2540' }}>
                  📊 {acquisitionStatus === 'Completed' ? 'Complete Acquisition Setup' : 'Edit Acquisition Dates'}
                </h4>
                <p style={{ margin: '0', color: '#6B7280', fontSize: '14px' }}>
                  {selectedAcquisition?.display_name} - {acquisitionStatus === 'Completed' ? 'Set end date to complete' : 'Update RIF start date'}
                </p>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#0A2540' }}>
                  Status *
                </label>
                <select
                  value={acquisitionStatus}
                  onChange={(e) => setAcquisitionStatus(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: '#0A2540'
                  }}
                >
                  <option value="Planning">Planning</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#0A2540' }}>
                  Start Date
                </label>
                <input
                  type="date"
                  value={acquisitionRifStartDate}
                  onChange={(e) => setAcquisitionRifStartDate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: '#0A2540'
                  }}
                />
              </div>

              {acquisitionStatus === 'Completed' && (
                <div style={{ marginBottom: '25px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#0A2540' }}>
                    RIF End Date *
                  </label>
                  <input
                    type="date"
                    value={acquisitionRifEndDate}
                    onChange={(e) => setAcquisitionRifEndDate(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: `2px solid ${!acquisitionRifEndDate ? '#800020' : '#e0e0e0'}`,
                      borderRadius: '8px',
                      fontSize: '14px',
                      color: '#0A2540'
                    }}
                  />
                  {!acquisitionRifEndDate && (
                    <p style={{ margin: '5px 0 0 0', color: '#800020', fontSize: '12px' }}>
                      End date is required when status is Completed
                    </p>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowAcquisitionStatusModal(false)}
                  style={{
                    background: '#95a5a6',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={submitAcquisitionStatusUpdate}
                  disabled={updatingStatus || (acquisitionStatus === 'Completed' && !acquisitionRifEndDate)}
                  style={{
                    background: (updatingStatus || (acquisitionStatus === 'Completed' && !acquisitionRifEndDate)) ? '#bdc3c7' : 'linear-gradient(135deg, #9b59b6, #8e44ad)',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    cursor: (updatingStatus || (acquisitionStatus === 'Completed' && !acquisitionRifEndDate)) ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  {updatingStatus ? '⏳ Updating...' : 
                   acquisitionStatus === 'Completed' ? '✅ Complete Acquisition' : '📅 Update Dates'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Country Status Modal */}
        {showCountryStatusModal && selectedCountryForStatus && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1500
          }}>
            <div style={{
              background: 'white',
              padding: '30px',
              borderRadius: '16px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              maxWidth: '500px',
              width: '90%',
              position: 'relative'
            }}>
              <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                <h4 style={{ margin: '0 0 5px 0', color: '#0A2540' }}>
                  📋 Complete Country RIF
                </h4>
                <p style={{ margin: '0', color: '#6B7280', fontSize: '14px' }}>
                  {selectedCountryForStatus.country} | {selectedCountryForStatus.employee_count.toLocaleString()} employees - Set end date to complete
                </p>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#0A2540' }}>
                  Status *
                </label>
                <select
                  value={countryStatus}
                  onChange={(e) => setCountryStatus(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '14px',
                    color: '#0A2540'
                  }}
                >
                  <option value="Planning">Planning</option>
                  <option value="In Progress">In Progress</option>
                  <option value="Completed">Completed</option>
                </select>
              </div>

              {countryStatus === 'Completed' && (
                <div style={{ marginBottom: '25px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#0A2540' }}>
                    End Date *
                  </label>
                  <input
                    type="date"
                    value={countryEndDate}
                    onChange={(e) => setCountryEndDate(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: `2px solid ${!countryEndDate ? '#800020' : '#e0e0e0'}`,
                      borderRadius: '8px',
                      fontSize: '14px',
                      color: '#0A2540'
                    }}
                  />
                  {!countryEndDate && (
                    <p style={{ margin: '5px 0 0 0', color: '#800020', fontSize: '12px' }}>
                      End date is required when status is Completed
                    </p>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowCountryStatusModal(false)}
                  style={{
                    background: '#95a5a6',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={submitCountryStatusUpdate}
                  disabled={updatingStatus || (countryStatus === 'Completed' && !countryEndDate)}
                  style={{
                    background: (updatingStatus || (countryStatus === 'Completed' && !countryEndDate)) ? '#bdc3c7' : 'linear-gradient(135deg, #9b59b6, #8e44ad)',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '8px',
                    cursor: (updatingStatus || (countryStatus === 'Completed' && !countryEndDate)) ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  {updatingStatus ? '⏳ Updating...' : '✅ Update Status'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step Completion Modal */}
        {showCompletionModal && selectedStep && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000
          }}>
            <div style={{
              background: 'white',
              padding: '40px',
              borderRadius: '16px',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              overflowY: 'auto',
              position: 'relative'
            }}>
              
              {/* Loading Overlay */}
              {completingStep && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(255, 255, 255, 0.9)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '16px',
                  zIndex: 10
                }}>
                  <div style={{
                    width: '50px',
                    height: '50px',
                    border: '5px solid #f3f3f3',
                    borderTop: '5px solid #4caf50',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    marginBottom: '20px'
                  }}></div>
                  <p style={{ color: '#0A2540', fontSize: '16px', fontWeight: '500', margin: '0' }}>
                    Updating step status to {selectedStatus}...
                  </p>
                </div>
              )}

              <h3 style={{ fontSize: '24px', color: '#0A2540', marginBottom: '20px', textAlign: 'center' }}>
                Step Details: {selectedStep.step_number}
              </h3>

              {/* Tab Navigation */}
              <div style={{ display: 'flex', marginBottom: '20px', borderBottom: '2px solid #e0e0e0' }}>
                <button
                  onClick={() => setActiveModalTab('details')}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    border: 'none',
                    background: activeModalTab === 'details' ? '#0A2540' : 'transparent',
                    color: activeModalTab === 'details' ? 'white' : '#6B7280',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    borderRadius: '4px 4px 0 0',
                    transition: 'all 0.2s ease'
                  }}
                >
                  📝 Step Details
                </button>
                <button
                  onClick={() => setActiveModalTab('history')}
                  style={{
                    flex: 1,
                    padding: '12px 20px',
                    border: 'none',
                    background: activeModalTab === 'history' ? '#0A2540' : 'transparent',
                    color: activeModalTab === 'history' ? 'white' : '#6B7280',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    borderRadius: '4px 4px 0 0',
                    transition: 'all 0.2s ease'
                  }}
                >
                  📊 History ({stepHistory.length})
                </button>
              </div>
              
              {/* Tab Content */}
              {activeModalTab === 'details' && (
                <>
                  <div style={{ marginBottom: '25px', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}>
                    <h4 style={{ margin: '0 0 15px 0', color: '#0A2540' }}>{selectedStep.step_name}</h4>
                
                {/* Step Description/Notes */}
                {selectedStep.notes && (
                  <div style={{ fontSize: '14px', color: '#6B7280', fontStyle: 'italic', marginBottom: '15px' }}>
                    {selectedStep.notes}
                  </div>
                )}
                
                <div style={{ fontSize: '14px', color: '#6B7280', display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
                  <span>DRI: {selectedStep.dri_role}</span>
                  <span style={{ 
                    color: selectedStep.risk_level === 'HIGH' ? '#FF6B6B' : selectedStep.risk_level === 'MEDIUM' ? '#ff9800' : '#4caf50',
                    fontWeight: '600'
                  }}>
                    Risk: {selectedStep.risk_level}
                  </span>
                  {(selectedStep.timeline_day_min !== null && selectedStep.timeline_day_min !== undefined) && (
                    <span>
                      Timeline: Day {selectedStep.timeline_day_min}
                      {(selectedStep.duration_days && selectedStep.duration_days > 1) ? ` (${selectedStep.duration_days} days)` : ''}
                    </span>
                  )}
                  <span style={{ color: selectedStep.is_critical_path ? '#FF6B6B' : '#6B7280', fontWeight: selectedStep.is_critical_path ? '600' : 'normal' }}>
                    Critical Path: {selectedStep.is_critical_path ? 'Yes' : 'No'}
                  </span>
                  <span style={{ color: selectedStep.is_legal_gate ? '#ff9800' : '#6B7280', fontWeight: selectedStep.is_legal_gate ? '600' : 'normal' }}>
                    Legal Gate: {selectedStep.is_legal_gate ? 'Yes' : 'No'}
                  </span>
                  {selectedStep.depends_on && (
                    <span>Dependencies: {selectedStep.depends_on}</span>
                  )}
                </div>
              </div>


              {/* Step Status Selection */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#0A2540' }}>
                  Step Status <span style={{ color: '#800020' }}>*</span>
                </label>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '16px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    background: 'white'
                  }}
                >
                  <option value="Pending">📋 Pending</option>
                  <option value="In Progress">🔄 In Progress</option>
                  <option value="Completed">✅ Completed</option>
                  <option value="Blocked">🚫 Blocked</option>
                  <option value="Skipped">⏭️ Skipped</option>
                </select>
              </div>

              {/* Smart Date Fields Based on Status */}
              <div style={{ marginBottom: '20px' }}>
                {/* Always show Planned Start Date */}
                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#0A2540' }}>
                    Planned Start Date
                  </label>
                  <input
                    type="date"
                    value={getPlannedStartDate(selectedStep)?.toISOString().split('T')[0] || ''}
                    readOnly
                    style={{
                      width: '100%',
                      padding: '12px',
                      fontSize: '16px',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px',
                      background: '#f8f9fa',
                      color: '#6B7280'
                    }}
                  />
                </div>

                {/* Conditional Date Fields */}
                <div style={{ display: 'grid', gridTemplateColumns: selectedStatus === 'Completed' ? '1fr 1fr' : '1fr', gap: '15px' }}>
                  {/* Show Actual Start Date for In Progress and Completed */}
                  {(selectedStatus === 'In Progress' || selectedStatus === 'Completed') && (
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#0A2540' }}>
                        Actual Start Date {(selectedStatus === 'In Progress' || selectedStatus === 'Completed') && <span style={{ color: '#800020' }}>*</span>}
                  </label>
                  <input
                    type="date"
                    value={actualStartDate}
                    onChange={(e) => setActualStartDate(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '12px',
                      fontSize: '16px',
                      border: '2px solid #e0e0e0',
                      borderRadius: '8px'
                    }}
                  />
                </div>
                  )}

                  {/* Show Action Date for Completed, Blocked, Skipped */}
                  {(['Completed', 'Blocked', 'Skipped'].includes(selectedStatus)) && (
                    <div>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#0A2540' }}>
                        {getDateLabel(selectedStatus)} {selectedStatus === 'Completed' && <span style={{ color: '#800020' }}>*</span>}
                </label>
                <input
                  type="date"
                  value={completionDate}
                  onChange={(e) => setCompletionDate(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '16px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px'
                  }}
                />
                    </div>
                  )}
                </div>
              </div>


              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#0A2540' }}>
                  {getNotesLabel(selectedStatus)}
                </label>
                <textarea
                  value={completionNotes}
                  onChange={(e) => setCompletionNotes(e.target.value)}
                  placeholder={getNotesPlaceholder(selectedStatus)}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '14px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ marginBottom: '30px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#0A2540' }}>
                  Actual Files/Folders Links
                  <span style={{ fontSize: '12px', color: '#6B7280', fontWeight: 'normal' }}> (Optional)</span>
                </label>
                <textarea
                  value={completionLinks}
                  onChange={(e) => setCompletionLinks(e.target.value)}
                  placeholder="Add links to the actual files/folders you used for this step (one per line)&#10;Example:&#10;https://drive.google.com/file/d/123 - Final business case&#10;https://drive.google.com/folder/d/456 - Supporting documents"
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px',
                    fontSize: '14px',
                    border: '2px solid #e0e0e0',
                    borderRadius: '8px',
                    resize: 'vertical'
                  }}
                />
                <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '5px' }}>
                  💡 Add links to the specific files/folders you created or used for this acquisition
                </div>
              </div>
                </>
              )}

              {/* History Tab Content */}
              {activeModalTab === 'history' && (
                <div style={{ marginBottom: '25px' }}>
                  <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '20px' }}>
                    <h4 style={{ margin: '0 0 20px 0', color: '#0A2540', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      📊 Status History Timeline
                    </h4>
                    
                    {loadingHistory ? (
                      <div style={{ textAlign: 'center', padding: '40px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          border: '4px solid #e0e0e0',
                          borderTop: '4px solid #0A2540',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                          margin: '0 auto 15px'
                        }}></div>
                        <p style={{ color: '#6B7280', fontSize: '14px', margin: '0' }}>
                          Loading step history...
                        </p>
                      </div>
                    ) : stepHistory.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
                        <p style={{ fontSize: '16px', margin: '0' }}>No status changes recorded yet.</p>
                        <p style={{ fontSize: '14px', margin: '10px 0 0 0' }}>History will appear here after the first status update.</p>
                      </div>
                    ) : (
                      <div style={{ position: 'relative' }}>
                        {/* Timeline Line */}
                        <div style={{
                          position: 'absolute',
                          left: '20px',
                          top: '0',
                          bottom: '0',
                          width: '2px',
                          background: 'linear-gradient(to bottom, #0A2540, #800020)',
                          zIndex: 1
                        }}></div>
                        
                        {stepHistory.map((historyItem: any, index: number) => {
                          const isLatest = index === stepHistory.length - 1;
                          const statusColors = {
                            'Pending': '#95a5a6',
                            'In Progress': '#f39c12', 
                            'Completed': '#50C878',
                            'Blocked': '#800020',
                            'Skipped': '#9b59b6'
                          };
                          
                          return (
                            <div key={index} style={{ 
                              position: 'relative', 
                              paddingLeft: '50px', 
                              paddingBottom: index === stepHistory.length - 1 ? '0' : '25px',
                              zIndex: 2
                            }}>
                              {/* Timeline Dot */}
                              <div style={{
                                position: 'absolute',
                                left: '11px',
                                top: '8px',
                                width: '18px',
                                height: '18px',
                                borderRadius: '50%',
                                background: statusColors[historyItem.new_status as keyof typeof statusColors] || '#95a5a6',
                                border: '3px solid white',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                zIndex: 3
                              }}></div>
                              
                              {/* Status Change Card */}
                              <div style={{
                                background: 'white',
                                border: `2px solid ${statusColors[historyItem.new_status as keyof typeof statusColors] || '#95a5a6'}`,
                                borderRadius: '8px',
                                padding: '15px',
                                boxShadow: isLatest ? '0 4px 12px rgba(52, 152, 219, 0.2)' : '0 2px 4px rgba(0,0,0,0.1)'
                              }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                                  <div>
                                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#0A2540', marginBottom: '4px' }}>
                                      {historyItem.old_status} → {historyItem.new_status}
                                      {isLatest && <span style={{ 
                                        background: '#0A2540', 
                                        color: 'white', 
                                        padding: '2px 6px', 
                                        borderRadius: '10px', 
                                        fontSize: '10px', 
                                        marginLeft: '8px' 
                                      }}>CURRENT</span>}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#6B7280' }}>
                                      {new Date(historyItem.changed_date).toLocaleDateString()} at {new Date(historyItem.changed_date).toLocaleTimeString()}
                                    </div>
                                  </div>
                                  <div style={{ fontSize: '12px', color: '#6B7280', textAlign: 'right' }}>
                                    {historyItem.days_in_status !== null && (
                                      <div>{historyItem.days_in_status} day{historyItem.days_in_status !== 1 ? 's' : ''}</div>
                                    )}
                                    <div>by {historyItem.changed_by}</div>
                                  </div>
                                </div>
                                
                                {/* Dates */}
                                {(historyItem.started_date || historyItem.completed_date) && (
                                  <div style={{ fontSize: '12px', color: '#34495e', marginBottom: '8px' }}>
                                    {historyItem.started_date && (
                                      <span style={{ marginRight: '15px' }}>
                                        📅 Started: {new Date(historyItem.started_date).toLocaleDateString()}
                                      </span>
                                    )}
                                    {historyItem.completed_date && (
                                      <span>
                                        🏁 {historyItem.new_status === 'Completed' ? 'Completed' : 
                                            historyItem.new_status === 'Blocked' ? 'Blocked' : 'Skipped'}: {new Date(historyItem.completed_date).toLocaleDateString()}
                                      </span>
                                    )}
                                  </div>
                                )}
                                
                                {/* Notes */}
                                {historyItem.notes && (
                                  <div style={{ 
                                    fontSize: '13px', 
                                    color: '#0A2540', 
                                    background: '#ecf0f1', 
                                    padding: '8px 12px', 
                                    borderRadius: '4px',
                                    fontStyle: 'italic'
                                  }}>
                                    💬 "{historyItem.notes}"
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Modal Actions */}
              <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
                <button
                  onClick={closeCompletionModal}
                  disabled={completingStep}
                  style={{
                    background: '#95a5a6',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: completingStep ? 'not-allowed' : 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
                  <button
                    onClick={submitStepCompletion}
                    disabled={completingStep || 
                      (selectedStatus === 'Completed' && (!completionDate || !actualStartDate)) ||
                      (selectedStatus === 'In Progress' && !actualStartDate)
                    }
                    style={{
                      background: (() => {
                        const isValid = (selectedStatus === 'Completed' && completionDate && actualStartDate) ||
                                       (selectedStatus === 'In Progress' && actualStartDate) ||
                                       (['Blocked', 'Skipped', 'Pending'].includes(selectedStatus));
                        return isValid ? 'linear-gradient(135deg, #4caf50, #45a049)' : '#bdc3c7';
                      })(),
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      cursor: (() => {
                        const isValid = (selectedStatus === 'Completed' && completionDate && actualStartDate) ||
                                       (selectedStatus === 'In Progress' && actualStartDate) ||
                                       (['Blocked', 'Skipped', 'Pending'].includes(selectedStatus));
                        return isValid && !completingStep ? 'pointer' : 'not-allowed';
                      })(),
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    {completingStep ? 
                      '⏳ Updating...' :
                      `✅ Update Status to ${selectedStatus}`
                    }
                  </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirmModal && stepToDelete && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '0',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'hidden',
              boxShadow: '0 25px 80px rgba(0, 0, 0, 0.4)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative'
            }}>
              
              {/* Modal Header */}
              <div style={{
                background: 'linear-gradient(135deg, #800020, #6B001A)',
                color: 'white',
                padding: '25px 30px',
                borderRadius: '16px 16px 0 0'
              }}>
                <h3 style={{ margin: '0', fontSize: '22px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '24px' }}>🗑️</span>
                  Delete Step
                </h3>
                <p style={{ margin: '5px 0 0 0', fontSize: '14px', opacity: '0.9' }}>
                  This action will mark the step as inactive
                </p>
              </div>

              {/* Modal Content */}
              <div style={{ padding: '30px', overflow: 'auto', flex: 1 }}>
                <div style={{ marginBottom: '25px' }}>
                  <h4 style={{ margin: '0 0 15px 0', fontSize: '18px', color: '#0A2540' }}>
                    Are you sure you want to delete this step?
                  </h4>
                  
                  <div style={{
                    background: '#fff5f5',
                    padding: '20px',
                    borderRadius: '12px',
                    border: '1px solid #fed7d7',
                    marginBottom: '20px'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                      <span style={{ fontSize: '16px', fontWeight: '600', color: '#0A2540' }}>
                        Step {stepToDelete.step_number}:
                      </span>
                      <span style={{ fontSize: '16px', color: '#0A2540' }}>
                        {stepToDelete.step_name}
                      </span>
                    </div>
                    
                    <div style={{ fontSize: '14px', color: '#6B7280' }}>
                      <strong>Type:</strong> {stepToDelete.step_type} | <strong>Phase:</strong> {stepToDelete.workflow_phase}
                    </div>
                  </div>
                  
                  <div style={{
                    background: '#fff8e1',
                    padding: '15px',
                    borderRadius: '8px',
                    border: '1px solid #ffcc02'
                  }}>
                    <h5 style={{ margin: '0 0 10px 0', fontSize: '14px', color: '#f57c00', fontWeight: '600' }}>
                      ⚠️ This action will:
                    </h5>
                    <ul style={{ margin: '0', paddingLeft: '20px', fontSize: '13px', color: '#6B7280' }}>
                      <li>Remove the step from this acquisition</li>
                      <li>Mark any existing progress as inactive</li>
                      <li>Preserve data for audit purposes (soft delete)</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Modal Actions */}
              <div style={{ 
                padding: '20px 30px', 
                background: '#f8f9fa', 
                borderRadius: '0 0 16px 16px',
                display: 'flex', 
                justifyContent: 'flex-end', 
                gap: '15px',
                borderTop: '1px solid #e9ecef',
                flexShrink: 0
              }}>
                <button
                  onClick={() => {
                    setShowDeleteConfirmModal(false);
                    setStepToDelete(null);
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #95a5a6, #7f8c8d)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!stepToDelete || !selectedCountryForProgress || !selectedAcquisition?.database_id) return;
                    
                    setShowDeleteConfirmModal(false);
                    setDeletingStep(true);
                    
                    try {
                      const response = await fetch(
                        `${API_BASE_URL}/api/acquisition-process-steps/acquisition/${selectedAcquisition.database_id}/country/${selectedCountryForProgress.id}/steps/${stepToDelete.id}`,
                        {
                          method: 'DELETE'
                        }
                      );

                      if (response.ok) {
                        addNotification('success', 'Step deleted successfully!');
                        setProcessSteps(prev => prev.filter(s => s.step_number !== stepToDelete.step_number));
                      } else {
                        const errorData = await response.json();
                        addNotification('error', `Failed to delete step: ${errorData.detail}`);
                      }
                    } catch (err) {
                      addNotification('error', `Error deleting step: ${err}`);
                    } finally {
                      setDeletingStep(false);
                      setStepToDelete(null);
                    }
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #800020, #6B001A)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    boxShadow: '0 4px 12px rgba(231, 76, 60, 0.3)'
                  }}
                >
                  🗑️ Delete Step
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step Edit Modal */}
        {showStepEditModal && selectedStepForEdit && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            padding: '20px'
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: '0',
              maxWidth: '800px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'hidden',
              boxShadow: '0 25px 80px rgba(0, 0, 0, 0.4)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative'
            }}>
              
              {/* Modal Spinner Overlay */}
              {updatingStep && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(255, 255, 255, 0.9)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 10,
                  borderRadius: '16px'
                }}>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '15px'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      border: '4px solid #e3f2fd',
                      borderTop: '4px solid #0A2540',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    <p style={{ 
                      margin: '0', 
                      fontSize: '16px', 
                      color: '#0A2540', 
                      fontWeight: '500' 
                    }}>
                      ⏳ Updating step...
                    </p>
                  </div>
                </div>
              )}
              {/* Modal Header */}
              <div style={{
                background: 'linear-gradient(135deg, #0A2540, #1A365D)',
                color: 'white',
                padding: '25px 30px',
                borderRadius: '16px 16px 0 0'
              }}>
                <h3 style={{ margin: '0', fontSize: '22px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '24px' }}>✏️</span>
                  Edit Step: {selectedStepForEdit.step_number}
                </h3>
                <p style={{ margin: '5px 0 0 0', fontSize: '14px', opacity: '0.9' }}>
                  Customize this step for your acquisition workflow
                </p>
              </div>

              {/* Modal Content */}
              <div style={{ padding: '30px', overflow: 'auto', flex: 1 }}>
                {/* Basic Information Section */}
                <div style={{
                  marginBottom: '30px',
                  padding: '20px',
                  background: '#f8f9fa',
                  borderRadius: '12px',
                  border: '1px solid #e9ecef'
                }}>
                  <h4 style={{ margin: '0 0 20px 0', fontSize: '16px', color: '#0A2540', fontWeight: '600' }}>
                    📋 Basic Information
                  </h4>
                  
                  <div style={{ display: 'grid', gap: '20px' }}>
                    {/* Step Number and Name */}
                    <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '20px', alignItems: 'end' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0A2540', fontSize: '14px' }}>
                          Step Number *
                        </label>
                        <input
                          type="text"
                          value={editingStep.step_number || ''}
                          onChange={(e) => setEditingStep({...editingStep, step_number: e.target.value})}
                          style={{
                            width: '100%',
                            padding: '12px 15px',
                            border: '2px solid #e9ecef',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'border-color 0.2s ease'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#0A2540'}
                          onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                        />
                      </div>

                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0A2540', fontSize: '14px' }}>
                          Step Name *
                        </label>
                        <input
                          type="text"
                          value={editingStep.step_name || ''}
                          onChange={(e) => setEditingStep({...editingStep, step_name: e.target.value})}
                          style={{
                            width: '100%',
                            padding: '12px 15px',
                            border: '2px solid #e9ecef',
                            borderRadius: '8px',
                            fontSize: '14px',
                            transition: 'border-color 0.2s ease'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#0A2540'}
                          onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Classification Section */}
                <div style={{
                  marginBottom: '30px',
                  padding: '20px',
                  background: '#fff5f5',
                  borderRadius: '12px',
                  border: '1px solid #fed7d7'
                }}>
                  <h4 style={{ margin: '0 0 20px 0', fontSize: '16px', color: '#0A2540', fontWeight: '600' }}>
                    🏷️ Step Classification
                  </h4>
                  
                  {/* Row 1: Process Category, Workflow Phase */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0A2540', fontSize: '14px' }}>
                        Process Category *
                      </label>
                      <select
                        value={editingStep.process_category || ''}
                        onChange={(e) => setEditingStep({...editingStep, process_category: e.target.value})}
                        required
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          border: '2px solid #fed7d7',
                          borderRadius: '8px',
                          fontSize: '14px',
                          background: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="">Select Category *</option>
                        {dropdownData.process_categories?.filter((category: string) => category && category.trim()).map((category: string) => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0A2540', fontSize: '14px' }}>
                        Workflow Phase *
                      </label>
                      <select
                        value={editingStep.workflow_phase || ''}
                        onChange={(e) => setEditingStep({...editingStep, workflow_phase: e.target.value})}
                        required
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          border: '2px solid #fed7d7',
                          borderRadius: '8px',
                          fontSize: '14px',
                          background: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="">Select Phase *</option>
                        {dropdownData.workflow_phases?.filter((phase: string) => phase && phase.trim()).map((phase: string) => (
                          <option key={phase} value={phase}>{phase}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Row 2: Process Type, Step Type */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0A2540', fontSize: '14px' }}>
                        Process Type *
                      </label>
                      <select
                        value={editingStep.process_type || ''}
                        onChange={(e) => setEditingStep({...editingStep, process_type: e.target.value})}
                        required
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          border: '2px solid #fed7d7',
                          borderRadius: '8px',
                          fontSize: '14px',
                          background: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="">Select Type *</option>
                        {getProcessTypesForCountry().map((type: string) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0A2540', fontSize: '14px' }}>
                        Step Type *
                      </label>
                      <select
                        value={editingStep.step_type || ''}
                        onChange={(e) => setEditingStep({...editingStep, step_type: e.target.value})}
                        required
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          border: '2px solid #fed7d7',
                          borderRadius: '8px',
                          fontSize: '14px',
                          background: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="">Select Type *</option>
                        {dropdownData.step_types?.filter((type: string) => type && type.trim()).map((type: string) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {/* Row 3: Parent Step (separate row for better fit) */}
                  <div style={{ display: 'grid', gridTemplateColumns: '180px 200px', gap: '20px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0A2540', fontSize: '14px' }}>
                        Parent Step
                      </label>
                      <input
                        type="text"
                        value={editingStep.parent_step || ''}
                        onChange={(e) => setEditingStep({...editingStep, parent_step: e.target.value})}
                        placeholder="e.g., 1, 2.1"
                        style={{
                          width: '100%',
                          padding: '12px 10px',
                          border: '2px solid #fed7d7',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0A2540', fontSize: '14px' }}>
                        Dependencies
                      </label>
                      <input
                        type="text"
                        value={editingStep.depends_on || ''}
                        onChange={(e) => setEditingStep({...editingStep, depends_on: e.target.value})}
                        placeholder="e.g., 1, 2.1, 3"
                        style={{
                          width: '100%',
                          padding: '12px 10px',
                          border: '2px solid #fed7d7',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Timeline & Resources Section */}
                <div style={{
                  marginBottom: '30px',
                  padding: '20px',
                  background: '#f0f8ff',
                  borderRadius: '12px',
                  border: '1px solid #bee5eb'
                }}>
                  <h4 style={{ margin: '0 0 20px 0', fontSize: '16px', color: '#0A2540', fontWeight: '600' }}>
                    ⏱️ Timeline & Resources
                  </h4>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0A2540', fontSize: '14px' }}>
                        Timeline Min (Days)
                      </label>
                      <input
                        type="number"
                        value={editingStep.timeline_day_min || 0}
                        onChange={(e) => setEditingStep({...editingStep, timeline_day_min: parseInt(e.target.value) || 0})}
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          border: '2px solid #bee5eb',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0A2540', fontSize: '14px' }}>
                        Timeline Max (Days)
                      </label>
                      <input
                        type="number"
                        value={editingStep.timeline_day_max || 0}
                        onChange={(e) => setEditingStep({...editingStep, timeline_day_max: parseInt(e.target.value) || 0})}
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          border: '2px solid #bee5eb',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0A2540', fontSize: '14px' }}>
                        Duration (Days)
                      </label>
                      <input
                        type="number"
                        value={editingStep.duration_days || 1}
                        onChange={(e) => setEditingStep({...editingStep, duration_days: parseInt(e.target.value) || 1})}
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          border: '2px solid #bee5eb',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0A2540', fontSize: '14px' }}>
                        DRI Role
                      </label>
                      <input
                        type="text"
                        value={editingStep.dri_role || ''}
                        onChange={(e) => setEditingStep({...editingStep, dri_role: e.target.value})}
                        placeholder="e.g., HR Ops, Legal, Finance"
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          border: '2px solid #bee5eb',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0A2540', fontSize: '14px' }}>
                        Risk Level
                      </label>
                      <select
                        value={editingStep.risk_level || 'MEDIUM'}
                        onChange={(e) => setEditingStep({...editingStep, risk_level: e.target.value})}
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          border: '2px solid #bee5eb',
                          borderRadius: '8px',
                          fontSize: '14px',
                          background: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="HIGH">🔴 HIGH</option>
                        <option value="MEDIUM">🟡 MEDIUM</option>
                        <option value="LOW">🟢 LOW</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Step Properties Section */}
                <div style={{
                  marginBottom: '30px',
                  padding: '20px',
                  background: '#f9f5ff',
                  borderRadius: '12px',
                  border: '1px solid #e9d5ff'
                }}>
                  <h4 style={{ margin: '0 0 20px 0', fontSize: '16px', color: '#0A2540', fontWeight: '600' }}>
                    ⚙️ Step Properties
                  </h4>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '25px' }}>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '12px', 
                      cursor: 'pointer',
                      padding: '15px',
                      background: 'white',
                      borderRadius: '8px',
                      border: '2px solid #e9d5ff',
                      transition: 'all 0.2s ease'
                    }}>
                      <input
                        type="checkbox"
                        checked={editingStep.is_critical_path || false}
                        onChange={(e) => setEditingStep({...editingStep, is_critical_path: e.target.checked})}
                        style={{ transform: 'scale(1.2)' }}
                      />
                      <div>
                        <div style={{ fontSize: '14px', color: '#0A2540', fontWeight: '600' }}>Critical Path</div>
                        <div style={{ fontSize: '12px', color: '#6B7280' }}>Blocks other steps</div>
                      </div>
                    </label>
                    
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '12px', 
                      cursor: 'pointer',
                      padding: '15px',
                      background: 'white',
                      borderRadius: '8px',
                      border: '2px solid #e9d5ff',
                      transition: 'all 0.2s ease'
                    }}>
                      <input
                        type="checkbox"
                        checked={editingStep.is_legal_gate || false}
                        onChange={(e) => setEditingStep({...editingStep, is_legal_gate: e.target.checked})}
                        style={{ transform: 'scale(1.2)' }}
                      />
                      <div>
                        <div style={{ fontSize: '14px', color: '#0A2540', fontWeight: '600' }}>Legal Gate</div>
                        <div style={{ fontSize: '12px', color: '#6B7280' }}>Requires legal review</div>
                      </div>
                    </label>
                    
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '12px', 
                      cursor: 'pointer',
                      padding: '15px',
                      background: 'white',
                      borderRadius: '8px',
                      border: '2px solid #e9d5ff',
                      transition: 'all 0.2s ease'
                    }}>
                      <input
                        type="checkbox"
                        checked={editingStep.is_optional || false}
                        onChange={(e) => setEditingStep({...editingStep, is_optional: e.target.checked})}
                        style={{ transform: 'scale(1.2)' }}
                      />
                      <div>
                        <div style={{ fontSize: '14px', color: '#0A2540', fontWeight: '600' }}>Optional</div>
                        <div style={{ fontSize: '12px', color: '#6B7280' }}>Can be skipped</div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Resources Section */}
                <div style={{
                  marginBottom: '30px',
                  padding: '20px',
                  background: '#f0fff4',
                  borderRadius: '12px',
                  border: '1px solid #c6f6d5'
                }}>
                  <h4 style={{ margin: '0 0 20px 0', fontSize: '16px', color: '#0A2540', fontWeight: '600' }}>
                    📁 Resources & Links
                  </h4>
                  
                  <div style={{ display: 'grid', gap: '20px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0A2540', fontSize: '14px' }}>
                        📄 Documents Required
                      </label>
                      <textarea
                        value={editingStep.documents_required || ''}
                        onChange={(e) => setEditingStep({...editingStep, documents_required: e.target.value})}
                        rows={2}
                        placeholder="Google Drive links to required documents (one per line)"
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          border: '2px solid #c6f6d5',
                          borderRadius: '8px',
                          fontSize: '14px',
                          resize: 'vertical',
                          fontFamily: 'monospace'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0A2540', fontSize: '14px' }}>
                        📧 Email Templates
                      </label>
                      <textarea
                        value={editingStep.email_templates || ''}
                        onChange={(e) => setEditingStep({...editingStep, email_templates: e.target.value})}
                        rows={2}
                        placeholder="Google Drive links to email templates (one per line)"
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          border: '2px solid #c6f6d5',
                          borderRadius: '8px',
                          fontSize: '14px',
                          resize: 'vertical',
                          fontFamily: 'monospace'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0A2540', fontSize: '14px' }}>
                        📞 Call Scripts
                      </label>
                      <textarea
                        value={editingStep.call_scripts || ''}
                        onChange={(e) => setEditingStep({...editingStep, call_scripts: e.target.value})}
                        rows={2}
                        placeholder="Google Drive links to call scripts (one per line)"
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          border: '2px solid #c6f6d5',
                          borderRadius: '8px',
                          fontSize: '14px',
                          resize: 'vertical',
                          fontFamily: 'monospace'
                        }}
                      />
                    </div>

                  </div>
                </div>

                {/* Description Section */}
                <div style={{
                  marginBottom: '20px',
                  padding: '20px',
                  background: '#fffbf0',
                  borderRadius: '12px',
                  border: '1px solid #ffeaa7'
                }}>
                  <h4 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#0A2540', fontWeight: '600' }}>
                    📝 Description
                  </h4>
                  <textarea
                    value={editingStep.step_description || ''}
                    onChange={(e) => setEditingStep({...editingStep, step_description: e.target.value})}
                    rows={3}
                    placeholder="Provide detailed instructions or notes for this step..."
                    style={{
                      width: '100%',
                      padding: '15px',
                      border: '2px solid #ffeaa7',
                      borderRadius: '8px',
                      fontSize: '14px',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      lineHeight: '1.5'
                    }}
                  />
                </div>
              </div>

              {/* Modal Actions */}
              <div style={{ 
                padding: '20px 30px', 
                background: '#f8f9fa', 
                borderRadius: '0 0 16px 16px',
                display: 'flex', 
                justifyContent: 'flex-end', 
                gap: '15px',
                borderTop: '1px solid #e9ecef',
                flexShrink: 0
              }}>
                <button
                  onClick={closeStepEditModal}
                  disabled={updatingStep}
                  style={{
                    background: 'linear-gradient(135deg, #95a5a6, #7f8c8d)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: updatingStep ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={updateStep}
                  disabled={updatingStep || !editingStep.step_number || !editingStep.step_name || !editingStep.process_category || !editingStep.workflow_phase || !editingStep.process_type || !editingStep.step_type}
                  style={{
                    background: updatingStep ? '#bdc3c7' : 'linear-gradient(135deg, #0A2540, #1A365D)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: (updatingStep || !editingStep.step_number || !editingStep.step_name) ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    boxShadow: updatingStep ? 'none' : '0 4px 12px rgba(52, 152, 219, 0.3)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {updatingStep ? '⏳ Updating...' : '✅ Update Step'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Step Modal */}
        {showAddStepModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              position: 'relative'
            }}>
              
              {/* Add Step Modal Spinner Overlay */}
              {updatingStep && (
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'rgba(255, 255, 255, 0.9)',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  zIndex: 10,
                  borderRadius: '12px'
                }}>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '15px'
                  }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      border: '4px solid #e8f5e8',
                      borderTop: '4px solid #50C878',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    <p style={{ 
                      margin: '0', 
                      fontSize: '16px', 
                      color: '#0A2540', 
                      fontWeight: '500' 
                    }}>
                      ⏳ Adding step...
                    </p>
                  </div>
                </div>
              )}
              {/* Modal Header */}
              <div style={{
                background: 'linear-gradient(135deg, #50C878, #3FA760)',
                color: 'white',
                padding: '25px 30px',
                borderRadius: '16px 16px 0 0'
              }}>
                <h3 style={{ margin: '0', fontSize: '22px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '24px' }}>➕</span>
                  Add New Step
                </h3>
                <p style={{ margin: '5px 0 0 0', fontSize: '14px', opacity: '0.9' }}>
                  Create a custom step for your acquisition workflow
                </p>
              </div>

              {/* Modal Content */}
              <div style={{ padding: '30px', overflow: 'auto', flex: 1 }}>
                {/* Basic Information Section */}
                <div style={{
                  marginBottom: '30px',
                  padding: '20px',
                  background: '#f8f9fa',
                  borderRadius: '12px',
                  border: '1px solid #e9ecef'
                }}>
                  <h4 style={{ margin: '0 0 20px 0', fontSize: '16px', color: '#0A2540', fontWeight: '600' }}>
                    📋 Basic Information
                  </h4>
                  
                  <div style={{ display: 'grid', gap: '20px' }}>
                    {/* Step Number and Name */}
                    <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '20px', alignItems: 'end' }}>
                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0A2540', fontSize: '14px' }}>
                          Step Number *
                        </label>
                        <input
                          type="text"
                          value={editingStep.step_number || ''}
                          onChange={(e) => setEditingStep({...editingStep, step_number: e.target.value})}
                          placeholder="e.g., 1.5, 2.1"
                          style={{
                            width: '100%',
                            padding: '12px 15px',
                            border: '2px solid #e9ecef',
                            borderRadius: '8px',
                            fontSize: '14px',
                            fontWeight: '500',
                            transition: 'border-color 0.2s ease'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#50C878'}
                          onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                        />
                      </div>

                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0A2540', fontSize: '14px' }}>
                          Step Name *
                        </label>
                        <input
                          type="text"
                          value={editingStep.step_name || ''}
                          onChange={(e) => setEditingStep({...editingStep, step_name: e.target.value})}
                          placeholder="Enter step name"
                          style={{
                            width: '100%',
                            padding: '12px 15px',
                            border: '2px solid #e9ecef',
                            borderRadius: '8px',
                            fontSize: '14px',
                            transition: 'border-color 0.2s ease'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#50C878'}
                          onBlur={(e) => e.target.style.borderColor = '#e9ecef'}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Classification Section */}
                <div style={{
                  marginBottom: '30px',
                  padding: '20px',
                  background: '#fff5f5',
                  borderRadius: '12px',
                  border: '1px solid #fed7d7'
                }}>
                  <h4 style={{ margin: '0 0 20px 0', fontSize: '16px', color: '#0A2540', fontWeight: '600' }}>
                    🏷️ Step Classification
                  </h4>
                  
                  {/* Row 1: Process Category, Workflow Phase */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0A2540', fontSize: '14px' }}>
                        Process Category *
                      </label>
                      <select
                        value={editingStep.process_category || ''}
                        onChange={(e) => setEditingStep({...editingStep, process_category: e.target.value})}
                        required
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          border: '2px solid #fed7d7',
                          borderRadius: '8px',
                          fontSize: '14px',
                          background: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="">Select Category *</option>
                        {dropdownData.process_categories?.filter((category: string) => category && category.trim()).map((category: string) => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0A2540', fontSize: '14px' }}>
                        Workflow Phase *
                      </label>
                      <select
                        value={editingStep.workflow_phase || 'RIF-Execution'}
                        onChange={(e) => setEditingStep({...editingStep, workflow_phase: e.target.value})}
                        required
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          border: '2px solid #fed7d7',
                          borderRadius: '8px',
                          fontSize: '14px',
                          background: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="">Select Phase *</option>
                        {dropdownData.workflow_phases?.filter((phase: string) => phase && phase.trim()).map((phase: string) => (
                          <option key={phase} value={phase}>{phase}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Row 2: Process Type, Step Type */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0A2540', fontSize: '14px' }}>
                        Process Type *
                      </label>
                      <select
                        value={editingStep.process_type || 'Universal'}
                        onChange={(e) => setEditingStep({...editingStep, process_type: e.target.value})}
                        required
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          border: '2px solid #fed7d7',
                          borderRadius: '8px',
                          fontSize: '14px',
                          background: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="">Select Type *</option>
                        {getProcessTypesForCountry().map((type: string) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0A2540', fontSize: '14px' }}>
                        Step Type *
                      </label>
                      <select
                        value={editingStep.step_type || 'Single'}
                        onChange={(e) => setEditingStep({...editingStep, step_type: e.target.value})}
                        required
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          border: '2px solid #fed7d7',
                          borderRadius: '8px',
                          fontSize: '14px',
                          background: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="">Select Type *</option>
                        {dropdownData.step_types?.filter((type: string) => type && type.trim()).map((type: string) => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  {/* Row 3: Parent Step (separate row for better fit) */}
                  <div style={{ display: 'grid', gridTemplateColumns: '180px 200px', gap: '20px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0A2540', fontSize: '14px' }}>
                        Parent Step
                      </label>
                      <input
                        type="text"
                        value={editingStep.parent_step || ''}
                        onChange={(e) => setEditingStep({...editingStep, parent_step: e.target.value})}
                        placeholder="e.g., 1, 2.1"
                        style={{
                          width: '100%',
                          padding: '12px 10px',
                          border: '2px solid #fed7d7',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0A2540', fontSize: '14px' }}>
                        Dependencies
                      </label>
                      <input
                        type="text"
                        value={editingStep.depends_on || ''}
                        onChange={(e) => setEditingStep({...editingStep, depends_on: e.target.value})}
                        placeholder="e.g., 1, 2.1, 3"
                        style={{
                          width: '100%',
                          padding: '12px 10px',
                          border: '2px solid #fed7d7',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Timeline & Resources Section */}
                <div style={{
                  marginBottom: '30px',
                  padding: '20px',
                  background: '#f0f8ff',
                  borderRadius: '12px',
                  border: '1px solid #bee5eb'
                }}>
                  <h4 style={{ margin: '0 0 20px 0', fontSize: '16px', color: '#0A2540', fontWeight: '600' }}>
                    ⏱️ Timeline & Resources
                  </h4>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0A2540', fontSize: '14px' }}>
                        Timeline Min (Days)
                      </label>
                      <input
                        type="number"
                        value={editingStep.timeline_day_min || 0}
                        onChange={(e) => setEditingStep({...editingStep, timeline_day_min: parseInt(e.target.value) || 0})}
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          border: '2px solid #bee5eb',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0A2540', fontSize: '14px' }}>
                        Timeline Max (Days)
                      </label>
                      <input
                        type="number"
                        value={editingStep.timeline_day_max || 0}
                        onChange={(e) => setEditingStep({...editingStep, timeline_day_max: parseInt(e.target.value) || 0})}
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          border: '2px solid #bee5eb',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0A2540', fontSize: '14px' }}>
                        Duration (Days)
                      </label>
                      <input
                        type="number"
                        value={editingStep.duration_days || 1}
                        onChange={(e) => setEditingStep({...editingStep, duration_days: parseInt(e.target.value) || 1})}
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          border: '2px solid #bee5eb',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0A2540', fontSize: '14px' }}>
                        DRI Role
                      </label>
                      <input
                        type="text"
                        value={editingStep.dri_role || ''}
                        onChange={(e) => setEditingStep({...editingStep, dri_role: e.target.value})}
                        placeholder="e.g., HR Ops, Legal, Finance"
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          border: '2px solid #bee5eb',
                          borderRadius: '8px',
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0A2540', fontSize: '14px' }}>
                        Risk Level
                      </label>
                      <select
                        value={editingStep.risk_level || 'MEDIUM'}
                        onChange={(e) => setEditingStep({...editingStep, risk_level: e.target.value})}
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          border: '2px solid #bee5eb',
                          borderRadius: '8px',
                          fontSize: '14px',
                          background: 'white',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="HIGH">🔴 HIGH</option>
                        <option value="MEDIUM">🟡 MEDIUM</option>
                        <option value="LOW">🟢 LOW</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Step Properties Section */}
                <div style={{
                  marginBottom: '30px',
                  padding: '20px',
                  background: '#f9f5ff',
                  borderRadius: '12px',
                  border: '1px solid #e9d5ff'
                }}>
                  <h4 style={{ margin: '0 0 20px 0', fontSize: '16px', color: '#0A2540', fontWeight: '600' }}>
                    ⚙️ Step Properties
                  </h4>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '25px' }}>
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '12px', 
                      cursor: 'pointer',
                      padding: '15px',
                      background: 'white',
                      borderRadius: '8px',
                      border: '2px solid #e9d5ff',
                      transition: 'all 0.2s ease'
                    }}>
                      <input
                        type="checkbox"
                        checked={editingStep.is_critical_path || false}
                        onChange={(e) => setEditingStep({...editingStep, is_critical_path: e.target.checked})}
                        style={{ transform: 'scale(1.2)' }}
                      />
                      <div>
                        <div style={{ fontSize: '14px', color: '#0A2540', fontWeight: '600' }}>Critical Path</div>
                        <div style={{ fontSize: '12px', color: '#6B7280' }}>Blocks other steps</div>
                      </div>
                    </label>
                    
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '12px', 
                      cursor: 'pointer',
                      padding: '15px',
                      background: 'white',
                      borderRadius: '8px',
                      border: '2px solid #e9d5ff',
                      transition: 'all 0.2s ease'
                    }}>
                      <input
                        type="checkbox"
                        checked={editingStep.is_legal_gate || false}
                        onChange={(e) => setEditingStep({...editingStep, is_legal_gate: e.target.checked})}
                        style={{ transform: 'scale(1.2)' }}
                      />
                      <div>
                        <div style={{ fontSize: '14px', color: '#0A2540', fontWeight: '600' }}>Legal Gate</div>
                        <div style={{ fontSize: '12px', color: '#6B7280' }}>Requires legal review</div>
                      </div>
                    </label>
                    
                    <label style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '12px', 
                      cursor: 'pointer',
                      padding: '15px',
                      background: 'white',
                      borderRadius: '8px',
                      border: '2px solid #e9d5ff',
                      transition: 'all 0.2s ease'
                    }}>
                      <input
                        type="checkbox"
                        checked={editingStep.is_optional || false}
                        onChange={(e) => setEditingStep({...editingStep, is_optional: e.target.checked})}
                        style={{ transform: 'scale(1.2)' }}
                      />
                      <div>
                        <div style={{ fontSize: '14px', color: '#0A2540', fontWeight: '600' }}>Optional</div>
                        <div style={{ fontSize: '12px', color: '#6B7280' }}>Can be skipped</div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Resources Section */}
                <div style={{
                  marginBottom: '30px',
                  padding: '20px',
                  background: '#f0fff4',
                  borderRadius: '12px',
                  border: '1px solid #c6f6d5'
                }}>
                  <h4 style={{ margin: '0 0 20px 0', fontSize: '16px', color: '#0A2540', fontWeight: '600' }}>
                    📁 Resources & Links
                  </h4>
                  
                  <div style={{ display: 'grid', gap: '20px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0A2540', fontSize: '14px' }}>
                        📄 Documents Required
                      </label>
                      <textarea
                        value={editingStep.documents_required || ''}
                        onChange={(e) => setEditingStep({...editingStep, documents_required: e.target.value})}
                        rows={2}
                        placeholder="Google Drive links to required documents (one per line)"
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          border: '2px solid #c6f6d5',
                          borderRadius: '8px',
                          fontSize: '14px',
                          resize: 'vertical',
                          fontFamily: 'monospace'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0A2540', fontSize: '14px' }}>
                        📧 Email Templates
                      </label>
                      <textarea
                        value={editingStep.email_templates || ''}
                        onChange={(e) => setEditingStep({...editingStep, email_templates: e.target.value})}
                        rows={2}
                        placeholder="Google Drive links to email templates (one per line)"
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          border: '2px solid #c6f6d5',
                          borderRadius: '8px',
                          fontSize: '14px',
                          resize: 'vertical',
                          fontFamily: 'monospace'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', color: '#0A2540', fontSize: '14px' }}>
                        📞 Call Scripts
                      </label>
                      <textarea
                        value={editingStep.call_scripts || ''}
                        onChange={(e) => setEditingStep({...editingStep, call_scripts: e.target.value})}
                        rows={2}
                        placeholder="Google Drive links to call scripts (one per line)"
                        style={{
                          width: '100%',
                          padding: '12px 15px',
                          border: '2px solid #c6f6d5',
                          borderRadius: '8px',
                          fontSize: '14px',
                          resize: 'vertical',
                          fontFamily: 'monospace'
                        }}
                      />
                    </div>

                  </div>
                </div>

                {/* Description Section */}
                <div style={{
                  marginBottom: '20px',
                  padding: '20px',
                  background: '#fffbf0',
                  borderRadius: '12px',
                  border: '1px solid #ffeaa7'
                }}>
                  <h4 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#0A2540', fontWeight: '600' }}>
                    📝 Description
                  </h4>
                  <textarea
                    value={editingStep.step_description || ''}
                    onChange={(e) => setEditingStep({...editingStep, step_description: e.target.value})}
                    rows={3}
                    placeholder="Provide detailed instructions or notes for this step..."
                    style={{
                      width: '100%',
                      padding: '15px',
                      border: '2px solid #ffeaa7',
                      borderRadius: '8px',
                      fontSize: '14px',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                      lineHeight: '1.5'
                    }}
                  />
                </div>
              </div>

              {/* Modal Actions */}
              <div style={{ 
                padding: '20px 30px', 
                background: '#f8f9fa', 
                borderRadius: '0 0 16px 16px',
                display: 'flex', 
                justifyContent: 'flex-end', 
                gap: '15px',
                borderTop: '1px solid #e9ecef',
                flexShrink: 0
              }}>
                <button
                  onClick={closeAddStepModal}
                  disabled={updatingStep}
                  style={{
                    background: 'linear-gradient(135deg, #95a5a6, #7f8c8d)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: updatingStep ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={addNewStep}
                  disabled={updatingStep || !editingStep.step_number || !editingStep.step_name || !editingStep.process_category || !editingStep.workflow_phase || !editingStep.process_type || !editingStep.step_type}
                  style={{
                    background: updatingStep ? '#bdc3c7' : 'linear-gradient(135deg, #50C878, #3FA760)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    cursor: (updatingStep || !editingStep.step_number || !editingStep.step_name || !editingStep.process_category || !editingStep.workflow_phase || !editingStep.process_type || !editingStep.step_type) ? 'not-allowed' : 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    boxShadow: updatingStep ? 'none' : '0 4px 12px rgba(39, 174, 96, 0.3)',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {updatingStep ? '⏳ Adding...' : '✅ Add Step'}
                  </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default App;
